import tiledConfig from "@/assets/tiled/Map1024_Version8.json";
import {
	BOOST_MAX,
	CONCURRENCY_LIMIT,
	EVENT_BUILDING_UNLOCK_LEVEL,
	FLAG_ENABLE_TIMERS,
	FLAG_REVEAL_MAP,
	GAME_TICKER_INTERVAL,
	INACTIVITY_TIMEOUT,
	OFFER_BUILDING_UNLOCK_LEVEL,
	SAFE_COORDINATE_OUTSIDE_MAP,
} from "@/config/constants";
import {
	appendPassiveEventiumToOfflineAccumulator,
	eventOfflineIncomeMultiplier,
	eventRepository,
	extendAccumulatorKeysWithEventiumIfActive,
	skipDemolishToStorage,
} from "@/event";
import { ResourceType } from "@/game/entities/config/resources";
import {
	ALL_VISIBILITY_TYPES,
	isTerrain,
	TileVisibilityType,
} from "@/game/entities/config/terrain.ts";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import useEmitter, {
	EmitterEvents,
	TileLabelAction,
	type TileRepositoryAddToRoadGraphPayload,
	type TileRepositoryUpgradePayload,
} from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import { type MapMutationAction } from "@/hooks/useMapActions";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import GameError, { GameErrorType } from "@/libs/GameError";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useMapExpansionStore from "@/store/useMapExpansionStore";
import useQuestStore from "@/store/useQuestStore";
import useQuickToolStore from "@/store/useQuickToolStore";
import useResourcesStorageStore from "@/store/useResourcesStorageStore";
import useResourcesStore, {
	UNCAPPED_RESOURCE_TYPES,
} from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import { SiegeSystemResult } from "@/store/useSiegeSystemStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { type Tuple } from "@/types";
import type { QuestActive } from "@/types/quest";
import { LevelAxis } from "@/types/quest";
import type {
	Reward,
	TileCoordinatesWithValue,
	UpgradeParams,
	UpgradeRewardValue,
} from "@/types/tile";
import {
	TileEntityParam,
	TileLabelSkin,
	TileLabelType,
	type SettlementItem,
	type TileCoordinates,
	type TileEntityParams,
	type TileId,
	type TileIndex,
	type TileNeighbour,
} from "@/types/tile";
import { type TiledConfig } from "@/types/tiled";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { randomInteger, roundNumber } from "@/utils/number";
import { useDebounceFn } from "@vueuse/core";
import pMap from "p-map";
import { storeToRefs } from "pinia";
import type BigShipyardBuildingTileEntity from "./entities/buildings/BigShipyardBuildingTileEntity";
import type GemsMineBuildingTileEntity from "./entities/buildings/GemsMineBuildingTileEntity";
import type SettlementCastleBuildingTileEntity from "./entities/buildings/SettlementCastleBuildingTileEntity";
import type ShipyardBuildingTileEntity from "./entities/buildings/ShipyardBuildingTileEntity";
import type WheatFarmBuildingTileEntity from "./entities/buildings/WheatFarmBuildingTileEntity";
import {
	FrameName,
	tilerepoBanditsFrameNames,
	tilerepoBeachesFrameNames,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoBridgeRoadsFrameNames,
	tilerepoBuildingsFrameNames,
	tilerepoCobbledRoadsFrameNames,
	tilerepoDirtRoadsFrameNames,
	tilerepoExplorationBuildingsFrameNames,
	tilerepoFarmFieldsFrameNames,
	tilerepoFarmsFrameNames,
	tilerepoFightableEnemiesFrameNames,
	tilerepoFightableMainEnemiesFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoFordCrossingRoadsFrameNames,
	tilerepoForestFrameNames,
	tilerepoFriendlyMilitaryUnitsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoGrasslandFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoLayer0FrameNames,
	tilerepoLayer1FrameNames,
	tilerepoLayer2FrameNames,
	tilerepoLayer3FrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoLumberMillsFrameNames,
	tilerepoNonSellableFrameNames,
	tilerepoOnlyOneBuildingPerCastleFrameNames,
	tilerepoPalisades1FrameNames,
	tilerepoPalisades3FrameNames,
	tilerepoPiratesFrameNames,
	tilerepoPoiFrameNames,
	tilerepoPOIs1FrameNames,
	tilerepoPremiumBuildings,
	tilerepoPremiumGemsBuildings,
	tilerepoRemovableEventBuildingsFrameNames,
	tilerepoRiversFrameNames,
	tilerepoRoadsFrameNames,
	tilerepoSalvageableFrameNames,
	tilerepoSeasFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
	tilerepoSettlementEmptyFrameNames,
	tilerepoShipyardFrameNames,
	tilerepoStoneBridgeRoadsFrameNames,
	tilerepoTunnelRoadsFrameNames,
	tilerepoWaterFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from "./entities/config";
import { QuestObjectiveActions } from "./entities/config/quests/quest_objective_actions";
import { settlementNames } from "./entities/config/settlements";
import AbstractPoiTileEntity from "./entities/poi/AbstractPoiTileEntity";
import type ForestTerrainTileEntity from "./entities/terrains/ForestTerrainTileEntity";
import type { TileEntityWithOnUpgradeMethod } from "./entities/TileEntity";
import TileEntity, {
	type TileEntityWithOnActivateMethod,
	type TileEntityWithOnBuildMethod,
	type TileEntityWithOnRevealMethod,
} from "./entities/TileEntity";
import { plagueRepository } from "./PlagueRepository";
import QuestRepository from "./QuestRepository";
import SegmentManager from "./SegmentManager";
import SoundService from "./SoundService";
import { BeachNeighbourType, getCorrectBeachFrameName } from "./utils/beaches";
import { createBuildingTileEntity } from "./utils/buildings";
import { createPoiTileEntity } from "./utils/pois";
import { getCorrectRoadFrameNameByPrevFrameName } from "./utils/quickTool";
import { getUnlocksByLevel } from "./utils/resources";
import { createRoadTileEntity } from "./utils/roads";
import { createSalvageableTileEntity } from "./utils/salvageables";
import { getSettlementPalisadeFrameNameByCoordinates } from "./utils/settlement";
import { createTerrainTileEntity } from "./utils/terrain";
import { getUpgradeCurrentReplaceValueById } from "./utils/upgrades";

// TODO: tilerepoXXX.has() -> isXXX()

// 4 - 8 MB na 1 uint32 udaj (0 - 4294967295) pre kazdy tile
// 2 - 4 MB na 1 uint16 udaj (0 - 65535) pre kazdy tile
// 1 - 2 MB na 1 uint8 udaj (0 - 255) pre kazdy tile

type TileEntityCacheKey = `${TileIndex}|${FrameName}`;

type TileRepositoryLayerIndexArray = [0, 1, 2, 3];

export type TileRepositoryLayerIndex = TileRepositoryLayerIndexArray[number];

export type TileRepositoryLayerCount = TileRepositoryLayerIndexArray["length"];

const LOCKED_FIELD_TICK_COUNT = 3;

const ROAD_REFRESH_DEBOUNCE_TIME_MS = 500;

const GC_ENTITY_CACHE_INTERVAL = 10_000; // 10 second

const RESOURCE_INCOME_REGEXP = /Income$/;

const RESOURCE_CONSUMPTION_REGEXP = /Consumption$/;

const DB_NAME = "GameCacheDB";
const DB_VERSION = 1;
const STORE_NAME = "roadGraph";
const GRAPH_KEY = "mainRoadGraph";

// Typ pre serializovaný graf
type SerializedRoadGraph = Array<[TileIndex, Array<TileIndex>]>;

class TileRepository {
	public readonly MAP_SIZE: number = 1024;

	public readonly TILE_SIZE: number = 256;

	public readonly OVERLAP_TILE_SIZE: number = 54;

	private readonly emitter: ReturnType<typeof useEmitter>;

	private readonly ticker: ReturnType<typeof useGameTicker>;

	private readonly resourcesStore: ReturnType<typeof useResourcesStore>;

	private readonly resourcesStorageStore: ReturnType<
		typeof useResourcesStorageStore
	>;

	private readonly tiledConfig: TiledConfig | null;

	private readonly tiles: Tuple<Uint16Array, TileRepositoryLayerCount>;

	private readonly params: TileEntityParams;

	private readonly tileIdToFrameNameCache = new Map<TileId, FrameName>([
		[0, FrameName.NOT_SET],
	]);

	private readonly frameNameToTileIdCache = new Map<FrameName, TileId>([
		[FrameName.NOT_SET, 0],
	]);

	private readonly lockedTiles = new Map<TileIndex, number>();

	private readonly fogRadiusExploration: number = 3;
	public readonly basicSeaExploration: number = 4;
	private readonly mainSettlementBorderCoords: Array<TileCoordinates> = [
		{ x: 511, y: 511 },
		{ x: 511, y: 512 },
		{ x: 511, y: 513 },
		{ x: 512, y: 511 },
		{ x: 512, y: 513 },
		{ x: 513, y: 511 },
		{ x: 513, y: 512 },
		{ x: 513, y: 513 },
	];
	private isUpdatingRoadNeighbors: boolean = false;

	private readonly roadGraph = new Map<TileIndex, Set<TileIndex>>();

	private readonly frameNameToTileIndexesCache = new Map<
		FrameName,
		Set<TileIndex>
	>(
		[
			FrameName.SETTLEMENT_CASTLE,
			...Array.from(tilerepoBuildingsFrameNames).filter(
				(entity: FrameName): boolean =>
					!tilerepoSettlementCastleBuildingsFrameNames.has(entity),
			),
			...Array.from(tilerepoRoadsFrameNames),
			...Array.from(tilerepoPOIs1FrameNames),
			...Array.from(tilerepoFightableEnemiesFrameNames),
		].map((frameName: FrameName): [FrameName, Set<TileIndex>] => [
			frameName,
			new Set<TileIndex>(),
		]),
	);

	private readonly blockedTileIndexesCache = new Map<
		TileIndex,
		Set<TileIndex>
	>();

	private readonly entityCache = new Map<TileEntityCacheKey, TileEntity>();

	private entityCacheGcIntervalId: ReturnType<typeof setInterval> | null =
		null;

	private _internalExploredTileCount: number = 0;

	private _internalExploredWaterTileCount: number = 0;

	private definedTiles: number = 0;

	private readonly SETTLEMENT_EMPTY_TILE_ID: TileId | null = null;
	private readonly SETTLEMENT_EMPTY_TILE_IDS: Set<TileId> = new Set<TileId>();
	private readonly SETTLEMENT_CASTLE_TILE_IDS: Set<TileId> =
		new Set<TileId>();
	private readonly FARM_FIELD_TILE_IDS: Set<TileId> = new Set<TileId>();

	private dbInstance: IDBDatabase | null = null;
	private dbPromise: Promise<IDBDatabase> | null = null; // Aby sme zabránili súbežným open volaniam

	public constructor(tiles?: Array<MapMutationAction>) {
		this.emitter = useEmitter();

		this.ticker = useGameTicker();

		this.resourcesStore = useResourcesStore();

		this.resourcesStorageStore = useResourcesStorageStore();

		this.tiledConfig = tiledConfig as unknown as TiledConfig;

		if (this.tiledConfig.width !== this.tiledConfig.height)
			throw new Error(
				"TileRepository: width and height must be the same",
			);

		if (this.tiledConfig.tilewidth !== this.tiledConfig.tileheight)
			throw new Error(
				"TileRepository: tilewidth and tileheight must be the same",
			);

		for (const tileset of this.tiledConfig.tilesets) {
			for (let i = 0; i < tileset.tilecount; i++) {
				const tile = tileset.tiles[i];

				// tile is not used in tileset (Kubo)
				if (!tile) continue;

				const id = tileset.firstgid + tile.id;
				if (!tile.properties?.length)
					throw new Error(
						`Tile properties not found for tile ${id}!`,
					);

				this.tileIdToFrameNameCache.set(
					id,
					(tile.properties[0].value + ".png") as FrameName,
				);
				this.frameNameToTileIdCache.set(
					(tile.properties[0].value + ".png") as FrameName,
					id,
				);
			}
		}

		// dynamically add beaches, index does not matter
		let id = 1_000_000;
		for (const frameName of tilerepoBeachesFrameNames) {
			this.tileIdToFrameNameCache.set(id, frameName);
			this.frameNameToTileIdCache.set(frameName, id);
			id++;
		}

		// dynamically add overlaps, index does not matter
		id = 2_000_000;
		const keys = Array.from(this.frameNameToTileIdCache.keys()).sort();
		for (let key of keys) {
			// TODO: Toto spravit len pre tiles, ktore maju odrezky - nie takto programaticky pre vsetky.
			if (key === FrameName.NOT_SET) continue;
			key = key.replace(".png", "_Top.png") as FrameName;
			this.tileIdToFrameNameCache.set(id, key);
			this.frameNameToTileIdCache.set(key, id);
			id++;
		}

		this.SETTLEMENT_EMPTY_TILE_ID = this.getTileIdByFrameName(
			FrameName.SETTLEMENT_EMPTY,
		);
		this.SETTLEMENT_EMPTY_TILE_IDS = new Set<TileId>(
			Array.from(tilerepoSettlementEmptyFrameNames)
				.map((frameName: FrameName): TileId | null =>
					this.getTileIdByFrameName(frameName),
				)
				.filter(
					(id: TileId | null): boolean => id !== null,
				) as Array<TileId>,
		);
		this.SETTLEMENT_CASTLE_TILE_IDS = new Set<TileId>(
			Array.from(tilerepoSettlementCastleBuildingsFrameNames)
				.map((frameName: FrameName): TileId | null =>
					this.getTileIdByFrameName(frameName),
				)
				.filter(
					(id: TileId | null): boolean => id !== null,
				) as Array<TileId>,
		);
		this.FARM_FIELD_TILE_IDS = new Set<TileId>(
			Array.from(tilerepoFarmFieldsFrameNames)
				.map((frameName: FrameName): TileId | null =>
					this.getTileIdByFrameName(frameName),
				)
				.filter(
					(id: TileId | null): boolean => id !== null,
				) as Array<TileId>,
		);

		this.tiles = [
			// Terrain
			new Uint16Array(this.size),
			// Palisades / Beaches
			new Uint16Array(this.size),
			// Castles / Buildings / Roads / Salvageables / Enemies
			new Uint16Array(this.size),
			// Palisades
			new Uint16Array(this.size),
		];

		this.params = {
			visibility: new Uint8Array(this.size),
			durability: new Uint32Array(this.size),
			isConnectedToMainSettlement: new Uint8Array(this.size),
			isBlockedByEnemy: new Uint8Array(this.size),
		};

		for (let i = 0; i < this.tiledConfig.layers[0].data.length; i++) {
			const { x, y } = this.getTileCoordinatesByTileIndex(i);

			// Kubo sa dakde uklikol a mimo mapy exituju neviditelny tile (aktualne id = 48), preto musime kontrolovat validnost tu.
			if (!this.isTileIdValid(this.tiledConfig.layers[0].data[i]))
				continue;

			if (this.tiledConfig.layers[0].data[i] !== 0) this.definedTiles++;

			for (let j = 0; j < this.tiledConfig.layers.length; j++) {
				this.setInternalTileValue(
					i,
					j as TileRepositoryLayerIndex,
					this.tiledConfig.layers[j].data[i],
				);
			}

			const dx = Math.abs(x - this.center);
			const dy = Math.abs(y - this.center);

			if (Math.max(dx, dy) <= 1 || FLAG_REVEAL_MAP)
				this.setInternalTileValue(
					i,
					TileEntityParam.VISIBILITY,
					TileVisibilityType.EXPLORED,
				);
			else if (Math.max(dx, dy) <= 2)
				this.setInternalTileValue(
					i,
					TileEntityParam.VISIBILITY,
					TileVisibilityType.FOG,
				);
			else
				this.setInternalTileValue(
					i,
					TileEntityParam.VISIBILITY,
					TileVisibilityType.BLACK,
				);

			// TODO:
			// set new castles in onActivate method (waiting for review), currently there are all 70 castles in buildings cache
			// if (i === MAIN_SETTLEMENT_TILE_INDEX) {
			// 	this.buildingTileIndexes
			// 		.get(FrameName.SETTLEMENT_CASTLE)!
			// 		.add(MAIN_SETTLEMENT_TILE_INDEX);
			// }

			// Nastavenie isBlockedByEnemy okolo banditov a pirátov
			const frameName2 = this.getFrameNameByTileId(
				this.getInternalTileValue(i, 2),
			);
			if (
				frameName2 &&
				(tilerepoPiratesFrameNames.has(frameName2) ||
					tilerepoBanditsFrameNames.has(frameName2) ||
					tilerepoFightableMainEnemiesFrameNames.has(frameName2))
			) {
				const entity = this.getTileEntityByTileIndex(i)!;
				const range = entity.range || 0;
				for (let dy = -range; dy <= range; dy++) {
					for (let dx = -range; dx <= range; dx++) {
						if (dx === 0 && dy === 0) continue;

						const x = entity.x + dx;
						const y = entity.y + dy;
						const i = this.getTileIndexByTileCoordinates({
							x,
							y,
						});

						this.setInternalTileValue(
							i,
							TileEntityParam.IS_BLOCKED_BY_ENEMY,
							1,
						);

						if (
							tilerepoFightableMainEnemiesFrameNames.has(
								entity.frameName,
							)
						) {
							this.setBlockedByEnemyTileIndexByTileIndex(
								i,
								entity.index,
								true,
							);
						}
					}
				}
			}
		}

		if (!FLAG_REVEAL_MAP && tiles) {
			for (const { tile, params } of tiles) {
				tile.tileIds = this.migrateSavedFarmFieldTileIds(tile.tileIds);

				for (let i = 0; i < tile.tileIds.length; i++) {
					// Ak teren nie je setnuty v ulozenych datach, skipneme import, lebo tile params budu nespravne (FOW).
					if (tile.tileIds[0] === 0) continue;

					this.setInternalTileValue(
						tile.tileIndex,
						i as TileRepositoryLayerIndex,
						tile.tileIds[i],
					);

					// Toto tu musi zostat, aby sme vedeli, ze riesime len odhalene tiles, inak by aj ostatne POI mohli nieco produkovat.
					if (i === 0 && tile.tileIds[i] !== 0) {
						this.handlePoiIndexesIncrement(tile.tileIndex, true);
					}
				}

				const entries = Object.entries(params) as Array<
					[keyof TileEntityParams, number]
				>;
				for (let i = 0; i < entries.length; i++) {
					const [key, value] = entries[i];
					if (!(key in this.params)) continue;

					this.setInternalTileValue(tile.tileIndex, key, value);

					if (key === TileEntityParam.IS_BLOCKED_BY_ENEMY && !value) {
						this.setBlockedByEnemyTileIndexByTileIndex(
							tile.tileIndex,
							null,
							false,
						);
					}

					// na chestky
					if (
						key === TileEntityParam.VISIBILITY &&
						value === TileVisibilityType.FOG
					) {
						const { x, y } = this.getTileCoordinatesByTileIndex(
							tile.tileIndex,
						);

						this.emitter.emit(EmitterEvents.PhaserTileFogAdded, {
							x,
							y,
						});
					}
				}
			}
		}

		for (let i = 0; i < this.size; i++) {
			if (!this.getInternalTileValue(i, 2)) continue;
			this.handleBuildingIndexesIncrement(i);
		}

		// fix Kubos bullshits
		this.runTiledErrata();

		this.fixRedTiles();

		// clear json reference
		this.tiledConfig = null;

		this.emitter.on(
			EmitterEvents.TileRepositoryUpgrade,
			({
				coordinates,
				frameName,
				noRefresh,
				onlyPalisades,
			}: TileRepositoryUpgradePayload): void => {
				this.setTileByTileCoordinates(
					coordinates,
					frameName,
					noRefresh,
					onlyPalisades,
				);
			},
		);

		this.emitter.on(
			EmitterEvents.TileRepositoryAddToRoadGraph,
			({ tileIndex }: TileRepositoryAddToRoadGraphPayload): void => {
				const { x, y } = this.getTileCoordinatesByTileIndex(tileIndex);
				this.updateRoadGraph(x, y);
			},
		);

		this.ticker.addHandler("0:tilerepo", this.onTick.bind(this));

		this.initEntityCacheGc();

		this.randomizeSettlementEmptyTiles();

		// eslint-disable-next-line no-console
		console.log(this);
	}

	public get size(): number {
		return this.MAP_SIZE ** 2;
	}

	public get center(): number {
		return this.MAP_SIZE / 2;
	}

	public get activeSettlementCount(): number {
		return this.getActiveSettlementCountOnLevelOrHigher();
	}

	public getActiveSettlementCountOnLevelOrHigher(level: number = 0): number {
		return Array.from(settlementNames.values()).reduce(
			(acc: number, { x, y }: SettlementItem): number => {
				const tileIndex = this.getTileIndexByTileCoordinates({ x, y });
				const entity = this.getTileEntityByTileIndex(
					tileIndex,
				) as SettlementCastleBuildingTileEntity | null;
				if (entity!.isActive && entity!.level >= level) {
					return acc + 1;
				} else {
					return acc;
				}
			},
			0,
		);
	}

	public getActiveSettlementCountOnLevelOrHigherAndAxis(
		level: number = 0,
		axis: LevelAxis,
	): number {
		return Array.from(settlementNames.values()).reduce(
			(acc: number, { x, y }: SettlementItem): number => {
				const tileIndex = this.getTileIndexByTileCoordinates({ x, y });
				const entity = this.getTileEntityByTileIndex(
					tileIndex,
				) as SettlementCastleBuildingTileEntity | null;

				let isOnAxis = false;
				switch (axis) {
					case LevelAxis.ABOVE:
						isOnAxis = y <= 512;
						break;
					case LevelAxis.UNDER:
						isOnAxis = y > 512;
						break;
					case LevelAxis.LEFT:
						isOnAxis = x <= 512;
						break;
					case LevelAxis.RIGHT:
						isOnAxis = x > 512;
						break;
				}

				if (entity!.isActive && entity!.level >= level && isOnAxis) {
					return acc + 1;
				} else {
					return acc;
				}
			},
			0,
		);
	}

	public getTileNumberByLayer(
		tileIndex: TileIndex,
		index: TileRepositoryLayerIndex = 0,
	): number {
		switch (index) {
			case 0:
				return this.getInternalTileValue(tileIndex, 0);
			case 1:
				return this.getInternalTileValue(tileIndex, 1);
			case 2:
				return this.getInternalTileValue(tileIndex, 2);
			case 3:
				return this.getInternalTileValue(tileIndex, 3);
			default:
				throw new GameError(
					GameErrorType.DATA_ERROR,
					`Invalid index: ${index}`,
				);
		}
	}

	public isTileIdValid(tileId: TileId): boolean {
		return this.tileIdToFrameNameCache.has(tileId);
	}

	public getFrameNameByTileId(tileId: TileId): FrameName | null {
		return this.isTileIdValid(tileId)
			? (this.tileIdToFrameNameCache.get(tileId) as FrameName)
			: null;
	}

	public getTileIdByFrameName(frameName: FrameName): TileId | null {
		return this.frameNameToTileIdCache.has(frameName)
			? (this.frameNameToTileIdCache.get(frameName) as number)
			: null;
	}

	//TODO treba zohladnit konkretny layer
	public get nonBlackTileCount(): number {
		let count = 0;
		for (let i = 0; i < this.size; i++) {
			const visibility = this.getInternalTileValue(
				i,
				TileEntityParam.VISIBILITY,
			);
			if (visibility !== TileVisibilityType.BLACK) count++;
		}
		return count;
	}

	public get exploredTileCount(): number {
		return this._internalExploredTileCount;

		// let count = 0;
		// for (let i = 0; i < this.size; i++) {
		// 	const visibility = this.getInternalTileValue(
		// 		i,
		// 		TileEntityParam.VISIBILITY,
		// 	);
		// 	if (visibility === TileVisibilityType.EXPLORED) count++;
		// }
		// return count;
	}

	public get exploredWaterTileCount(): number {
		return this._internalExploredWaterTileCount;
	}

	public get exploredWaterTileIncome(): number {
		return (
			this.exploredWaterTileCount *
			(1 + getUpgradeCurrentReplaceValueById(UpgradeIds.naval_trade))
		);
	}

	public get exploredTilePercent(): number {
		return this.exploredTileCount / this.definedTiles || 0;
	}

	public isTileDefined(coordinates: TileCoordinates): boolean {
		const tileIndex = this.getTileIndexByTileCoordinates(coordinates);
		return this.getInternalTileValue(tileIndex, 0) !== 0;
	}

	// TODO: algoritmus na prehladavanie zo stredu
	public getTileCount0(frameNames: Set<FrameName>, limit?: number): number {
		const tileIds = new Set<TileId>(
			Array.from(frameNames)
				.map((frameName: FrameName): TileId | null =>
					this.getTileIdByFrameName(frameName),
				)
				.filter(
					(tileId: TileId | null): boolean => tileId !== null,
				) as Array<TileId>,
		);

		let count: number = 0;

		for (let i = 0; i < this.size; i++) {
			if (
				tileIds.has(this.getInternalTileValue(i, 0)) &&
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED
			)
				count++;

			if (limit && count >= limit) break;
		}

		return count;
	}

	public getTileCoordinates0(frameName: FrameName): TileCoordinates[] {
		const coordinates: TileCoordinates[] = [];

		for (let i = 0; i < this.size; i++) {
			if (
				this.getTileIdByFrameName(frameName) ===
					this.getInternalTileValue(i, 0) &&
				!this.getInternalTileValue(i, 2) &&
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED
			)
				coordinates.push(this.getTileCoordinatesByTileIndex(i));
		}

		return coordinates;
	}

	// TODO: algoritmus na prehladavanie zo stredu
	public getTileCount2(frameNames: Set<FrameName>, limit?: number): number {
		const frameNamesArray = Array.from(frameNames);

		const useCache = frameNamesArray.every(
			(frameName: FrameName): boolean =>
				this.frameNameToTileIndexesCache.has(frameName),
		);

		if (useCache)
			return frameNamesArray.reduce(
				(acc: number, frameName: FrameName): number =>
					!this.frameNameToTileIndexesCache.has(frameName) ||
					(limit && acc >= limit)
						? acc
						: acc +
							this.frameNameToTileIndexesCache.get(frameName)!
								.size,
				0,
			);

		const tileIds = new Set<TileId>(
			Array.from(frameNames)
				.map((frameName: FrameName): TileId | null =>
					this.getTileIdByFrameName(frameName),
				)
				.filter(
					(tileId: TileId | null): boolean => tileId !== null,
				) as Array<TileId>,
		);

		let count: number = 0;

		for (let i = 0; i < this.size; i++) {
			if (
				tileIds.has(this.getInternalTileValue(i, 2)) &&
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED
			)
				count++;

			if (limit && count >= limit) break;
		}

		return count;
	}

	public getTileCoordinates2(frameName: FrameName): TileCoordinates[] {
		const coordinates: TileCoordinates[] = [];
		const useCache = this.frameNameToTileIndexesCache.has(frameName);

		if (useCache) {
			const tileIndexes = this.frameNameToTileIndexesCache.get(frameName);
			if (!tileIndexes || tileIndexes.size === 0) return [];

			for (const tileIndex of tileIndexes) {
				coordinates.push(this.getTileCoordinatesByTileIndex(tileIndex));
			}
			return coordinates;
		}

		for (let i = 0; i < this.size; i++) {
			if (
				this.getTileIdByFrameName(frameName) ===
					this.getInternalTileValue(i, 2) &&
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED
			)
				coordinates.push(this.getTileCoordinatesByTileIndex(i));
		}

		return coordinates;
	}

	public getSettlementTileCount2(
		{ x, y }: TileCoordinates,
		buildings: Set<FrameName>,
	): number {
		let count: number = 0;
		this.getNeighbourTileIndexesByTileCoordinates(
			{ x, y },
			1,
			false,
			new Set<TileVisibilityType>([TileVisibilityType.EXPLORED]),
		).forEach((tileIndex: TileIndex): void => {
			const frameName = this.getFrameNameByTileId(
				this.getInternalTileValue(tileIndex, 2),
			);
			if (frameName && buildings.has(frameName)) count++;
		});
		return count;
	}

	public getBlockedSettlementTileCount2(
		{ x, y }: TileCoordinates,
		range: number,
	): number {
		let count: number = 0;
		this.getNeighbourTileIndexesByTileCoordinates(
			{ x, y },
			range,
			false,
			ALL_VISIBILITY_TYPES,
		).forEach((tileIndex: TileIndex): void => {
			const frameName = this.getFrameNameByTileId(
				this.getInternalTileValue(tileIndex, 2),
			);
			if (
				(frameName && tilerepoSalvageableFrameNames.has(frameName)) ||
				this.getInternalTileValue(
					tileIndex,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
				) === 1
			)
				count++;
		});
		return count;
	}

	public getTileIdsByTileIndex(
		tileIndex: TileIndex,
	): Tuple<TileId, TileRepositoryLayerCount> {
		const emptyTileId = this.getTileIdByFrameName(FrameName.NOT_SET)!;
		const tileId0 = this.getInternalTileValue(tileIndex, 0);
		const tileId1 = this.getInternalTileValue(tileIndex, 1);
		const tileId2 = this.getInternalTileValue(tileIndex, 2);
		const tileId3 = this.getInternalTileValue(tileIndex, 3);
		return [
			this.isTileIdValid(tileId0) ? tileId0 : emptyTileId,
			this.isTileIdValid(tileId1) ? tileId1 : emptyTileId,
			this.isTileIdValid(tileId2) ? tileId2 : emptyTileId,
			this.isTileIdValid(tileId3) ? tileId3 : emptyTileId,
		];
	}

	public getTileIdsByTileCoordinates({
		x,
		y,
	}: TileCoordinates): Tuple<TileId, TileRepositoryLayerCount> {
		const tileIndex = this.getTileIndexByTileCoordinates({ x, y });
		return this.getTileIdsByTileIndex(tileIndex);
	}

	public getTileIdByTileIndex(tileIndex: TileIndex): TileId | null {
		const [tileId0, , tileId2] = this.getTileIdsByTileIndex(tileIndex);
		return tileId2 || tileId0 || null;
	}

	public getTileIdByTileCoordinates({
		x,
		y,
	}: TileCoordinates): TileId | null {
		const tileIndex = this.getTileIndexByTileCoordinates({ x, y });
		return this.getTileIdByTileIndex(tileIndex);
	}

	public getTileParamsByTileIndex(
		tileIndex: TileIndex,
	): Record<keyof TileEntityParams, number> {
		return {
			visibility: this.getInternalTileValue(
				tileIndex,
				TileEntityParam.VISIBILITY,
			),
			durability: this.getInternalTileValue(
				tileIndex,
				TileEntityParam.DURABILITY,
			),
			isConnectedToMainSettlement: this.getInternalTileValue(
				tileIndex,
				TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
			),
			isBlockedByEnemy: this.getInternalTileValue(
				tileIndex,
				TileEntityParam.IS_BLOCKED_BY_ENEMY,
			),
		};
	}

	public createEntityByFrameName0(
		frameName: FrameName,
		coordinates: TileCoordinates,
	): TileEntity | null {
		if (!tilerepoLayer0FrameNames.has(frameName)) return null;

		const cacheKey =
			`${this.getTileIndexByTileCoordinates(coordinates)}|${frameName}` as TileEntityCacheKey;
		if (this.entityCache.has(cacheKey))
			return this.entityCache.get(cacheKey)!;

		const entity =
			createTerrainTileEntity(frameName, coordinates, this) ||
			createPoiTileEntity(frameName, coordinates, this) ||
			null;
		if (entity)
			this.entityCache.set(`${entity.index}|${entity.frameName}`, entity);

		return entity;
	}

	public createEntityByFrameName2(
		frameName: FrameName,
		coordinates: TileCoordinates,
	): TileEntity | null {
		if (!tilerepoLayer2FrameNames.has(frameName)) return null;

		const cacheKey =
			`${this.getTileIndexByTileCoordinates(coordinates)}|${frameName}` as TileEntityCacheKey;
		if (this.entityCache.has(cacheKey))
			return this.entityCache.get(cacheKey)!;

		const entity =
			createBuildingTileEntity(frameName, coordinates, this) ||
			createRoadTileEntity(frameName, coordinates, this) ||
			createSalvageableTileEntity(frameName, coordinates, this) ||
			null;
		if (entity)
			this.entityCache.set(`${entity.index}|${entity.frameName}`, entity);

		return entity;
	}

	public createEntityByFrameName(
		frameName: FrameName,
		coordinates: TileCoordinates = {
			x: SAFE_COORDINATE_OUTSIDE_MAP,
			y: SAFE_COORDINATE_OUTSIDE_MAP,
		},
	): TileEntity | null {
		return (
			this.createEntityByFrameName2(frameName, coordinates) ||
			this.createEntityByFrameName0(frameName, coordinates) ||
			null
		);
	}

	public getTileEntityFactoriesByTileIndex(
		tileIndex: TileIndex,
	): Tuple<() => TileEntity | null, TileRepositoryLayerCount> {
		const coordinates = this.getTileCoordinatesByTileIndex(tileIndex);

		// da sa pouzit toto getTileIdsByTileIndex
		return [
			(): TileEntity | null =>
				this.tileIdToFrameNameCache.has(
					this.getInternalTileValue(tileIndex, 0),
				)
					? this.createEntityByFrameName0(
							this.tileIdToFrameNameCache.get(
								this.getInternalTileValue(tileIndex, 0),
							) as FrameName,
							coordinates,
						)
					: null,
			(): TileEntity | null => null,
			(): TileEntity | null =>
				this.tileIdToFrameNameCache.has(
					this.getInternalTileValue(tileIndex, 2),
				)
					? this.createEntityByFrameName2(
							this.tileIdToFrameNameCache.get(
								this.getInternalTileValue(tileIndex, 2),
							) as FrameName,
							coordinates,
						)
					: null,
			(): TileEntity | null => null,
		];
	}

	private setRandomSettlementEmptyFrameNameByTileIndex(
		tileIndex: TileIndex,
		init?: boolean,
	): void {
		const {
			SETTLEMENT_EMPTY_TILE_ID,
			SETTLEMENT_EMPTY_TILE_IDS,
			SETTLEMENT_CASTLE_TILE_IDS,
		} = this;

		const tileId0 = this.getInternalTileValue(tileIndex, 0);
		const tileId2 = this.getInternalTileValue(tileIndex, 2);

		if (
			(init
				? tileId0 === SETTLEMENT_EMPTY_TILE_ID
				: SETTLEMENT_EMPTY_TILE_IDS.has(tileId0)) &&
			!SETTLEMENT_CASTLE_TILE_IDS.has(tileId2)
		) {
			const neighbours = this.getNeighbourTileIndexesByTileCoordinates(
				this.getTileCoordinatesByTileIndex(tileIndex),
				2,
				false,
				ALL_VISIBILITY_TYPES,
			).reduce((acc: Set<FrameName>, tileIndex: TileIndex) => {
				const tileId0 = this.getInternalTileValue(tileIndex, 0);
				if (SETTLEMENT_EMPTY_TILE_IDS.has(tileId0))
					acc.add(this.getFrameNameByTileId(tileId0)!);
				return acc;
			}, new Set<FrameName>());
			const unusedFrameNames = Array.from(
				tilerepoSettlementEmptyFrameNames,
			).filter(
				(frameName: FrameName): boolean => !neighbours.has(frameName),
			);
			const unusedFrameName =
				unusedFrameNames[randomInteger(0, unusedFrameNames.length - 1)];

			this.setInternalTileValue(
				tileIndex,
				0,
				this.getTileIdByFrameName(unusedFrameName)!,
			);
		}
	}

	private getRandomGrasslandFrameName(): FrameName {
		const frameNames = [
			FrameName.GRASSLAND1,
			FrameName.GRASSLAND2,
			FrameName.GRASSLAND3,
			FrameName.GRASSLAND4,
		];
		return frameNames[Math.trunc(Math.random() * frameNames.length)];
	}

	/**
	 * Metóda sa využíva na vkladanie a odoberanie konkrétnych frames do konkrétnych layerov. Jedná sa o naše
	 * interné layery, nie SpriteGPULayery vo Phasery. Po zadaní x/y a frameName metóda podľa svojej internej logiky
	 * pridá TileEntity do príslušnej vrstvy.
	 * @param x
	 * @param y
	 * @param frameName
	 * @param noRefresh
	 * @param onlyPalisades
	 */
	public setTileByTileCoordinates(
		{ x, y }: TileCoordinates,
		frameName: FrameName | null,
		noRefresh: boolean = false,
		onlyPalisades: boolean = false,
	): void {
		let noRefreshRoads: boolean = false;
		// TODO: let noRefreshStorage: boolean = false;
		// TODO: let noRefreshProduction: boolean = false;
		let refreshed: boolean = false;
		const tileIndex = this.getTileIndexByTileCoordinates({ x, y });

		// TODO: toto sa da pouzit na viac miestach, napr hned v metode nizsie
		const prevFrameNameForSiegeSystem = this.getFrameNameByTileId(
			this.getTileIdByTileIndex(tileIndex)!,
		);

		this.markSegmentAsDirty({
			tileIndex,
			prevFrameName: this.getFrameNameByTileId(
				this.getInternalTileValue(tileIndex, 2) ||
					this.getInternalTileValue(tileIndex, 0),
			),
			nextFrameName: frameName,
		});

		if (!frameName || frameName === FrameName.NOT_SET) {
			const prevTileId0 = this.getInternalTileValue(tileIndex, 0);
			const prevFrameName0 = this.getFrameNameByTileId(prevTileId0);
			const prevTileId2 = this.getInternalTileValue(tileIndex, 2);
			const prevFrameName2 = this.getFrameNameByTileId(prevTileId2);

			// Toto musi byt este pred zmenou.
			if (
				prevFrameName2 &&
				tilerepoExplorationBuildingsFrameNames.has(prevFrameName2)
			) {
				const entity = this.getTileEntityByTileIndex(tileIndex)!;
				const { production } = entity;
				if (production.has(ResourceType.EXPLORATION_SPEED)) {
					this.resourcesStore.decrease(
						ResourceType.EXPLORATION_SPEED,
						production.get(
							ResourceType.EXPLORATION_SPEED,
						)! as number,
						"setTileByTileCoordinates",
					);
					this.emitter.emit(
						EmitterEvents.PhaserRecalculateExplorationSpeed,
					);
				}
			}

			if (
				tilerepoFarmFieldsFrameNames.has(prevFrameName2!) &&
				frameName === FrameName.NOT_SET
			) {
				// Plus 1 because it should start subtracting from next tick.
				this.lockedTiles.set(tileIndex, LOCKED_FIELD_TICK_COUNT + 1);
			}

			if (
				prevFrameName0 &&
				tilerepoSettlementEmptyFrameNames.has(prevFrameName0)
			) {
				this.setRandomSettlementEmptyFrameNameByTileIndex(tileIndex);
			}

			this.handleBuildingIndexesDecrement(tileIndex);
			this.setInternalTileValue(tileIndex, 2, 0);
			this.setInternalTileValue(
				tileIndex,
				TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				0,
			);

			if (TileEntity.isMounted)
				this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
					coordinates: { x, y },
				});

			if (!this.isUpdatingRoadNeighbors) {
				this.isUpdatingRoadNeighbors = true;
				this.updateSurroundingRoadTiles(x, y);

				// Skontrolujeme, či to, čo sme práve zbúrali (prevFrameName2), bol Shipyard.
				// Ak áno, musíme prebudovať celý graf, aby zmizli neplatné morské cesty.
				// Ak nie, stačí rýchly lokálny update.
				if (
					prevFrameName2 &&
					tilerepoShipyardFrameNames.has(prevFrameName2)
				) {
					this.updateRoadGraph(x, y, true);
				} else {
					this.updateRoadGraph(x, y);
				}

				this.isUpdatingRoadNeighbors = false;
			}

			if (!noRefresh) {
				//tu som vlozil aj x/y koordinaty entity ktora sa uz zmazala, lebo inak rendering nevie
				//ze ma oznacit aj x/y neexistujucej entity bez transparetneho cerveneho overlayu
				//lebo refreshMainSettlementConnections sa pozera nanovo na cely layer2 a tam uz ta
				//entita tam nie je, teda vzdy pri burani mi nezmizol overlay a ostal na trave
				this.refreshMainSettlementConnections(
					!this.getInternalTileValue(
						tileIndex,
						TileEntityParam.IS_BLOCKED_BY_ENEMY,
					),
					{ x, y },
				);
			}

			this.emitter.emit(EmitterEvents.PhaserRemoveRangeSelector, {
				x,
				y,
			});

			this.handleSiegeSystemUnitUpgrade(
				tileIndex,
				prevFrameNameForSiegeSystem,
				frameName,
			);

			const sm = useSegmentManager();
			sm.run(true);

			return;
		}

		if (tilerepoLayer0FrameNames.has(frameName) && !onlyPalisades) {
			const prevTileId0 = this.getInternalTileValue(tileIndex, 0);
			const prevFrameName0 = this.getFrameNameByTileId(prevTileId0);

			if (prevFrameName0 && isTerrain(prevFrameName0)) {
				noRefreshRoads = true;
			}

			if (frameName === FrameName.GRASSLAND) {
				// if (
				// 	prevFrameName &&
				// 	tilerepoForestFrameNames.has(prevFrameName)
				// ) {
				// 	frameName = FrameName.RUINED_FOREST;
				// } else {
				// 	frameName =
				// 		GRASSLAND[Math.trunc(Math.random() * GRASSLAND.length)];
				// }

				frameName = this.getRandomGrasslandFrameName();
			}

			// TODO: Vsetky vyskyty treba posunut co najvyssie.
			const tileId = this.getTileIdByFrameName(frameName);
			if (!tileId)
				throw new Error(
					`Tile ID not found for frame name: "${frameName}"!`,
				);

			this.setInternalTileValue(tileIndex, 0, tileId);

			if (TileEntity.isMounted)
				this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
					coordinates: { x, y },
				});
		}

		if (tilerepoLayer1FrameNames.has(frameName)) {
			if (tilerepoPalisades1FrameNames.has(frameName)) {
				const frameName = getSettlementPalisadeFrameNameByCoordinates({
					x,
					y,
				});

				const tileId = this.getTileIdByFrameName(frameName);
				if (!tileId)
					throw new Error(
						`Tile ID not found for frame name: "${frameName}"!`,
					);

				this.setInternalTileValue(tileIndex, 1, tileId);

				if (TileEntity.isMounted)
					this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
						coordinates: { x, y },
					});
			}

			if (tilerepoBeachesFrameNames.has(frameName)) {
				const tileId = this.getTileIdByFrameName(frameName);
				if (!tileId)
					throw new Error(
						`Tile ID not found for frame name: "${frameName}"!`,
					);

				this.setInternalTileValue(tileIndex, 1, tileId);

				if (TileEntity.isMounted)
					this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
						coordinates: { x, y },
					});
			}
		}

		if (tilerepoLayer2FrameNames.has(frameName) && !onlyPalisades) {
			if (tilerepoSettlementCastleBuildingsFrameNames.has(frameName)) {
				frameName = FrameName.SETTLEMENT_CASTLE;
			}

			if (tilerepoRoadsFrameNames.has(frameName)) {
				frameName = this.getCorrectRoadFrameName(x, y, frameName);

				const frameName0 = this.getFrameNameByTileId(
					this.getInternalTileValue(tileIndex, 0),
				);

				if (
					frameName0 &&
					(tilerepoForestFrameNames.has(frameName0) ||
						frameName0 === FrameName.RUINED_FOREST)
				) {
					this.setInternalTileValue(
						tileIndex,
						0,
						this.getTileIdByFrameName(
							this.getRandomGrasslandFrameName(),
						)!,
					);
				}
			}

			const tileId = this.getTileIdByFrameName(frameName);
			if (!tileId)
				throw new Error(
					`Tile ID not found for frame name: "${frameName}"!`,
				);

			this.handleBuildingIndexesDecrement(tileIndex);
			this.setInternalTileValue(tileIndex, 2, tileId);
			this.handleBuildingIndexesIncrement(tileIndex);

			// Toto musi byt az po zmene.
			if (tilerepoExplorationBuildingsFrameNames.has(frameName)) {
				const entity = this.getTileEntityByTileIndex(tileIndex)!;
				const { production } = entity;
				if (production.has(ResourceType.EXPLORATION_SPEED)) {
					this.resourcesStore.increase(
						ResourceType.EXPLORATION_SPEED,
						production.get(
							ResourceType.EXPLORATION_SPEED,
						)! as number,
						"setTileByTileCoordinates",
					);
					this.emitter.emit(
						EmitterEvents.PhaserRecalculateExplorationSpeed,
					);
				}
			}

			// TODO: Toto treba refactornut.
			const entity =
				this.getTileEntityFactoriesByTileIndex(tileIndex)[2]();

			if (TileEntity.isMounted)
				this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
					coordinates: { x, y },
				});

			if (
				(tilerepoRoadsFrameNames.has(frameName) &&
					frameName !== FrameName.DIRT_ROAD_TUTORIAL) ||
				tilerepoBuildingsFrameNames.has(frameName)
			) {
				const isShipyard = tilerepoShipyardFrameNames.has(frameName);

				// Ak staviame loďku na mori, tiež chceme isShipyard správanie (rebuild sea paths)
				const isBoatOnSea =
					tilerepoFishingBoatsFrameNames.has(frameName) ||
					tilerepoBigFishingBoatsFrameNames.has(frameName);

				this.updateRoadGraph(x, y, isShipyard || isBoatOnSea);
			}

			if (entity) {
				//vsetky polia musia byt "odpojene"
				if (tilerepoFarmFieldsFrameNames.has(entity.frameName)) {
					this.setInternalTileValue(
						entity.index,
						TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
						0,
					);
				} else {
					this.setInternalTileValue(
						entity.index,
						TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
						this.isTileConnectedToMainSettlement(x, y) ? 1 : 0,
					);
				}

				if ("onBuild" in entity)
					(entity as TileEntityWithOnBuildMethod).onBuild();

				const neighbours =
					this.getNeighbourTileIndexesByTileCoordinates(
						{ x, y },
						0,
						false,
					).filter(
						(tileIndex: TileIndex): boolean =>
							this.getInternalTileValue(
								tileIndex,
								TileEntityParam.VISIBILITY,
							) === TileVisibilityType.EXPLORED,
					);
				if (neighbours.length > 0) {
					neighbours.forEach((tileIndex: TileIndex): void => {
						const neighbourEntity = this.getTileEntityByTileIndex(
							tileIndex,
						) as TileEntity &
							TileEntityWithOnActivateMethod &
							TileEntityWithOnRevealMethod;
						if (!neighbourEntity) return;

						const hasOnReveal = "onReveal" in neighbourEntity;
						const hasOnActivate = "onActivate" in neighbourEntity;

						// ak je nova entita cesta a susedna este nenapojeny settlement, vyhod hlasku
						if (
							entity.frameName.includes("Road") &&
							entity.isConnectedToMainSettlement &&
							tilerepoSettlementEmptyFrameNames.has(
								neighbourEntity.frameName,
							) &&
							!neighbourEntity.isConnectedToMainSettlement
						) {
							this.emitter.emit(
								EmitterEvents.PhaserSuccessMessage,
								{
									x: entity.x,
									y: entity.y,
									text: "ui.town_connected",
								},
							);
						}

						if (
							(hasOnReveal || hasOnActivate) &&
							!refreshed &&
							!noRefresh &&
							!noRefreshRoads
						) {
							// eslint-disable-next-line no-console
							console.info("early refresh");

							if (useQuickToolStore().active)
								this.debouncedRefreshMainSettlementConnections();
							else this.refreshMainSettlementConnections();
							refreshed = true;
						}

						if (
							!tilerepoPoiFrameNames.has(
								neighbourEntity.frameName,
							)
						) {
							if (hasOnReveal) neighbourEntity.onReveal();
							if (hasOnActivate) neighbourEntity.onActivate();
						}
					});
				}
			}
		}

		if (tilerepoLayer3FrameNames.has(frameName)) {
			if (tilerepoPalisades3FrameNames.has(frameName)) {
				const frameName = getSettlementPalisadeFrameNameByCoordinates({
					x,
					y,
				});

				const tileId = this.getTileIdByFrameName(frameName);
				if (!tileId)
					throw new Error(
						`Tile ID not found for frame name: "${frameName}"!`,
					);

				this.setInternalTileValue(tileIndex, 3, tileId);

				if (TileEntity.isMounted)
					this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
						coordinates: { x, y },
					});
			}
		}

		if (!this.isUpdatingRoadNeighbors && !onlyPalisades) {
			this.isUpdatingRoadNeighbors = true;
			this.updateSurroundingRoadTiles(x, y);
			this.isUpdatingRoadNeighbors = false;
		}

		if (
			!refreshed &&
			!noRefresh &&
			!noRefreshRoads &&
			!onlyPalisades &&
			frameName !== FrameName.DIRT_ROAD_TUTORIAL &&
			!tilerepoSettlementEmptyFrameNames.has(frameName) &&
			!tilerepoSettlementCastleBuildingsFrameNames.has(frameName) &&
			!tilerepoPalisades1FrameNames.has(frameName) &&
			!tilerepoPalisades3FrameNames.has(frameName) &&
			!tilerepoFarmFieldsFrameNames.has(frameName)
		) {
			// eslint-disable-next-line no-console
			console.info("late refresh", frameName);
			if (tilerepoRoadsFrameNames.has(frameName)) {
				useUserStore().setRoadsAndBridges();
			}
			if (useQuickToolStore().active)
				this.debouncedRefreshMainSettlementConnections();
			else this.refreshMainSettlementConnections();
		}

		if (!onlyPalisades) {
			this.emitter.emit(EmitterEvents.PhaserAddRangeSelector, {
				x,
				y,
			});
		}

		this.handleSiegeSystemUnitUpgrade(
			tileIndex,
			prevFrameNameForSiegeSystem,
			frameName,
		);

		const sm = useSegmentManager();
		sm.run(true);
	}

	// https://vueuse.org/shared/useDebounceFn/
	public debouncedRefreshMainSettlementConnections = useDebounceFn(
		this.refreshMainSettlementConnections.bind(this),
		ROAD_REFRESH_DEBOUNCE_TIME_MS,
	);

	// Fallback, ak by debounce nefungoval spravne.
	// Nechavam to tu len ako dokumentaciu, ako by sa to dalo spravit s AbortControllerom.
	// https://medium.com/@bramus/cancel-a-javascript-promise-with-abortcontroller-3540cbbda0a9
	// public abortableRefreshMainSettlementConnections(
	// 	controller: AbortController,
	// 	useRemovedTile?: boolean,
	// 	coordinates?: TileCoordinates,
	// ): void {}

	/**
	 * Metóda má za úlohu po úprave mapy spôsobom - stavania/búrania/modifikácie tiles - prepočítať atribút
	 * isConnectedToMainSettlement pre každú entity ktorá má byť v layery 2 - budovy, salvagables, settlemtny atd. a jednej výnimke
	 * v v layery 0 - FrameName.SETTLEMENT_EMPTY.
	 * Následne pošle zoznam všetkých týchto súradníc x/y do Phaseru aby došlo k vykresleniu / odstráneniu vykreslenia
	 * červeného tintu nad najvyšším memberom - vizualizácia odpojenia od hlavného mesta
	 * @param useRemovedTile
	 * @param coordinates
	 */
	public refreshMainSettlementConnections(
		useRemovedTile?: boolean,
		coordinates?: TileCoordinates,
	): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("refreshMainSettlementConnections");
		}

		// OPTIMALIZÁCIA 1: Efektívnejšie štruktúry pre BFS
		const visited = new Set<number>();
		const queue: number[] = [];
		let queueHead = 0; // Pointer namiesto queue.shift()

		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		// 1. Inicializácia BFS zo štartovacích bodov (Main Settlement Border)
		for (const { x: sx, y: sy } of this.mainSettlementBorderCoords) {
			for (const dir of directions) {
				const nx = sx + dir.dx;
				const ny = sy + dir.dy;

				// Rýchly check bounds, ak je to nutné (getTileIndex to možno robí, ale pre istotu)
				if (
					nx < 0 ||
					nx >= this.MAP_SIZE ||
					ny < 0 ||
					ny >= this.MAP_SIZE
				)
					continue;

				const key = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});

				// Ak je to uzol v grafe, pridáme do fronty
				if (this.roadGraph.has(key)) {
					if (!visited.has(key)) {
						visited.add(key);
						queue.push(key);
					}
				} else {
					// Ak to nie je uzol grafu (napr. len susedná dekorácia), len označíme ako visited
					// aby sa javila ako pripojená, ale nepokračujeme v BFS cez ňu
					visited.add(key);
				}
			}
		}

		// 2. BFS cez graf ciest (Optimalizované s pointerom)
		while (queueHead < queue.length) {
			const current = queue[queueHead++]; // O(1) namiesto O(N) shift()

			const neighbors = this.roadGraph.get(current);
			if (!neighbors) continue;

			for (const neighbor of neighbors) {
				if (!visited.has(neighbor)) {
					visited.add(neighbor);
					queue.push(neighbor);
				}
			}
		}

		const tiles = new Set<TileCoordinatesWithValue>();

		if (useRemovedTile && coordinates) {
			tiles.add({
				x: coordinates.x,
				y: coordinates.y,
				value: true, // true = disconnected overlay (zobraz červenú)
			});
		}

		// 3. Prejdeme relevantné entity
		// OPTIMALIZÁCIA 2: Zjednodušená logika
		// Predpokladáme, že roadGraph je "source of truth". Ak je entita v roadGraph,
		// BFS (visited) už rozhodlo, či je pripojená.

		for (const [frameName, tileIndexes] of this
			.frameNameToTileIndexesCache) {
			// Rýchly skip pre irelevantné kategórie
			if (tilerepoPoiFrameNames.has(frameName)) continue;

			// Zistíme či je tento frameName relevantný (Building alebo Road)
			const isBuilding =
				tilerepoBuildingsFrameNames.has(frameName) &&
				!tilerepoSettlementCastleBuildingsFrameNames.has(frameName);
			const isRoad = tilerepoRoadsFrameNames.has(frameName);

			if (!isBuilding && !isRoad) continue;

			const isTutorialRoad = frameName === FrameName.DIRT_ROAD_TUTORIAL;

			for (const tileIndex of tileIndexes) {
				// Nemusíme volať getTileEntityByTileIndex pre každú iteráciu, ak nám stačí index a visited
				// Ale potrebujeme entity.requirements pre 'requireRoad'

				let isConnectedToMainSettlement = 0;

				if (isTutorialRoad) {
					// Výnimka pre tutoriál
					isConnectedToMainSettlement = 1;
				} else {
					// HLAVNÁ OPTIMALIZÁCIA:
					// Ak je tile vo 'visited', znamená to, že je v grafe A je dostupný z hradu.
					// Nemusíme znova kontrolovať susedov ani mosty - to už vyriešil RoadGraph a BFS.
					if (visited.has(tileIndex)) {
						isConnectedToMainSettlement = 1;
					} else {
						// Fallback: Ak entita nie je v grafe (napr. bug alebo edge case),
						// ale má suseda, ktorý je visited?
						// V korektnom RoadGraphe by každá validná budova/cesta mala byť uzlom.
						// Ak chceme byť super-safe, môžeme nechať check susedov, ale zjednodušený:
						/* const neighbors = this.getNeighbourTileIndexesByTileCoordinates(
                             this.getTileCoordinatesByTileIndex(tileIndex), 1
                        );
                        if (neighbors.some(n => visited.has(n))) isConnectedToMainSettlement = 1;
                        */

						// Pre maximálny výkon veríme grafu:
						isConnectedToMainSettlement = 0;
					}
				}

				// Update interného stavu
				// Pozor: Toto je zápis do poľa (rýchly), ale ak sa hodnota nezmenila, setInternalTileValue
				// v tvojej implementácii emituje eventy. Pre výkon by bolo dobré volať to len pri zmene.
				const currentValue = this.getInternalTileValue(
					tileIndex,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				);
				if (currentValue !== isConnectedToMainSettlement) {
					this.setInternalTileValue(
						tileIndex,
						TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
						isConnectedToMainSettlement,
					);
				}

				// Check requirements - tu musíme získať entitu
				// Toto je drahšie, ale nutné pre vizualizáciu (nie každá budova potrebuje cestu)
				// Môžeme to optimalizovať tak, že entitu získame len ak isConnected == 0

				let showRedOverlay = false;

				if (isConnectedToMainSettlement === 0) {
					const entity = this.getTileEntityByTileIndex(tileIndex);
					if (entity) {
						const needsConnection =
							entity.requirements.has(
								ResourceType.SETTLEMENT_CONNECTION,
							) &&
							entity.requirements.get(
								ResourceType.SETTLEMENT_CONNECTION,
							)! > 0;
						if (needsConnection) {
							showRedOverlay = true;
						}
					}
				}

				// Pridáme do zoznamu na prekreslenie
				// Optimalizácia: Posielame len tie, čo potrebujú vizuálnu zmenu, alebo všetky?
				// Pôvodný kód posielal všetky. Necháme to tak pre konzistenciu renderera.
				const { x, y } = this.getTileCoordinatesByTileIndex(tileIndex);
				tiles.add({
					x,
					y,
					value: !isConnectedToMainSettlement && showRedOverlay, // value = true znamená "zobraz červenú"
					// Pôvodná logika bola: !!isConnected || !requireRoad -> value (čo asi znamenalo "je to OK"?)
					// Tvoj PhaserRenderDisconnect asi očakáva: True = Disconnected, False = Connected?
					// Podľa pôvodného kódu: `value: !!isConnectedToMainSettlement || !requireRoad`
					// Ak je pripojený (true) -> value true.
					// Ak nie je pripojený ale nepotrebuje cestu (true) -> value true.
					// Takže to vyzerá, že posielaš "IsValid", nie "IsDisconnected".
					// Nechám pôvodnú logiku:
				});

				// Pôvodný výpočet pre tiles.add:
				let isValid = isConnectedToMainSettlement === 1;
				if (!isValid) {
					const entity = this.getTileEntityByTileIndex(tileIndex);
					if (
						!entity ||
						!entity.requirements.has(
							ResourceType.SETTLEMENT_CONNECTION,
						) ||
						entity.requirements.get(
							ResourceType.SETTLEMENT_CONNECTION,
						) === 0
					) {
						isValid = true;
					}
				}

				// Tu bol asi bug v pôvodnom sete tiles.add value.
				// Ak PhaserRenderDisconnect vizualizuje červenú, tak zvyčajne posielame "true = error".
				// Ale tvoj kód posielal "true = ok". Skontroluj si to v Phaseri.
				// Predpokladám, že posielaš "status OK".
				tiles.add({ x, y, value: isValid });
			}
		}

		// 4. Settlement Logic (Optimalizované)
		for (const settlement of settlementNames.values()) {
			let isConnected = false;
			const settlementCenterIndex = this.getTileIndexByTileCoordinates({
				x: settlement.x,
				y: settlement.y,
			});

			// Rýchly check centra (zvyčajne stačí)
			if (visited.has(settlementCenterIndex)) {
				isConnected = true;
			} else {
				// Check okolia len ak centrum nie je visited (pre istotu)
				const neighbors = this.getNeighbourTileIndexesByTileCoordinates(
					{ x: settlement.x, y: settlement.y },
					1,
					true,
				);
				if (neighbors.some(idx => visited.has(idx))) {
					isConnected = true;
				}
			}

			// Update a emit labelov
			// Pre SettlementCastleBuildings...
			// Tu musíme iterovať okolie aby sme našli budovu hradu a updatli jej label
			// (Tu sa veľa optimalizovať nedá bez zmeny štruktúry, ale je to len pár tiles)
			const range = this.getNeighbourTileIndexesByTileCoordinates(
				{ x: settlement.x, y: settlement.y },
				1,
				true,
			);

			for (const idx of range) {
				if (
					this.getInternalTileValue(
						idx,
						TileEntityParam.VISIBILITY,
					) !== TileVisibilityType.EXPLORED
				)
					continue;

				const tileId2 = this.getInternalTileValue(idx, 2);
				if (!tileId2) {
					// Ak nie je budova, nastavíme param (pre Settlement Empty)
					this.setInternalTileValue(
						idx,
						TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
						isConnected ? 1 : 0,
					);
					const { x, y } = this.getTileCoordinatesByTileIndex(idx);
					tiles.add({ x, y, value: isConnected });
					continue;
				}

				const frameName = this.getFrameNameByTileId(tileId2);
				if (
					frameName &&
					tilerepoSettlementCastleBuildingsFrameNames.has(frameName)
				) {
					const prevIsConnected =
						this.getInternalTileValue(
							idx,
							TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
						) === 1;

					if (isConnected !== prevIsConnected) {
						const { x, y } =
							this.getTileCoordinatesByTileIndex(idx);
						this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
							coordinates: { x, y },
							skin: TileLabelSkin.LEVEL,
							action: TileLabelAction.UPDATE,
							type: isConnected
								? TileLabelType.SETTLEMENT
								: TileLabelType.SETTLEMENT_NOT_CONNECTED,
						});
					}
				}

				this.setInternalTileValue(
					idx,
					TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
					isConnected ? 1 : 0,
				);
				const { x, y } = this.getTileCoordinatesByTileIndex(idx);
				tiles.add({ x, y, value: isConnected });
			}
		}
		this.emitter.emit(EmitterEvents.PhaserRenderDisconnect, tiles);
		useSegmentManager().run(true);
		QuestRepository.getInstance().manageQuestState(
			QuestObjectiveActions.HAVE_SETTLEMENTS_AXIS,
		);

		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("refreshMainSettlementConnections");
		}
	}

	/**
	 * Metoda sluzi na aktualizaciu pamatoveho grafu reprezentujuceho cesty/budovy/settlementy
	 * skratka vsetko co v hre funguje ako spojnica - cesta. V prípade, že sa táto metóda používa
	 * pri aktualizácii grafu pri stavbe prístavu, dochádza k vytvoreniu "morskej cesty". To znamena, že
	 * sa pridelia x/y súradnice do grafu jednotlivých morských tiles a prepoja sa s najbliším prístavom prepojeným
	 * s hlavným settlementom
	 * @param x
	 * @param y
	 * @param isShipyard
	 * @private
	 */
	private updateRoadGraph(x: number, y: number, isShipyard?: boolean): void {
		const tileIndex = this.getTileIndexByTileCoordinates({ x: x, y: y });
		const neighborDirections =
			this.getDefaulNonDiagonalNeighborCoordinates();

		const frameName0 = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 0),
		);
		const frameName2 = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 2),
		);

		// 1. Validácia: Zistíme, či tento tile môže byť uzlom (Sea, Road, Building, Settlement Empty)
		let isGraphNode = this.isValidGraphNode(frameName0, frameName2);

		// Vylúčime len farmy (FarmFields), ktoré sú na súši a neslúžia ako cesta
		if (frameName2 && tilerepoFarmFieldsFrameNames.has(frameName2)) {
			isGraphNode = false;
		}

		// 2. Čistenie: Ak to nie je validný uzol, zmažeme ho a odpojíme susedov
		if (!isGraphNode || !frameName0 || frameName0 === FrameName.NOT_SET) {
			if (this.roadGraph.has(tileIndex)) {
				this.roadGraph.delete(tileIndex);
				for (const dir of neighborDirections) {
					const nx = x + dir.dx;
					const ny = y + dir.dy;
					if (
						nx < 0 ||
						nx >= this.MAP_SIZE ||
						ny < 0 ||
						ny >= this.MAP_SIZE
					)
						continue;

					const neighborKey = this.getTileIndexByTileCoordinates({
						x: nx,
						y: ny,
					});
					this.roadGraph.get(neighborKey)?.delete(tileIndex);
				}
				if (isShipyard) this.rebuildShipyardConnections(tileIndex);
			}
			return;
		}

		// 4. Aktualizácia spojení so susedmi
		const neighbors = new Set<number>();
		for (const dir of neighborDirections) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;

			if (nx < 0 || nx >= this.MAP_SIZE || ny < 0 || ny >= this.MAP_SIZE)
				continue;

			const neighborKey = this.getTileIndexByTileCoordinates({
				x: nx,
				y: ny,
			});
			const neighborFrame0 = this.getFrameNameByTileId(
				this.getInternalTileValue(neighborKey, 0),
			);
			const neighborFrame2 = this.getFrameNameByTileId(
				this.getInternalTileValue(neighborKey, 2),
			);

			let isNeighborGraphNode = this.isValidGraphNode(
				neighborFrame0,
				neighborFrame2,
			);
			// Susedné pole (FarmField) nie je cesta
			if (
				neighborFrame2 &&
				tilerepoFarmFieldsFrameNames.has(neighborFrame2)
			) {
				isNeighborGraphNode = false;
			}

			if (!isNeighborGraphNode) {
				if (this.roadGraph.has(neighborKey))
					this.roadGraph.get(neighborKey)!.delete(tileIndex);
				continue;
			}

			// Kontrola kompatibility (Zem k zemi, Voda k vode, Shipyard ku všetkému)
			// OPRAVA: Posielame pôvodné frameName0 a frameName2
			if (
				!this.areGraphNodesCompatible(
					frameName0,
					frameName2,
					neighborFrame0,
					neighborFrame2,
				)
			) {
				if (this.roadGraph.has(neighborKey))
					this.roadGraph.get(neighborKey)!.delete(tileIndex);
				continue;
			}

			// -------------------- Logika Mostov (Bridge Restrictions)
			let bridgeCompatible = true;
			if (
				frameName2 &&
				tilerepoBridgeRoadsFrameNames.has(frameName2 as FrameName)
			) {
				// Horizontálny most (L/R)
				if (
					[
						FrameName.WOODEN_BRIDGE_1,
						FrameName.STONE_BRIDGE_1,
						FrameName.FORD_CROSSING_1,
					].includes(frameName2 as FrameName)
				) {
					if (dir.dx === 0) bridgeCompatible = false; // Ignoruj vertikálnych susedov
				}
				// Vertikálny most (U/D)
				if (
					[
						FrameName.WOODEN_BRIDGE_2,
						FrameName.STONE_BRIDGE_2,
						FrameName.FORD_CROSSING_2,
					].includes(frameName2 as FrameName)
				) {
					if (dir.dy === 0) bridgeCompatible = false; // Ignoruj horizontálnych susedov
				}
			}

			// To isté musíme skontrolovať aj pre suseda, ak je on most!
			if (
				neighborFrame2 &&
				tilerepoBridgeRoadsFrameNames.has(neighborFrame2 as FrameName)
			) {
				if (
					[
						FrameName.WOODEN_BRIDGE_1,
						FrameName.STONE_BRIDGE_1,
						FrameName.FORD_CROSSING_1,
					].includes(neighborFrame2 as FrameName)
				) {
					if (dir.dx === 0) bridgeCompatible = false;
				}
				if (
					[
						FrameName.WOODEN_BRIDGE_2,
						FrameName.STONE_BRIDGE_2,
						FrameName.FORD_CROSSING_2,
					].includes(neighborFrame2 as FrameName)
				) {
					if (dir.dy === 0) bridgeCompatible = false;
				}
			}

			if (!bridgeCompatible) {
				if (this.roadGraph.has(neighborKey))
					this.roadGraph.get(neighborKey)!.delete(tileIndex);
				continue;
			}

			// Ak sme prešli všetkými kontrolami, vytvoríme/potvrdíme spojenie
			neighbors.add(neighborKey);
			if (!this.roadGraph.has(neighborKey))
				this.roadGraph.set(neighborKey, new Set());
			this.roadGraph.get(neighborKey)!.add(tileIndex);
		}

		this.roadGraph.set(tileIndex, neighbors);
		if (isShipyard) this.rebuildShipyardConnections(tileIndex);
	}

	/**
	 * Metoda sluzi na aktualizaciu okololotych cestnych frames
	 * primárne nesluzi na aktualizaciu grafu(ale aj to sa deje), len vizualnu aktualizaciu
	 * ciest v okoli novo postavenej cesty
	 * @param x
	 * @param y
	 * @private
	 */
	private updateSurroundingRoadTiles(x: number, y: number): void {
		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		for (const dir of directions) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;
			const tileIndex = this.getTileIndexByTileCoordinates({
				x: nx,
				y: ny,
			});
			const entity =
				this.getTileEntityFactoriesByTileIndex(tileIndex)[2]();
			if (!entity) continue;

			const currentFrameName = entity.frameName;
			if (!tilerepoRoadsFrameNames.has(currentFrameName)) continue;

			// Prepočítať nový frame
			const newFrameName = this.getCorrectRoadFrameName(
				nx,
				ny,
				currentFrameName,
			);

			// Aplikovať zmenu len vtedy ak susedny tile je cesta ktora sa
			//vizualne musi zmenit. Inak sa táto operácia nevolá pri
			//búrani ničoho, ani cesty
			if (newFrameName !== currentFrameName) {
				this.setTileByTileCoordinates(
					{ x: nx, y: ny },
					newFrameName,
					true,
				);
			}
		}
	}

	/**
	 * Wrapper ktorý umožňuje identifikovať či je tile spojený cestou - suchozemská, morská, budovy atd
	 * s hlavným settlementov. V princípe sa jedná len o použitie metódy isBuildingConnectedByRoadWithDifferentBuilding
	 * s hardcoded main settlement tiles
	 * @param x
	 * @param y
	 */
	public isTileConnectedToMainSettlement(x: number, y: number): boolean {
		return this.isBuildingConnectedByRoadWithDifferentBuilding(
			x,
			y,
			this.mainSettlementBorderCoords,
		);
	}

	/**
	 * EXTREMNE DOLEZITA METODA!!! Sluzi ako základný kamen pri identifikácií, v reálnom čase, či je akýkoľvek tile
	 * spojený s iným tilom
	 * @param xFrom
	 * @param yFrom
	 * @param coordinatesTo
	 * @private
	 */
	private isBuildingConnectedByRoadWithDifferentBuilding(
		xFrom: number,
		yFrom: number,
		coordinatesTo: { x: number; y: number }[],
	): boolean {
		const visited = new Set<number>();
		const queue: number[] = [];

		const targetNeighbors: number[] = this.getNeighborRoadKeys(
			xFrom,
			yFrom,
		);
		if (targetNeighbors.length === 0) return false;

		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		// Cestné tiles okolo tiles ku ktoremu sa chceme dostat
		const mainRoadKeys = new Set<number>();
		for (const { x: sx, y: sy } of coordinatesTo) {
			for (const dir of directions) {
				const nx = sx + dir.dx;
				const ny = sy + dir.dy;
				const key = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});
				if (this.roadGraph.has(key)) {
					mainRoadKeys.add(key);
				}
			}
		}

		// Spustíme BFS z každého "susedného cestného uzla"
		for (const startKey of targetNeighbors) {
			queue.push(startKey);
			visited.add(startKey);
		}

		while (queue.length > 0) {
			const current = queue.shift()!;
			if (mainRoadKeys.has(current)) return true;

			const neighbors = this.roadGraph.get(current);
			if (!neighbors) continue;

			for (const neighbor of neighbors) {
				if (!visited.has(neighbor)) {
					visited.add(neighbor);
					queue.push(neighbor);
				}
			}
		}

		return false;
	}

	/**
	 * Metóda slúži na získanie pola tileIndexov reprezentujúce tiles, ktoré reprezentujú cesty a susedia
	 * s tile na koordinátoch x/y zaslaných ako argumenty. Využíva sa nie na rendering účeli ale na prehľadávanie
	 * roadGraph štruktúry
	 * @param x
	 * @param y
	 * @private
	 */
	private getNeighborRoadKeys(x: number, y: number): number[] {
		const keys: number[] = [];
		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		for (const dir of directions) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;
			const key = this.getTileIndexByTileCoordinates({ x: nx, y: ny });

			if (this.roadGraph.has(key)) {
				keys.push(key);
			}
		}

		return keys;
	}

	/**
	 * Metóda ktorej úlohou je vrátiť korektný frameName (textúru s atlasu) pre entitu cesta/most.
	 * Keďže funkcionálne robia všetky tieto entity to isté, nie je potrebné s nimi špeciálne manipulovať
	 * jedná sa len o vizuálnu korekciu s dôvodu nadpájania ciest.
	 * @param x
	 * @param y
	 * @param frameName
	 */
	public getCorrectRoadFrameName(
		x: number,
		y: number,
		frameName: FrameName,
	): FrameName {
		if (frameName === FrameName.DIRT_ROAD_TUTORIAL) return frameName;

		if (tilerepoTunnelRoadsFrameNames.has(frameName)) {
			return frameName;
		} else if (
			tilerepoFordCrossingRoadsFrameNames.has(frameName) ||
			tilerepoWoodenBridgeRoadsFrameNames.has(frameName) ||
			tilerepoStoneBridgeRoadsFrameNames.has(frameName)
		) {
			const ny = y + 1;
			const neighborTileIndex = this.getTileIndexByTileCoordinates({
				x: x,
				y: ny,
			});
			const terrainEntity =
				this.getTileEntityFactoriesByTileIndex(neighborTileIndex)[0]();
			if (!terrainEntity) return frameName;

			let currentBridgeFrameName = "";
			if (frameName.startsWith("Ford_Crossing")) {
				currentBridgeFrameName = "Ford_Crossing_";
			} else if (frameName.startsWith("Bridge_Wooden")) {
				currentBridgeFrameName = "Bridge_Wooden_";
			} else if (frameName.startsWith("Bridge_Stone")) {
				currentBridgeFrameName = "Bridge_Stone_";
			}

			if (tilerepoRiversFrameNames.has(terrainEntity.frameName)) {
				currentBridgeFrameName = currentBridgeFrameName + "L_R.png";
			} else {
				currentBridgeFrameName = currentBridgeFrameName + "U_D.png";
			}

			return currentBridgeFrameName as FrameName;
		} else {
			let roadType;
			if (tilerepoDirtRoadsFrameNames.has(frameName)) {
				roadType = "Dirt";
			} else if (tilerepoCobbledRoadsFrameNames.has(frameName)) {
				roadType = "Cobbled";
			} else {
				roadType = "Gravel";
			}

			let currentFrameName = "Road_" + roadType + "_";
			let codeName = "";

			const directions = [
				{ name: "R", dx: 1, dy: 0 },
				{ name: "L", dx: -1, dy: 0 },
				{ name: "D", dx: 0, dy: 1 },
				{ name: "U", dx: 0, dy: -1 },
			];

			for (const dir of directions) {
				const nx = x + dir.dx;
				const ny = y + dir.dy;
				const neighborTileIndex = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});
				const entity2 =
					this.getTileEntityFactoriesByTileIndex(
						neighborTileIndex,
					)[2]();
				const entity0 =
					this.getTileEntityFactoriesByTileIndex(
						neighborTileIndex,
					)[0]();

				// Budovy, salvageables, settlement blato
				const isRoadRelevantEntity =
					(entity2 &&
						!tilerepoFarmFieldsFrameNames.has(entity2.frameName) &&
						!tilerepoSalvageableFrameNames.has(entity2.frameName) &&
						!tilerepoFishingBoatsFrameNames.has(
							entity2.frameName,
						) &&
						!tilerepoBigFishingBoatsFrameNames.has(
							entity2.frameName,
						)) ||
					(entity0 &&
						tilerepoSettlementEmptyFrameNames.has(
							entity0.frameName,
						));

				if (!isRoadRelevantEntity) continue;

				codeName += `_1_${dir.name}`;
			}

			switch (codeName) {
				case "_1_R":
				case "_1_L":
				case "_1_R_1_L":
					currentFrameName += "L_R.png";
					break;
				case "_1_D":
				case "_1_U":
				case "_1_D_1_U":
					currentFrameName += "U_D.png";
					break;
				case "_1_R_1_D":
					currentFrameName += "R_D.png";
					break;
				case "_1_R_1_U":
					currentFrameName += "U_R.png";
					break;
				case "_1_L_1_D":
					currentFrameName += "L_D.png";
					break;
				case "_1_L_1_U":
					currentFrameName += "U_L.png";
					break;
				case "_1_R_1_L_1_D":
					currentFrameName += "T_D.png";
					break;
				case "_1_R_1_L_1_U":
					currentFrameName += "T_U.png";
					break;
				case "_1_R_1_D_1_U":
					currentFrameName += "T_R.png";
					break;
				case "_1_L_1_D_1_U":
					currentFrameName += "T_L.png";
					break;
				case "_1_R_1_L_1_D_1_U":
					currentFrameName += "X.png";
					break;
				default:
					currentFrameName += "U_D.png"; // fallback
			}

			return currentFrameName as FrameName;
		}
	}

	/**
	 * Metóda ktorá identifukuje, či tile na koordinátoch x/y susedí so settlementom. Nebavíme sa o
	 * samotnej budove settlementu ale s tým blatom na ktorom je možné stavať normálne budovy. Metóda slúži
	 * pre identifikáciu výnimky pre Vue.js komponent TileDetailView
	 * @param x
	 * @param y
	 */
	public isNeighborToSettlement(x: number, y: number): boolean {
		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		for (const dir of directions) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;

			const neighborTileIndex = this.getTileIndexByTileCoordinates({
				x: nx,
				y: ny,
			});

			if (
				this.getInternalTileValue(
					neighborTileIndex,
					TileEntityParam.VISIBILITY,
				) !== TileVisibilityType.EXPLORED
			)
				continue;

			const entity =
				this.getTileEntityFactoriesByTileIndex(neighborTileIndex)[0]();
			if (!entity) return false;

			if (
				tilerepoSettlementEmptyFrameNames.has(entity.frameName) &&
				entity.isConnectedToMainSettlement
			) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Metóda identifikuje, či aktuálny tile na x/y je susedným tile ktorý je spojený s main settlementom.
	 * Metóda slúži pre identifikáciu výnimky pre Vue.js komponent TileDetailView
	 * @param x
	 * @param y
	 */
	public isNeighborToEntityConnectedToMainSettlement(
		x: number,
		y: number,
	): boolean {
		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		for (const dir of directions) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;

			const neighborTileIndex = this.getTileIndexByTileCoordinates({
				x: nx,
				y: ny,
			});

			if (
				this.getInternalTileValue(
					neighborTileIndex,
					TileEntityParam.VISIBILITY,
				) !== TileVisibilityType.EXPLORED
			)
				continue;

			const entity =
				this.getTileEntityFactoriesByTileIndex(neighborTileIndex)[2]();
			if (!entity) continue;

			// Budova vedla mostu sa nema dat stavat.
			let isParallelBridge = false;
			if (tilerepoBridgeRoadsFrameNames.has(entity.frameName)) {
				if (dir.dx === 0 && entity.frameName.endsWith("_L_R.png"))
					isParallelBridge = true;
				if (dir.dy === 0 && entity.frameName.endsWith("_U_D.png"))
					isParallelBridge = true;
			}

			if (!isParallelBridge && entity.isConnectedToMainSettlement)
				return true;
		}

		return false;
	}

	private isNeighbourToSettlement({ x, y }: TileCoordinates): boolean {
		return this.getNeighbourTileIndexesByTileCoordinates({
			x,
			y,
		}).some(
			(tileIndex: TileIndex): boolean =>
				!!this.getTileEntityByTileIndex(tileIndex)
					?.isConnectedToMainSettlement,
		);
	}

	public getTileEntityByTileIndex(tileIndex: TileIndex): TileEntity | null {
		const factories = this.getTileEntityFactoriesByTileIndex(tileIndex);
		return factories[2]() || factories[0]() || null;
	}

	public getTileEntityByTileCoordinates({
		x,
		y,
	}: TileCoordinates): TileEntity | null {
		const tileIndex = this.getTileIndexByTileCoordinates({ x, y });
		return this.getTileEntityByTileIndex(tileIndex);
	}

	public isTileLocked(tileIndex: TileIndex): boolean {
		return this.lockedTiles.has(tileIndex);
	}

	// Ak radius je 0, tak vyberie susedov len po horizontalnej a vertikalnej osi.
	// Ak je kladne cislo, tak vyberie vsetkych susedov okolo.
	public getNeighbourTileIndexesByTileCoordinates(
		{ x, y }: TileCoordinates,
		// TODO: as object param
		radius: number = 1,
		includeSelf: boolean = false,
		visbilities: Set<TileVisibilityType> = new Set([
			TileVisibilityType.EXPLORED,
		]),
		isSettlementQuest?: boolean,
	): Array<TileIndex> {
		if (radius < 0) throw new Error("Invalid radius!");

		const neighbours: Array<TileIndex> = [];
		const isRadius0 = radius === 0;
		if (isRadius0) radius = 1;

		for (let i = -radius; i <= radius; i++) {
			for (let j = -radius; j <= radius; j++) {
				if (i === 0 && j === 0 && !includeSelf) continue;

				// condition for neighbours only vertical and horizontal
				if (isRadius0 && Math.abs(i) === 1 && Math.abs(j) === 1)
					continue;

				const tileIndex = this.getTileIndexByTileCoordinates({
					x: x + j,
					y: y + i,
				});

				if (isSettlementQuest) {
					const isOutOfBounds = this.getTileIdsByTileIndex(
						tileIndex,
					).every((tileId: number): boolean => tileId === 0);
					if (isOutOfBounds) {
						neighbours.push(tileIndex);
						continue;
					}
				}

				if (
					!visbilities.has(
						this.getInternalTileValue(
							tileIndex,
							TileEntityParam.VISIBILITY,
						),
					)
				)
					continue;

				neighbours.push(tileIndex);
			}
		}

		return neighbours;
	}

	public getTileCoordinatesByTileIndex(
		tileIndex: TileIndex,
	): TileCoordinates {
		return {
			x: tileIndex % this.MAP_SIZE,
			y: Math.trunc(tileIndex / this.MAP_SIZE),
		};
	}

	public getTileIndexByTileCoordinates(
		{ x, y }: TileCoordinates,
		layerIndex?: number,
	): TileIndex {
		const { MAP_SIZE } = this;
		const index = x + y * MAP_SIZE;
		if (!layerIndex) return index;
		return index + layerIndex * MAP_SIZE ** 2;
	}

	public getParam(
		tileIndex: TileIndex,
		name: keyof TileEntityParams,
	): number | null {
		return this.getInternalTileValue(tileIndex, name) ?? null;
	}

	public setParam(
		tileIndex: TileIndex,
		name: keyof TileEntityParams,
		value: number,
	): void {
		this.setInternalTileValue(tileIndex, name, value);
	}

	public getBuildingCountByFrameName(
		frameName: FrameName,
		onlyConnected?: boolean,
	): number {
		if (onlyConnected) {
			const tileIndexes = this.frameNameToTileIndexesCache.get(frameName);
			if (!tileIndexes?.size) return 0;

			return Array.from(tileIndexes).filter((tileIndex: TileIndex) => {
				const entity = this.getTileEntityByTileIndex(tileIndex);
				if (!entity) return false;
				return entity.isConnectionToMainSettlementActive;
			}).length;
		}

		return this.frameNameToTileIndexesCache.get(frameName)?.size ?? 0;
	}

	public async onTick(): Promise<void> {
		this.checkInactivity();
		await pMap(
			[
				this.handleLockedTiles.bind(this),
				this.handleForestTiles.bind(this),
				this.handleSettlementCastleTiles.bind(this),
				this.handleFarmTiles.bind(this),
				(): void => void useSegmentManager().run(false, true),
			],
			async (callback: CallableFunction): Promise<void> =>
				await callback(),
			{
				concurrency: CONCURRENCY_LIMIT,
			},
		);
		eventRepository.onTick();
		this.checkIncomeBoostTimeout();
	}

	private checkInactivity(): void {
		// check if 15 minutes from last click lasted
		const timeNow = Date.now();
		const runtimeStore = useRuntimeStore();
		if (runtimeStore.lastClickTimestamp === null) {
			// ak neklikol vobec, nastavim aktualny cas
			runtimeStore.setLastClickTimestamp(timeNow);
		}
		if (runtimeStore.lastCameraChangeTimestamp === null) {
			// ak neklikol vobec, nastavim aktualny cas
			runtimeStore.setLastCameraChangeTimestamp(timeNow);
		}
		if (
			runtimeStore.lastClickTimestamp &&
			timeNow - runtimeStore.lastClickTimestamp! >= INACTIVITY_TIMEOUT
		) {
			this.emitter.emit(EmitterEvents.GameShowInactivePopup);
			return;
		}
		if (
			runtimeStore.lastCameraChangeTimestamp &&
			timeNow - runtimeStore.lastCameraChangeTimestamp! >=
				INACTIVITY_TIMEOUT
		) {
			this.emitter.emit(EmitterEvents.GameShowInactivePopup);
			return;
		}
	}

	public checkIncomeBoostTimeout(): void {
		const runtimeStore = useRuntimeStore();
		const userStore = useUserStore();
		if (!runtimeStore.incomeBoostActive) return;
		userStore.setIncomeBoostTicksRemaining(
			userStore.incomeBoostTicksRemaining - 1,
		);
		if (userStore.incomeBoostTicksRemaining <= 0) {
			if (
				runtimeStore.incomeBoostActive &&
				userStore.incomeBoostsUsedToday <
					BOOST_MAX +
						getUpgradeCurrentReplaceValueById(UpgradeIds.witch_lake)
			) {
				runtimeStore.setInfoNotifications(1);
			}
			runtimeStore.setIncomeBoostActive(false);
		}
	}

	public handlePoiIndexesIncrement(
		tileIndex: TileIndex,
		isInit?: boolean,
	): void {
		const frameName = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 0),
		);
		if (
			!frameName ||
			!this.frameNameToTileIndexesCache.has(frameName) ||
			!tilerepoPoiFrameNames.has(frameName)
		)
			return;

		this.frameNameToTileIndexesCache.get(frameName)!.add(tileIndex);

		if (isInit) return;

		useSegmentManager().run(true);
	}

	private handleBuildingIndexesIncrement(tileIndex: TileIndex): void {
		const frameName = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 2),
		);
		if (
			!frameName ||
			!this.frameNameToTileIndexesCache.has(frameName) ||
			(!tilerepoBuildingsFrameNames.has(frameName) &&
				!tilerepoRoadsFrameNames.has(frameName) &&
				!tilerepoFightableEnemiesFrameNames.has(frameName))
		)
			return;

		this.frameNameToTileIndexesCache.get(frameName)!.add(tileIndex);
	}

	private handleBuildingIndexesDecrement(tileIndex: TileIndex): void {
		const frameName = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 2),
		);
		if (!frameName || !this.frameNameToTileIndexesCache.has(frameName))
			return;

		this.frameNameToTileIndexesCache.get(frameName)!.delete(tileIndex);
	}

	private handleLockedTiles(): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("handleLockedTiles");
		}
		for (const [tileIndex, ticks] of this.lockedTiles.entries()) {
			if (ticks <= 1) {
				this.lockedTiles.delete(tileIndex);
				continue;
			}

			this.lockedTiles.set(tileIndex, ticks - 1);
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("handleLockedTiles");
		}
	}

	private handleForestTiles(): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("handleForestTiles");
		}
		const forests = new Set<TileIndex>();

		const lumberMillTileIndexes = Array.from(tilerepoLumberMillsFrameNames)
			.map(
				(frameName: FrameName): Array<TileIndex> =>
					Array.from(
						this.frameNameToTileIndexesCache.get(frameName) || [],
					),
			)
			.flat();

		for (const tileIndex of lumberMillTileIndexes) {
			const entity = this.getTileEntityByTileIndex(tileIndex);
			if (!entity || !entity.range) continue;

			const neighbours = this.getNeighbourTileIndexesByTileCoordinates(
				this.getTileCoordinatesByTileIndex(tileIndex),
				entity.range,
			)
				.map((tileIndex: TileIndex): TileNeighbour | null => {
					const [tileId] = this.getTileIdsByTileIndex(tileIndex);
					if (!tileId) return null;

					const frameName = this.getFrameNameByTileId(tileId);
					if (!frameName) return null;

					return {
						tileIndex,
						tileId,
						frameName,
					};
				})
				.filter(
					(neighbour: TileNeighbour | null): boolean =>
						neighbour !== null &&
						tilerepoForestFrameNames.has(neighbour.frameName),
				) as Array<TileNeighbour>;

			neighbours.forEach((neighbour: TileNeighbour): void => {
				if (forests.has(neighbour.tileIndex)) return;
				forests.add(neighbour.tileIndex);
			});
		}

		for (const forest of forests) {
			const entity = this.getTileEntityByTileIndex(
				forest,
			) as ForestTerrainTileEntity | null;
			if (!entity) continue;
			if (
				entity.frameName === FrameName.OLD_FOREST &&
				useUpgradesStore().getCurrentLevelById(
					UpgradeIds.reforestation,
				) > 0
			)
				continue;

			const assignedLumberMillTileIndexes =
				entity.assignedLumberMillTileIndexes ?? [];

			switch (assignedLumberMillTileIndexes.length) {
				case 0: {
					// nothing
					break;
				}
				case 1: {
					// nothing
					break;
				}
				case 2: {
					// nothing
					break;
				}
				case 3: {
					if (randomInteger(0, 100) <= 50) {
						this.setTileByTileCoordinates(
							this.getTileCoordinatesByTileIndex(forest),
							FrameName.RUINED_FOREST,
							true,
						);
					}
					break;
				}
				case 4: {
					if (randomInteger(0, 100) <= 75) {
						this.setTileByTileCoordinates(
							this.getTileCoordinatesByTileIndex(forest),
							FrameName.RUINED_FOREST,
							true,
						);
					}
					break;
				}
				default: {
					this.setTileByTileCoordinates(
						this.getTileCoordinatesByTileIndex(forest),
						FrameName.RUINED_FOREST,
						true,
					);
					break;
				}
			}
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("handleForestTiles");
		}
	}

	public handleSettlementCastleTiles(multiplier: number = 1): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("handleSettlementCastleTiles");
		}

		const tileIndexes = Array.from(
			this.frameNameToTileIndexesCache.get(FrameName.SETTLEMENT_CASTLE) ||
				[],
		);

		for (const tileIndex of tileIndexes) {
			const entity = this.getTileEntityByTileIndex(
				tileIndex,
			) as SettlementCastleBuildingTileEntity | null;
			if (!entity) continue;

			entity.onTick(multiplier);
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("handleSettlementCastleTiles");
		}
	}

	private handleFarmTiles(): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("handleFarmTiles");
		}

		const tileIndexes = Array.from(tilerepoFarmsFrameNames)
			.map(
				(frameName: FrameName): Array<TileIndex> =>
					Array.from(
						this.frameNameToTileIndexesCache.get(frameName) || [],
					),
			)
			.flat();

		for (const tileIndex of tileIndexes) {
			const entity = this.getTileEntityByTileIndex(
				tileIndex,
			) as WheatFarmBuildingTileEntity | null;
			if (
				!entity ||
				(entity.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
					entity.requirements.get(
						ResourceType.SETTLEMENT_CONNECTION,
					) === 1 &&
					!entity.isConnectedToMainSettlement)
			)
				continue;

			entity.onTick();
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("handleFarmTiles");
		}
	}

	/**
	 * @param offlineMultiplicator - if provided, the method will multiply the production by this value, useful for offline production calculations
	 * @returns Partial<Record<ResourceType, number>>
	 */
	public handleGlobalOfflineProduction(
		offlineMultiplicator: number,
	): Partial<Record<ResourceType, number>> {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("handleGlobalOfflineProduction");
		}

		if (!offlineMultiplicator || offlineMultiplicator <= 0)
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid offlineMultiplicator: ${offlineMultiplicator}`,
			);

		const entities = this.getAllProductionEntities();
		let offlineTickAccumulatorByFrameName =
			this.getOfflineTickAccumulatorByFrameName(entities);
		let offlineTickAccumulator = this.getOfflineTickAccumulator(
			offlineTickAccumulatorByFrameName,
		) as Partial<Record<ResourceType, number>>;

		let offlineTickAccumulatorKeys = Object.keys(
			offlineTickAccumulator,
		) as Array<ResourceType>;
		const penaltyKeys = offlineTickAccumulatorKeys.filter(
			(key: ResourceType): boolean =>
				!RESOURCE_INCOME_REGEXP.test(key) &&
				!RESOURCE_CONSUMPTION_REGEXP.test(key),
		);
		const isAccumulatorPositive = penaltyKeys.every(
			(key: ResourceType): boolean => offlineTickAccumulator[key]! >= 0,
		);

		if (!isAccumulatorPositive) {
			const penalties: Partial<Record<ResourceType, number>> = {};
			for (const key of penaltyKeys) {
				if (offlineTickAccumulator[key]! >= 0) continue;

				// Bacha, tu teoreticky moze nastat delenie nulou. Ale nemalo by byt, nakolko v takom pripade sa uz nejedna o penaltu (podmienka vyssie).
				penalties[key] =
					(offlineTickAccumulator[`${key}Income` as ResourceType] ||
						0) /
					(offlineTickAccumulator[
						`${key}Consumption` as ResourceType
					] || 0);
			}

			offlineTickAccumulatorByFrameName =
				this.getOfflineTickAccumulatorByFrameName(entities, penalties);
			offlineTickAccumulator = this.getOfflineTickAccumulator(
				offlineTickAccumulatorByFrameName,
			) as Partial<Record<ResourceType, number>>;
		}

		const questRewardEntries = Object.entries(
			useQuestStore().rewardsByTick,
		) as Array<[ResourceType, number]>;
		for (const [rewardEnumKey, rewardValue] of questRewardEntries) {
			offlineTickAccumulator[rewardEnumKey] =
				(offlineTickAccumulator[rewardEnumKey] || 0) + rewardValue;
		}

		// pridaj 1 coin za kazde odhalene policko, odpocitaj vodu, ta ma zisk podla upgradu naval_trade
		const {
			exploredTileCount,
			exploredWaterTileCount,
			exploredWaterTileIncome,
		} = this;
		const totalTileIncome =
			exploredTileCount -
			exploredWaterTileCount +
			exploredWaterTileIncome;
		offlineTickAccumulator[ResourceType.COINS] =
			(offlineTickAccumulator[ResourceType.COINS] || 0) + totalTileIncome;

		appendPassiveEventiumToOfflineAccumulator(offlineTickAccumulator);

		const offlineTicks = this.getOfflineTickCount();

		offlineTickAccumulatorKeys = extendAccumulatorKeysWithEventiumIfActive(
			offlineTickAccumulatorKeys,
		);

		const offlineAccumulator: Partial<Record<ResourceType, number>> = {};
		for (const key of offlineTickAccumulatorKeys) {
			if (
				RESOURCE_INCOME_REGEXP.test(key) ||
				RESOURCE_CONSUMPTION_REGEXP.test(key) ||
				offlineTickAccumulator[key]! <= 0
			)
				continue;

			offlineAccumulator[key] =
				offlineTickAccumulator[key]! * offlineTicks;
		}

		const { foodPenaltyCoefficient, populationPenaltyCoefficient } =
			this.getResourcePenalties(entities);

		const offlineAccumulatorKeys = Object.keys(
			offlineAccumulator,
		) as Array<ResourceType>;

		for (const key of offlineAccumulatorKeys) {
			offlineAccumulator[key] = roundNumber(
				offlineAccumulator[key]! *
					foodPenaltyCoefficient *
					populationPenaltyCoefficient *
					(key === ResourceType.EVENTIUM
						? eventOfflineIncomeMultiplier()
						: useUserStore().offlinePercentage) *
					offlineMultiplicator,
			);

			if (offlineAccumulator[key] > 0)
				offlineAccumulator[key] = this.handleResourceCap(
					key,
					offlineAccumulator[key],
				);

			if (offlineAccumulator[key] === 0) delete offlineAccumulator[key];
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("handleGlobalOfflineProduction");
		}

		return offlineAccumulator;
	}

	public getOfflineTickCount(): number {
		const { maxOfflineTime, lastTickBefore } = useUserStore();
		const offlineTime = Math.min(lastTickBefore, maxOfflineTime);
		return Math.trunc(offlineTime / GAME_TICKER_INTERVAL);
	}

	public calculateMissedTickIncome(
		tickCount: number,
	): Partial<Record<ResourceType, number>> {
		const perTickResources = useSegmentManager().run(true);
		const rewards: Partial<Record<ResourceType, number>> = {};

		for (const [resource, perTickValue] of Object.entries(
			perTickResources,
		) as Array<[ResourceType, number]>) {
			if (perTickValue <= 0 || resource.endsWith("Diff")) continue;

			const income = this.handleResourceCap(
				resource,
				perTickValue * tickCount,
			);
			if (income > 0) rewards[resource] = income;
		}

		return rewards;
	}

	private getOfflineTickAccumulatorByFrameName(
		entities: Set<TileEntity>,
		penalties?: Partial<Record<ResourceType, number>>,
	): Partial<Record<FrameName, Partial<Record<ResourceType, number>>>> {
		return Array.from(entities).reduce(
			(
				accumulator: Partial<
					Record<FrameName, Partial<Record<ResourceType, number>>>
				>,
				{ frameName, production, consumption }: TileEntity,
			): Partial<
				Record<FrameName, Partial<Record<ResourceType, number>>>
			> => {
				if (!production.size && !consumption.size) return accumulator;

				if (!accumulator[frameName]) accumulator[frameName] = {};

				const incomeResourceEnumKeys = new Set<ResourceType>();
				if (production.size) {
					const productionEntries = production.entries();
					for (const [
						incomeEnumKey,
						incomeValue,
					] of productionEntries) {
						if (
							[
								ResourceType.FOOD_INCOME,
								ResourceType.POPULATION_INCOME,
							].includes(incomeEnumKey)
						)
							continue;
						if (!RESOURCE_INCOME_REGEXP.test(incomeEnumKey))
							continue;

						const resourceEnumKey = incomeEnumKey.replace(
							RESOURCE_INCOME_REGEXP,
							"",
						) as ResourceType;
						incomeResourceEnumKeys.add(resourceEnumKey);

						accumulator[frameName][incomeEnumKey] =
							(accumulator[frameName][incomeEnumKey] || 0) +
							(incomeValue as number);
						accumulator[frameName][resourceEnumKey] =
							(accumulator[frameName][resourceEnumKey] || 0) +
							(incomeValue as number);
					}
				}

				const consumptionResourceEnumKeys = new Set<ResourceType>();
				if (consumption.size) {
					const consumptionEntries = consumption.entries();
					for (const [
						consumptionEnumKey,
						consumptionValue,
					] of consumptionEntries) {
						if (
							[
								ResourceType.FOOD_CONSUMPTION,
								ResourceType.POPULATION_CONSUMPTION,
							].includes(consumptionEnumKey)
						)
							continue;
						if (
							!RESOURCE_CONSUMPTION_REGEXP.test(
								consumptionEnumKey,
							)
						)
							continue;

						const resourceEnumKey = consumptionEnumKey.replace(
							RESOURCE_CONSUMPTION_REGEXP,
							"",
						) as ResourceType;
						consumptionResourceEnumKeys.add(resourceEnumKey);

						accumulator[frameName][consumptionEnumKey] =
							(accumulator[frameName][consumptionEnumKey] || 0) +
							consumptionValue;
						accumulator[frameName][resourceEnumKey] =
							(accumulator[frameName][resourceEnumKey] || 0) -
							consumptionValue;
					}
				}

				if (
					incomeResourceEnumKeys.size &&
					consumptionResourceEnumKeys.size &&
					penalties
				) {
					const penaltyResourceEnumKeys = Object.keys(
						penalties,
					) as Array<ResourceType>;

					for (const penaltyResourceEnumKey of penaltyResourceEnumKeys) {
						if (
							!consumptionResourceEnumKeys.has(
								penaltyResourceEnumKey,
							)
						)
							continue;

						for (const incomeResourceEnumKey of incomeResourceEnumKeys) {
							if (
								accumulator[frameName][
									incomeResourceEnumKey
								]! <= 0
							)
								continue;

							accumulator[frameName][incomeResourceEnumKey]! *=
								penalties[penaltyResourceEnumKey]!;
						}
					}
				}

				return accumulator;
			},
			{},
		);
	}

	private getOfflineTickAccumulator(
		offlineTickAccumulatorByFrameName: Partial<
			Record<FrameName, Partial<Record<ResourceType, number>>>
		>,
	): Partial<Record<ResourceType, number>> {
		return Object.entries(offlineTickAccumulatorByFrameName).reduce(
			(
				accumulator: Partial<Record<ResourceType, number>>,
				[, resources]: [string, Partial<Record<ResourceType, number>>],
			): Partial<Record<ResourceType, number>> => {
				const resourceEntries = Object.entries(resources) as Array<
					[ResourceType, number]
				>;
				return resourceEntries.length
					? resourceEntries.reduce(
							(
								accumulator: Partial<
									Record<ResourceType, number>
								>,
								[resourceEnumKey, resourceValue],
							): Partial<Record<ResourceType, number>> => {
								accumulator[resourceEnumKey] =
									(accumulator[resourceEnumKey] || 0) +
									resourceValue;
								return accumulator;
							},
							accumulator,
						)
					: accumulator;
			},
			{},
		);
	}

	public getAllProductionEntities(): Set<TileEntity> {
		const entities = new Set<TileEntity>();
		const tileIndexes = new Set<TileIndex>(
			Array.from(this.frameNameToTileIndexesCache.keys())
				.map(
					(frameName: FrameName): Array<TileIndex> =>
						Array.from(
							this.frameNameToTileIndexesCache.get(frameName) ||
								[],
						),
				)
				.flat(),
		);

		for (const tileIndex of tileIndexes) {
			const entity = this.getTileEntityByTileIndex(tileIndex);
			if (!entity) continue;

			// skipneme neodhalene settlementy
			if ("isActive" in entity && !entity.isActive) continue;

			// skipneme budovy, ktore maju byt pripojene na settlement ale nie su
			if (
				entity.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
				entity.requirements.get(ResourceType.SETTLEMENT_CONNECTION) ===
					1 &&
				!entity.isConnectedToMainSettlement
			)
				continue;

			// skipne nepreskumané POI
			if (
				entity instanceof AbstractPoiTileEntity &&
				!entity.isFullyExplored
			)
				continue;

			entities.add(entity);
		}

		return entities;
	}

	private getResourcePenalties(entities?: Set<TileEntity>): {
		foodPenaltyCoefficient: number;
		populationPenaltyCoefficient: number;
	} {
		if (!entities) entities = this.getAllProductionEntities();

		let foodIncome: number = 0;
		let foodConsumption: number = 0;

		let populationIncome: number = 0;
		let populationConsumption: number = 0;

		// collect food and population, penalty does not apply here
		for (const { production, consumption } of entities) {
			if (production.size) {
				if (production.has(ResourceType.FOOD_INCOME))
					foodIncome += production.get(
						ResourceType.FOOD_INCOME,
					)! as number;
				if (production.has(ResourceType.POPULATION_INCOME))
					populationIncome += production.get(
						ResourceType.POPULATION_INCOME,
					)! as number;
			}

			if (consumption.size) {
				if (consumption.has(ResourceType.FOOD_CONSUMPTION))
					foodConsumption += consumption.get(
						ResourceType.FOOD_CONSUMPTION,
					)!;
				if (consumption.has(ResourceType.POPULATION_CONSUMPTION))
					populationConsumption += consumption.get(
						ResourceType.POPULATION_CONSUMPTION,
					)!;
			}
		}

		const foodDiff = foodIncome - foodConsumption;
		const populationDiff = populationIncome - populationConsumption;

		// set food and population incomes, consumptions and diffs
		this.resourcesStore.set(
			ResourceType.FOOD_INCOME,
			foodIncome,
			"getResourcePenalties",
		);
		this.resourcesStore.set(
			ResourceType.FOOD_CONSUMPTION,
			foodConsumption,
			"getResourcePenalties",
		);
		this.resourcesStore.set(
			ResourceType.POPULATION_INCOME,
			populationIncome,
			"getResourcePenalties",
		);
		this.resourcesStore.set(
			ResourceType.POPULATION_CONSUMPTION,
			populationConsumption,
			"getResourcePenalties",
		);
		this.resourcesStore.set(
			ResourceType.FOOD_DIFF,
			foodDiff,
			"getResourcePenalties",
		);
		this.resourcesStore.set(
			ResourceType.POPULATION_DIFF,
			populationDiff,
			"getResourcePenalties",
		);

		// return penalties
		return {
			foodPenaltyCoefficient: SegmentManager.getPenaltyCoefficient(
				foodIncome,
				foodConsumption,
			),
			populationPenaltyCoefficient: SegmentManager.getPenaltyCoefficient(
				populationIncome,
				populationConsumption,
			),
		};
	}

	private handleResourceCap(
		key: ResourceType,
		increment: number,
		alreadyAccumulatedValue: number = 0,
	): number {
		if (UNCAPPED_RESOURCE_TYPES.has(key)) return increment;

		const limitValue = this.resourcesStorageStore[key];
		const currentValue = this.resourcesStore[key] + alreadyAccumulatedValue;
		const nextValue = currentValue + increment;
		const cappedNextValue = Math.max(
			Math.min(limitValue, nextValue),
			currentValue,
		);

		return cappedNextValue - currentValue;
	}

	/**
	 * Metóda má za úlohu vykonávať jak dátovú tak aj rendering operáciu exploringu. Jedná sa o jedinú metódu
	 * v tejto triede ktorá zasahuje do renderingu.
	 * @param clickedTile
	 */
	// TODO: premiestnit do mapy
	public exploreNewTiles(clickedTile: TileEntity): void {
		if (!clickedTile) return;
		clickedTile.exploredTimestamp = new Date().getTime();

		if (clickedTile.isBlockedByEnemy) {
			const hasUnitToIgnoreTint =
				tilerepoFriendlyUnitsFrameNames.has(clickedTile.frameName) ||
				tilerepoFightableEnemiesFrameNames.has(clickedTile.frameName);

			if (!hasUnitToIgnoreTint) {
				this.emitter.emit(
					EmitterEvents.PhaserRenderBlockedByEnemy,
					new Set([
						{ x: clickedTile.x, y: clickedTile.y, value: false },
					]),
				);
			}
		}

		// Update the visibility of the clicked tile to EXPLORED
		this.setInternalTileValue(
			clickedTile.index,
			TileEntityParam.VISIBILITY,
			TileVisibilityType.EXPLORED,
		);

		this.emitter.emit(EmitterEvents.PhaserTileExplored, {
			x: clickedTile.x,
			y: clickedTile.y,
		});

		//ak je novoobjavený tile typu settlement tak ho pridaj do roadGraph
		//táto operácia sa robí pri stavaní budov do layer 2 ale toto je výnimka
		//kedy sa do roadGraph dostane objekt s layeru 0 ktorý sme nestavali, ale bol už na mape
		if (
			tilerepoSettlementEmptyFrameNames.has(clickedTile.frameName) ||
			tilerepoSettlementCastleBuildingsFrameNames.has(
				clickedTile.frameName,
			)
		) {
			const key = this.getTileIndexByTileCoordinates({
				x: clickedTile.x,
				y: clickedTile.y,
			});
			if (!this.roadGraph.has(key)) {
				this.roadGraph.set(key, new Set());
			}

			// Prepoj uzol s existujúcimi susedmi
			for (const {
				dx,
				dy,
			} of this.getDefaulNonDiagonalNeighborCoordinates()) {
				const nx = clickedTile.x + dx;
				const ny = clickedTile.y + dy;
				const neighborKey = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});

				// Ak existuje susedný uzol v roadGraph, vytvor obojstranné spojenie
				if (this.roadGraph.has(neighborKey)) {
					this.roadGraph.get(key)!.add(neighborKey);
					this.roadGraph.get(neighborKey)!.add(key);
				}
			}
		}

		for (
			let dy = -this.fogRadiusExploration;
			dy <= this.fogRadiusExploration;
			dy++
		) {
			for (
				let dx = -this.fogRadiusExploration;
				dx <= this.fogRadiusExploration;
				dx++
			) {
				const distance = Math.abs(dx) + Math.abs(dy);
				const maxDistance =
					1 +
					getUpgradeCurrentReplaceValueById(UpgradeIds.cartography) +
					getUpgradeCurrentReplaceValueById(UpgradeIds.espionage);
				const isInLockedRegion =
					useMapExpansionStore().isInLockedRegionByCoordinates({
						x: clickedTile.x + dx,
						y: clickedTile.y + dy,
					});

				if (isInLockedRegion && distance !== 1) continue;

				if (distance >= 1 && distance <= maxDistance) {
					const x = clickedTile.x + dx;
					const y = clickedTile.y + dy;
					const i = this.getTileIndexByTileCoordinates({
						x,
						y,
					});

					// musime prerenderovat fog, lebo mimo hranic mapy je schovany a potrebujeme tam v tento moment zobrazit unlock sipku
					// if (
					// 	isInLockedRegion &&
					// 	distance === 1 &&
					// 	this.getInternalTileValue(
					// 		i,
					// 		TileEntityParam.VISIBILITY,
					// 	) === TileVisibilityType.FOG
					// ) {
					// 	this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
					// 		coordinates: { x, y },
					// 	});
					// }

					if (
						this.getInternalTileValue(
							i,
							TileEntityParam.VISIBILITY,
						) !== TileVisibilityType.BLACK
					)
						continue;

					this.setInternalTileValue(
						i,
						TileEntityParam.VISIBILITY,
						TileVisibilityType.FOG,
					);

					const questStore = useQuestStore();
					const activePlagueQuest = questStore.getActiveQuests.find(
						(q: QuestActive): boolean =>
							q.objective.action ===
							QuestObjectiveActions.CLEAR_PLAGUE,
					);

					if (activePlagueQuest) {
						plagueRepository.changeSkin(i);
					}

					this.emitter.emit(EmitterEvents.PhaserPutTileAt, {
						coordinates: { x, y },
					});
					this.emitter.emit(EmitterEvents.PhaserTileFogAdded, {
						x,
						y,
						fade: !tilerepoSettlementCastleBuildingsFrameNames.has(
							clickedTile.frameName,
						),
					});
				}
			}
		}
	}

	/**
	 * Táto metóda identfikuje, či je alebo nieje možné vykonávať exploring v rámci konkrétneho mora. Dôvod
	 * overovani je, že uvedený počet tiles ktoré od shipyardu je alebo nie je možné explorovať sa navyšuje
	 * počtom shipyardom v príslušnom mory. Teda nie je to tak, že by mal Shipyard nejaký rádius, ale shipyardy v rámci mora
	 * majú nejakú pomyselnú silu exploringu ktorá sa aplikuje na každý shipyard.
	 * @param clickedTile
	 * @param checkCoast
	 */
	public canExploreSeaTile(
		clickedTile: TileEntity,
		checkCoast: boolean,
	): boolean {
		const x = clickedTile.x;
		const y = clickedTile.y;

		// 1. Kontrola, že je to more
		if (!tilerepoSeasFrameNames.has(clickedTile.frameName)) return false; // Netrápi nás

		if (checkCoast) {
			// 2. Kontrola pobrežia - toto by sa asi nemalo stať lebo metóda sa používa aktuálne
			//len v MainMapScen::exploreTile a tam pred použitím tejto metódy je presne identická kontrola
			//ale pre budúcnosť som to sem pre istotu dal
			const directions = this.getDefaulNonDiagonalNeighborCoordinates();

			for (const dir of directions) {
				const nx = x + dir.dx;
				const ny = y + dir.dy;
				const neighborTileIndex = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});
				const neighborTile =
					this.getTileEntityFactoriesByTileIndex(
						neighborTileIndex,
					)[0]();
				if (!neighborTile) continue;

				if (
					this.getInternalTileValue(
						neighborTileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED &&
					!tilerepoSeasFrameNames.has(neighborTile.frameName)
				) {
					// Susedí s pobrežím
					return true;
				}
			}
		}

		// 3. Zistiť shipyardy na rovnakom mori
		const shipyardsInSameSea = this.shipyardsInSameSea(clickedTile);

		// 4. Výpočet max vzdialenosti
		const seaExplorationPower = shipyardsInSameSea
			.map((shipyard: TileEntity): number => {
				if (tilerepoShipyardFrameNames.has(shipyard.frameName)) {
					return (shipyard as ShipyardBuildingTileEntity)
						.seaExplorationPower;
				}
				return 0;
			})
			.reduce((acc: number, current: number): number => acc + current, 0);
		const maxSeaExploreDistance =
			this.basicSeaExploration +
			getUpgradeCurrentReplaceValueById(UpgradeIds.coast_patrol) +
			seaExplorationPower;

		// 5. Nájsť cestu len k týmto shipyardom
		const distanceToShipyard = this.findSeaPathToShipyard(
			x,
			y,
			shipyardsInSameSea,
		);

		// 6. Overenie vzdialenosti
		if (
			distanceToShipyard !== null &&
			distanceToShipyard <= maxSeaExploreDistance
		) {
			return true;
		}

		// 7. Inak false
		return false;
	}

	/**
	 * Úlohou tejto metódy je vrátiť shipyardy, ktoré sa nachádzajú v rovnakom mori
	 * @param clickedTile
	 * @param returnOnlyActive
	 */
	public shipyardsInSameSea(
		clickedTile: TileEntity,
		returnOnlyActive: boolean = true,
	): Array<TileEntity> {
		// Zistiť všetky tiles v rovnakom mori
		const connectedSeaTiles = this.getConnectedSeaTiles(
			clickedTile.x,
			clickedTile.y,
		);

		// vratit shipyardy na rovnakom mori
		return this.getAllShipyardEntities(returnOnlyActive).filter(
			(shipyard: TileEntity): boolean =>
				connectedSeaTiles.has(`${shipyard.x},${shipyard.y}`),
		);
	}

	/**
	 * Úlohou tejto metódy je vrátiť celé číslo, reprezentujúce vzdialenosť od najbližšieho shipyardu
	 * ktorý sa nachádza v poly zaslanom ako argument. Čím vyššie cíšlo, tým väčšia vzdialenosť.
	 * @param targetX
	 * @param targetY
	 * @param shipyards
	 */
	public findSeaPathToShipyard(
		targetX: number,
		targetY: number,
		shipyards: TileEntity[], // prístavy len z tohto mora
	): number | null {
		const visited = new Set<string>();
		const queue: { x: number; y: number; distance: number }[] = [];

		// 1. Pridáme shipyardy z rovnakého mora do fronty
		for (const shipyard of shipyards) {
			const sx = shipyard.x;
			const sy = shipyard.y;
			const key = `${sx},${sy}`;

			queue.push({ x: sx, y: sy, distance: 0 }); // Začíname z prístavu
			visited.add(key);
		}

		// 2. BFS prehľadávanie - https://en.wikipedia.org/wiki/Breadth-first_search
		while (queue.length > 0) {
			const { x, y, distance } = queue.shift()!;

			// Ak sme našli cieľ
			if (x === targetX && y === targetY) {
				return distance;
			}

			//Ak sme cieľ nenašli, preskúmame susedov
			const directions = this.getDefaulNonDiagonalNeighborCoordinates();

			for (const dir of directions) {
				const nx = x + dir.dx;
				const ny = y + dir.dy;
				const key = `${nx},${ny}`;

				if (visited.has(key)) continue;

				const neighborTileIndex = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});
				const neighborTile =
					this.getTileEntityFactoriesByTileIndex(
						neighborTileIndex,
					)[0]();
				if (!neighborTile) continue;

				// Ak je to more a preskúmané, alebo ak je to cieľ (aj keď FOG)
				if (
					tilerepoSeasFrameNames.has(neighborTile.frameName) &&
					(this.getInternalTileValue(
						neighborTileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED ||
						(nx === targetX && ny === targetY)) // povolíme cieľ aj keď FOG
				) {
					queue.push({ x: nx, y: ny, distance: distance + 1 });
					visited.add(key);
				}
			}
		}

		// Ak sa nenašla žiadna cesta
		return null;
	}

	/**
	 * Metóda má za úloho pomocou BFS identifikovať priestor v ktorom má vyhľadať najkradšiu trasu medzi
	 * start x/y a end x/y. Priestor ktorý sa môže prehľadávať je vložený ako argument allowedTiles. Čiže daná
	 * metóda sa síce volá findShortestSeaPathBFS avšak môže byť použitá na akýkoľvek priestor pozostávajúci s x/y
	 * koordinátov
	 * @param start
	 * @param end
	 * @param allowedTiles
	 */
	public findShortestSeaPathBFS(
		start: TileCoordinates,
		end: TileCoordinates,
		allowedTiles: Set<string>, // Set of "x,y" strings
	): TileCoordinates[] {
		const visited: Set<string> = new Set<string>();
		const queue: TileCoordinates[] = [];
		const cameFrom: Map<string, TileCoordinates> = new Map<
			string,
			TileCoordinates
		>();

		const getKey = (tile: TileCoordinates): string => `${tile.x},${tile.y}`;

		queue.push(start);
		visited.add(getKey(start));

		const directions: { dx: number; dy: number }[] =
			this.getDefaulNonDiagonalNeighborCoordinates();

		while (queue.length > 0) {
			const current: TileCoordinates = queue.shift()!;

			for (const { dx, dy } of directions) {
				const neighbor: TileCoordinates = {
					x: current.x + dx,
					y: current.y + dy,
				};
				const neighborKey = getKey(neighbor);

				if (
					allowedTiles.has(neighborKey) &&
					!visited.has(neighborKey)
				) {
					queue.push(neighbor);
					visited.add(neighborKey);
					cameFrom.set(neighborKey, current);

					if (neighbor.x === end.x && neighbor.y === end.y) {
						// reconstruct path
						const path: TileCoordinates[] = [end];
						let step: TileCoordinates = current;
						while (getKey(step) !== getKey(start)) {
							path.push(step);
							step = cameFrom.get(getKey(step))!;
						}
						path.push(start);
						path.reverse();
						return path;
					}
				}
			}
		}

		return []; // no path found
	}

	public getAShipyardEntity(): ShipyardBuildingTileEntity | null {
		const shipyardTileIndexes = this.frameNameToTileIndexesCache.get(
			FrameName.SHIPYARD,
		)!;
		const bigShipyardTileIndexes = this.frameNameToTileIndexesCache.get(
			FrameName.BIG_SHIPYARD,
		)!;
		const tileIndexes = new Set<TileIndex>([
			...shipyardTileIndexes,
			...bigShipyardTileIndexes,
		]);
		if (tileIndexes.size === 0) return null;

		return this.getTileEntityByTileIndex(
			tileIndexes.values().next().value!,
		) as ShipyardBuildingTileEntity | BigShipyardBuildingTileEntity | null;
	}

	/**
	 * Metóda vráti všetky TileEntity typu shipyard v rámci mapy.
	 * @param returnOnlyActive - Ak true, vráti všetky postavené lodenice bez ohľadu na to, či sú pripojené k settlementu.
	 */
	public getAllShipyardEntities(
		returnOnlyActive: boolean = true,
	): Array<TileEntity> {
		const shipyardTileId = this.getTileIdByFrameName(FrameName.SHIPYARD)!;
		const bigShipyardTileId = this.getTileIdByFrameName(
			FrameName.BIG_SHIPYARD,
		)!;
		const shipyardTileIds = new Set<TileId>([
			shipyardTileId,
			bigShipyardTileId,
		]);
		const shipyards = new Array<TileEntity>();
		for (let i = 0; i < this.size; i++) {
			if (!shipyardTileIds.has(this.getInternalTileValue(i, 2))) continue;

			const entity = this.getTileEntityFactoriesByTileIndex(i)[2]();
			if (!entity) continue;

			// AK SME V REŽIME BUDOVANIA GRAFU, PRESKOČÍME KONTROLU PRIPOJENIA
			if (returnOnlyActive) {
				if (!entity.isConnectionToMainSettlementActive) continue;
			}

			shipyards.push(entity);
		}

		return shipyards;
	}

	public getAllFishingBoatEntities(): Array<TileEntity> {
		return [
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.SHALLOW_SEA_FISHING_BOAT,
			),
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.DEEP_SEA_FISHING_BOAT,
			),
		];
	}

	public getAllBigFishingBoatEntities(): Array<TileEntity> {
		return [
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
			),
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.DEEP_SEA_BIG_FISHING_BOAT,
			),
		];
	}

	public getAllFishTrapEntities(): Array<TileEntity> {
		return [
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
			),
			...this.getAllBuildingEntitiesByFrameName(
				FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
			),
		];
	}

	// Centralize the layer-2 cache lookup - building queries handle missing cache
	private getAllBuildingEntitiesByFrameName(
		frameName: FrameName,
	): Array<TileEntity> {
		const tileIndexes = this.frameNameToTileIndexesCache.get(frameName);
		if (!tileIndexes) return [];

		const entities = new Array<TileEntity>();
		for (const tileIndex of tileIndexes) {
			const entity =
				this.getTileEntityFactoriesByTileIndex(tileIndex)[2]();
			if (!entity) continue;
			entities.push(entity);
		}
		return entities;
	}

	public getAllBuildingEntitiesByFrameNames(
		frameNames: Set<FrameName>,
	): Array<TileEntity> {
		const entities = new Array<TileEntity>();
		for (const frameName of frameNames) {
			entities.push(...this.getAllBuildingEntitiesByFrameName(frameName));
		}
		return entities;
	}

	public getAllFishermansHutEntities(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(FrameName.FISHERMANS_HUT);
	}

	public getAllDarkwoodLumberMills(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(
			FrameName.DARKWOOD_LUMBER_MILL,
		);
	}

	public getAllRafts(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(FrameName.RAFT);
	}

	public getAllBottleTraps(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(FrameName.BOTTLETRAP);
	}

	public getAllHuntingCamps(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(FrameName.HUNTING_CAMP);
	}

	public getAllHighlandHunters(): Array<TileEntity> {
		return this.getAllBuildingEntitiesByFrameName(
			FrameName.HIGHLAND_HUNTER,
		);
	}

	/**
	 * Metóda ma identifikovať zoznam všetkých tiles v tvare reťazca "x,y", ktoré sa nachádzajú v tom istom mory.
	 * Pod týmto pojmom rozumieme všetky ohalené tiles v kategórií tilerepoSeasFrameNames ktoré sú ohraničené buď
	 * neodhlaenými tiles alebo tiles s inej kategórie ako sú tilerepoSeasFrameNames.
	 * @param startX
	 * @param startY
	 */
	// TODO: prerobit kluc na tileIndex
	public getConnectedSeaTiles(startX: number, startY: number): Set<string> {
		// Výsledný set stringov (pre kompatibilitu)
		const result = new Set<string>();
		// Interný set pre rýchle O(1) overovanie navštívených indexov
		const visitedIndexes = new Set<number>();
		// Fronta používa len čísla (TileIndex) - žiadne objekty {x,y}
		const queue: number[] = [];
		let queueHead = 0; // Pointer pre "shift"
		// Inicializácia
		const startIndex = this.getTileIndexByTileCoordinates({
			x: startX,
			y: startY,
		});
		// Pridanie štartovacieho bodu
		queue.push(startIndex);
		visitedIndexes.add(startIndex);
		result.add(`${startX},${startY}`);

		const directions = this.getDefaulNonDiagonalNeighborCoordinates();
		const mapSize = this.MAP_SIZE; // Cache pre lokálny prístup

		while (queueHead < queue.length) {
			// 1. Získame index bez rezania poľa (shift)
			const currentIndex = queue[queueHead++];

			// Rýchly prevod index - x, y (matematika je rýchlejšia ako volanie metódy)
			const cx = currentIndex % mapSize;
			const cy = Math.trunc(currentIndex / mapSize);

			for (const dir of directions) {
				const nx = cx + dir.dx;
				const ny = cy + dir.dy;

				// 2. Rýchly Bounds Check
				if (nx < 0 || nx >= mapSize || ny < 0 || ny >= mapSize)
					continue;

				// 3. Výpočet indexu suseda priamo
				const nIndex = nx + ny * mapSize;

				if (visitedIndexes.has(nIndex)) continue;

				// 4. OPTIMALIZÁCIA: Čítame priamo z pamäte (žiadne vytváranie Entity objektov!)

				// A) Check Viditeľnosti (Uint8Array lookup)
				const visibility = this.getInternalTileValue(
					nIndex,
					TileEntityParam.VISIBILITY,
				);
				if (visibility !== TileVisibilityType.EXPLORED) continue;

				// B) Check Typu políčka (Uint16Array lookup + Map lookup)
				// Zaujíma nás Layer 0 (voda)
				const tileId0 = this.getInternalTileValue(nIndex, 0);
				// Rýchly check či frameName sedí (getFrameNameByTileId je len Map.get)
				const frameName = this.getFrameNameByTileId(tileId0);

				if (frameName && tilerepoSeasFrameNames.has(frameName)) {
					visitedIndexes.add(nIndex);
					queue.push(nIndex);

					// Pridáme do výsledku pre návratovú hodnotu
					result.add(`${nx},${ny}`);
				}
			}
		}

		return result;
	}

	/**
	 * Metóda ktorá má identifikovať či je daný shipyard pripojený k hlavnému settlementu
	 * akýkoľvek vodným spôsobom POZOR!!! táto metóda nereflektuje ak je shipyard napojený
	 * suchozemskou cestou
	 * @param entity
	 * @private
	 */
	private isShipyardConnectedToMainSettlement(
		entity: TileEntity | null,
	): boolean {
		//ak entita na ktorú sa pýtame nie je shipyard hned ukoncujeme metodu
		if (!entity || !tilerepoShipyardFrameNames.has(entity.frameName))
			return false;
		//získaj všetky prístavy ktoré sú aktívne (spojené s main settlementom)
		const shipyards: TileEntity[] = this.getAllShipyardEntities();
		//zostav more v ktorom sa tento shipyard nachádza
		const connectedSeaTiles = this.getConnectedSeaTiles(entity.x, entity.y);
		//zisti či sa v mory v ktorom je shipyard na ktorý sa pýtame (entity)
		//nachádza aspoň jeden shipyard spojený s mai nsettlmentom === aj tento shipyard je tým pádom
		//spojený s main settlementom
		for (let i = 0; i < shipyards.length; i++) {
			const { x, y } = shipyards[i];
			if (connectedSeaTiles.has(`${x},${y}`)) return true;
		}
		//ak nie, tak nie je spojený s main settlmentom
		return false;
	}

	/**
	 * Táto metóda identfikuje, či aktuálny tile na x/y má aspoň jedného suseda ktorý je odhalený
	 * @param x
	 * @param y
	 */
	public hasExploredNeighbor(x: number, y: number): boolean {
		const directions = this.getDefaulNonDiagonalNeighborCoordinates();

		for (const dir of directions) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;

			const neighborTile = this.getTileEntityByTileCoordinates({
				x: nx,
				y: ny,
			});
			if (!neighborTile) continue;

			const neighborVisibility = neighborTile.getParamByName(
				TileEntityParam.VISIBILITY,
			);

			if (neighborVisibility === TileVisibilityType.EXPLORED) {
				return true; // Má suseda, ktorý je už preskúmaný
			}
		}

		return false; // Nemá platného suseda
	}

	public getDefaulNonDiagonalNeighborCoordinates(): {
		dx: number;
		dy: number;
	}[] {
		return [
			{ dx: 1, dy: 0 },
			{ dx: -1, dy: 0 },
			{ dx: 0, dy: 1 },
			{ dx: 0, dy: -1 },
		];
	}

	public showNotPossibleBuildMessage(
		nextFrameName: FrameName,
		selectedTile: TileEntity,
		limitReached: boolean = false,
	): void {
		const entity = this.createEntityByFrameName(nextFrameName);
		const buildingsStorageStore = useBuildingsStorageStore();
		const unitSupplyLimit = entity?.siegeAttributes?.supplyLimit ?? 0;

		const isSupplyLimitSatisfied =
			unitSupplyLimit > 0
				? true
				: useSiegeSystemRepository().supplyLimit + unitSupplyLimit >= 0;
		// TODO: get directly from config
		const townBuildings = [
			FrameName.SMALL_HOUSES,
			FrameName.BAKERY,
			FrameName.TOLL_HOUSE,
			FrameName.BLACKSMITH,
			FrameName.MARKET,
			FrameName.TOOLMAKER,
			FrameName.ARMORY,
			FrameName.BARRACKS,
			FrameName.HEALERS_HUT,
			FrameName.TAILORS_WORKSHOP,
			FrameName.MEDIUM_HOUSES,
			FrameName.UNIVERSITY,
			FrameName.INN,
			FrameName.SHOEMAKER,
			FrameName.PATISSERIE,
			FrameName.GUARD_TOWER,
			FrameName.CARPENTRY_DISTRICT,
			FrameName.STONEMASONRY_DISTRICT,
			FrameName.TAX_OFFICE,
			FrameName.LARGE_HOUSES,
			FrameName.ARTISAN_SMITH,
		];
		if (limitReached) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.building_limit",
				messageTime: 1800,
			});
			return;
		}
		if (
			!selectedTile.isLevelRequirementSatisfied(entity!) &&
			(buildingsStorageStore?.[nextFrameName] || 0) === 0
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.building_not_available",
				messageTime: 1800,
			});
			return;
		}
		if (
			tilerepoOnlyOneBuildingPerCastleFrameNames.has(nextFrameName) &&
			!selectedTile.canUpgradeTo.has(nextFrameName) &&
			!townBuildings.includes(selectedTile.frameName) &&
			!tilerepoOnlyOneBuildingPerCastleFrameNames.has(
				selectedTile.frameName,
			)
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: selectedTile.frameName.includes("Settlement")
					? "ui.building_town_limit"
					: "ui.not_possible_outside_town",
				messageTime: 1800,
			});
			return;
		} else if (selectedTile.frameName.includes("Settlement")) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_possible_in_town",
				messageTime: 1800,
			});
			return;
		} else if (
			townBuildings.includes(nextFrameName) &&
			!townBuildings.includes(selectedTile.frameName)
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_possible_outside_town",
				messageTime: 1800,
			});
			return;
		} else if (
			entity?.requirements.has(ResourceType.LEVEL) &&
			[OFFER_BUILDING_UNLOCK_LEVEL, EVENT_BUILDING_UNLOCK_LEVEL].includes(
				entity.requirements.get(ResourceType.LEVEL)!,
			) &&
			tilerepoNonSellableFrameNames.has(nextFrameName) &&
			buildingsStorageStore?.[nextFrameName] === 0
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.building_not_available",
				messageTime: 1800,
			});
			return;
		} else if (
			entity &&
			entity.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
			entity.requirements.get(ResourceType.SETTLEMENT_CONNECTION) === 1 &&
			!this.isNeighborToEntityConnectedToMainSettlement(
				selectedTile.x,
				selectedTile.y,
			)
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.connect_road",
				messageTime: 1800,
			});
			return;
		} else if (!selectedTile.canUpgradeTo.has(nextFrameName)) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_possible_on_this_terrain",
				messageTime: 1800,
			});
			return;
		} else if (
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName) &&
			!selectedTile.isBlockedByEnemy
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_possible_on_this_terrain",
				messageTime: 1800,
			});
			return;
		} else if (
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName) &&
			!useSiegeSystemRepository().isInActiveSiegeSystemAreaByTileIndex(
				selectedTile.index,
			) &&
			useSiegeSystemRepository().mainEnemyTileIndex !== null
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.not_in_this_battlefield",
				messageTime: 1800,
			});
			return;
		} else if (
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName) &&
			!isSupplyLimitSatisfied
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				text: "ui.insufficient_supply_limit",
				messageTime: 1800,
			});
			return;
		}
	}

	public handleUpgrade(
		prevFrameName: FrameName,
		nextFrameName: FrameName,
		selectedTile: TileEntity,
		isSkip?: boolean,
		storeToInventory = false,
		playSound = true,
	): TileEntity | null {
		if (
			selectedTile.isBlockedByEnemy &&
			!tilerepoFriendlyUnitsFrameNames.has(prevFrameName) &&
			!tilerepoFriendlyUnitsFrameNames.has(nextFrameName)
		)
			return null;
		const unitSupplyLimit =
			this.createEntityByFrameName(nextFrameName)?.siegeAttributes
				?.supplyLimit ?? 0;
		const isSupplyLimitSatisfied =
			unitSupplyLimit > 0
				? true
				: useSiegeSystemRepository().supplyLimit + unitSupplyLimit >= 0;
		if (
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName) &&
			(!selectedTile.isBlockedByEnemy ||
				!isSupplyLimitSatisfied ||
				(!useSiegeSystemRepository().isInActiveSiegeSystemAreaByTileIndex(
					selectedTile.index,
				) &&
					useSiegeSystemRepository().mainEnemyTileIndex !== null))
		) {
			return null;
		}

		const grantReward = (
			reward: Reward | Partial<Record<ResourceType, UpgradeRewardValue>>,
		): void => {
			const { mainLevel } = useSettlementsStore();
			const unlocks = getUnlocksByLevel();

			const entries = Object.entries(reward || {}) as Array<
				[ResourceType, UpgradeRewardValue]
			>;
			if (!entries.length) return;

			const resolvedReward = {} as Reward;
			for (const [key, value] of entries) {
				if (unlocks[resourceTypeToCurrencyType(key)]! > mainLevel)
					continue;

				const resolvedValue =
					typeof value === "function" ? value() : value;

				this.resourcesStore.increase(
					key,
					resolvedValue,
					"handleUpgrade: reward",
				);

				resolvedReward[key] = resolvedValue;
			}

			useEmitter().emit(EmitterEvents.PhaserTileClick, {
				x: selectedTile!.x,
				y: selectedTile!.y,
				reward: resolvedReward,
				randomX: false,
			});
		};

		if (nextFrameName === FrameName.BOUNTIFUL_MARKETPLACE) {
			const entity = this.createEntityByFrameName(nextFrameName, {
				x: selectedTile.x,
				y: selectedTile.y,
			});
			if (entity?.range) {
				const neighbourTileIndexes =
					this.getNeighbourTileIndexesByTileCoordinates(
						{
							x: selectedTile.x,
							y: selectedTile.y,
						},
						entity.range,
					);
				for (const neighbourTileIndex of neighbourTileIndexes) {
					const neighbourTileId2 = this.getInternalTileValue(
						neighbourTileIndex,
						2,
					);
					if (!neighbourTileId2) continue;

					const neighbourFrameName2 =
						this.getFrameNameByTileId(neighbourTileId2);
					if (!neighbourFrameName2) continue;

					if (neighbourFrameName2 === entity.frameName) {
						useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
							x: selectedTile!.x,
							y: selectedTile!.y,
							text: "ui.building_overlap",
							messageTime: 1800,
						});
						return null;
					}
				}
			}
		}

		const buildingsStorageStore = useBuildingsStorageStore();
		const buildingsStorageStoreRefs = storeToRefs(
			useBuildingsStorageStore(),
		);
		const fishingBoatsCount =
			buildingsStorageStoreRefs[FrameName.SHALLOW_SEA_FISHING_BOAT]
				.value +
			buildingsStorageStoreRefs[FrameName.DEEP_SEA_FISHING_BOAT].value;
		const bigFishingBoatsCount =
			buildingsStorageStoreRefs[FrameName.SHALLOW_SEA_BIG_FISHING_BOAT]
				.value +
			buildingsStorageStoreRefs[FrameName.DEEP_SEA_BIG_FISHING_BOAT]
				.value;
		const fishTrapsCount =
			buildingsStorageStoreRefs[FrameName.SHALLOW_SEA_SMALL_FISH_TRAP]
				.value +
			buildingsStorageStoreRefs[FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP]
				.value;
		const lighthousesCount =
			buildingsStorageStoreRefs[FrameName.LIGHTHOUSE].value +
			buildingsStorageStoreRefs[FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE]
				.value;
		const blastFurnacesCount =
			buildingsStorageStoreRefs[FrameName.HILLS_BLAST_FURNACE].value +
			buildingsStorageStoreRefs[FrameName.MOUNTAINS_BLAST_FURNACE].value;
		const lookoutTowersCount =
			buildingsStorageStoreRefs[FrameName.HILLS_LOOKOUT_TOWER].value +
			buildingsStorageStoreRefs[FrameName.MOUNTAINS_LOOKOUT_TOWER].value;

		if (
			!storeToInventory &&
			tilerepoNonSellableFrameNames.has(prevFrameName)
		) {
			storeToInventory = true;
		}

		if (
			((selectedTile.isStorable && storeToInventory) ||
				tilerepoPremiumBuildings.has(prevFrameName)) &&
			nextFrameName === FrameName.NOT_SET
		) {
			const skipInventoryForEndedEvent =
				skipDemolishToStorage(prevFrameName);

			if (!skipInventoryForEndedEvent) {
				// Ak menim tile, ktory mozem dat do storage, tak ho tam dam.
				buildingsStorageStore.increase(
					prevFrameName,
					1,
					"handleUpgrade: put building into storage",
				);
			}
		} else if (
			(tilerepoNonSellableFrameNames.has(nextFrameName) ||
				tilerepoBuildingsFrameNames.has(nextFrameName)) &&
			((tilerepoFishingBoatsFrameNames.has(nextFrameName) &&
				fishingBoatsCount > 0) ||
				(tilerepoBigFishingBoatsFrameNames.has(nextFrameName) &&
					bigFishingBoatsCount > 0) ||
				(tilerepoFishTrapsFrameNames.has(nextFrameName) &&
					fishTrapsCount > 0) ||
				(tilerepoLightHousesFrameNames.has(nextFrameName) &&
					lighthousesCount > 0) ||
				(tilerepoBlastFurnacesFrameNames.has(nextFrameName) &&
					blastFurnacesCount > 0) ||
				(tilerepoLookoutTowersFrameNames.has(nextFrameName) &&
					lookoutTowersCount > 0) ||
				buildingsStorageStoreRefs[nextFrameName]?.value > 0)
		) {
			// Ak staviam budovu, ktoru uz mam v storage, tak ju pouzijem zo storage, ale musim najskor pozriet requirements.

			const entity = this.createEntityByFrameName(nextFrameName, {
				x: selectedTile.x,
				y: selectedTile.y,
			});

			if (
				entity &&
				entity.requirements.has(ResourceType.SETTLEMENT_CONNECTION) &&
				entity.requirements.get(ResourceType.SETTLEMENT_CONNECTION) ===
					1 &&
				!this.isNeighborToEntityConnectedToMainSettlement(
					selectedTile.x,
					selectedTile.y,
				) &&
				!this.isNeighbourToSettlement({
					x: selectedTile.x,
					y: selectedTile.y,
				}) &&
				!this.isShipyardConnectedToMainSettlement(entity) &&
				!tilerepoSettlementEmptyFrameNames.has(selectedTile.frameName)
			) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: selectedTile!.x,
					y: selectedTile!.y,
					text: "ui.connect_road",
					messageTime: 1800,
				});
				return null;
			}

			if (
				entity &&
				entity.requirements.has(ResourceType.SETTLEMENT_LEVEL) &&
				selectedTile.assignedCastleTileEntity &&
				selectedTile.assignedCastleTileEntity.level <
					entity.requirements.get(ResourceType.SETTLEMENT_LEVEL)!
			) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: selectedTile!.x,
					y: selectedTile!.y,
					text: "ui.settlement_level_requirement_not_met",
					messageTime: 1800,
				});
				return null;
			}

			const populationConsumption = (
				entity!.consumption?.has(ResourceType.POPULATION_CONSUMPTION)
					? entity!.consumption?.get(
							ResourceType.POPULATION_CONSUMPTION,
						)
					: 0
			) as number;
			if (populationConsumption > 0) {
				const enoughResources =
					this.resourcesStore.validateResourceAmount(
						ResourceType.POPULATION_DIFF,
						populationConsumption,
					);
				if (!enoughResources) {
					this.emitter.emit(EmitterEvents.PhaserNotEnoughResource, {
						x: selectedTile.x,
						y: selectedTile.y,
						resource: "workforce",
					});
					return null;
				}
			}
			const foodConsumption = (
				entity!.consumption?.has(ResourceType.FOOD_CONSUMPTION)
					? entity!.consumption?.get(ResourceType.FOOD_CONSUMPTION)
					: 0
			) as number;
			if (foodConsumption > 0) {
				const enoughResources =
					this.resourcesStore.validateResourceAmount(
						ResourceType.FOOD_DIFF,
						foodConsumption,
					);

				if (!enoughResources) {
					this.emitter.emit(EmitterEvents.PhaserNotEnoughResource, {
						x: selectedTile.x,
						y: selectedTile.y,
						resource: "food",
					});
					return null;
				}
			}

			if (tilerepoFishingBoatsFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.SHALLOW_SEA_FISHING_BOAT) {
					if (
						buildingsStorageStoreRefs[
							FrameName.SHALLOW_SEA_FISHING_BOAT
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.DEEP_SEA_FISHING_BOAT
							].value === 0
						)
							throw new Error("No fishing boats in storage");

						buildingsStorageStore.decrease(
							FrameName.DEEP_SEA_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.DEEP_SEA_FISHING_BOAT) {
					if (
						buildingsStorageStoreRefs[
							FrameName.DEEP_SEA_FISHING_BOAT
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.SHALLOW_SEA_FISHING_BOAT
							].value === 0
						)
							throw new Error("No fishing boats in storage");

						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.DEEP_SEA_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else if (tilerepoBigFishingBoatsFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.SHALLOW_SEA_BIG_FISHING_BOAT) {
					if (
						buildingsStorageStoreRefs[
							FrameName.SHALLOW_SEA_BIG_FISHING_BOAT
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.DEEP_SEA_BIG_FISHING_BOAT
							].value === 0
						)
							throw new Error("No big fishing boats in storage");

						buildingsStorageStore.decrease(
							FrameName.DEEP_SEA_BIG_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.DEEP_SEA_BIG_FISHING_BOAT) {
					if (
						buildingsStorageStoreRefs[
							FrameName.DEEP_SEA_BIG_FISHING_BOAT
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.SHALLOW_SEA_BIG_FISHING_BOAT
							].value === 0
						)
							throw new Error("No big fishing boats in storage");

						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_BIG_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.DEEP_SEA_BIG_FISHING_BOAT,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else if (tilerepoFishTrapsFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.SHALLOW_SEA_SMALL_FISH_TRAP) {
					if (
						buildingsStorageStoreRefs[
							FrameName.SHALLOW_SEA_SMALL_FISH_TRAP
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP
							].value === 0
						)
							throw new Error("No fish traps in storage");

						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP) {
					if (
						buildingsStorageStoreRefs[
							FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.SHALLOW_SEA_SMALL_FISH_TRAP
							].value === 0
						)
							throw new Error("No fish traps in storage");

						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_SMALL_FISH_TRAP,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else if (tilerepoLightHousesFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.LIGHTHOUSE) {
					if (
						buildingsStorageStoreRefs[FrameName.LIGHTHOUSE]
							.value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE
							].value === 0
						)
							throw new Error("No lighthouses in storage");

						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.LIGHTHOUSE,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE) {
					if (
						buildingsStorageStoreRefs[
							FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[FrameName.LIGHTHOUSE]
								.value === 0
						)
							throw new Error("No lighthouses in storage");

						buildingsStorageStore.decrease(
							FrameName.LIGHTHOUSE,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else if (tilerepoBlastFurnacesFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.HILLS_BLAST_FURNACE) {
					if (
						buildingsStorageStoreRefs[FrameName.HILLS_BLAST_FURNACE]
							.value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.MOUNTAINS_BLAST_FURNACE
							].value === 0
						)
							throw new Error("No blast furnaces in storage");

						buildingsStorageStore.decrease(
							FrameName.MOUNTAINS_BLAST_FURNACE,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.HILLS_BLAST_FURNACE,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.MOUNTAINS_BLAST_FURNACE) {
					if (
						buildingsStorageStoreRefs[
							FrameName.MOUNTAINS_BLAST_FURNACE
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.HILLS_BLAST_FURNACE
							].value === 0
						)
							throw new Error("No blast furnaces in storage");

						buildingsStorageStore.decrease(
							FrameName.HILLS_BLAST_FURNACE,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.MOUNTAINS_BLAST_FURNACE,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else if (tilerepoLookoutTowersFrameNames.has(nextFrameName)) {
				if (nextFrameName === FrameName.HILLS_LOOKOUT_TOWER) {
					if (
						buildingsStorageStoreRefs[FrameName.HILLS_LOOKOUT_TOWER]
							.value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.MOUNTAINS_LOOKOUT_TOWER
							].value === 0
						)
							throw new Error("No lookout towers in storage");

						buildingsStorageStore.decrease(
							FrameName.MOUNTAINS_LOOKOUT_TOWER,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.HILLS_LOOKOUT_TOWER,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}

				if (nextFrameName === FrameName.MOUNTAINS_LOOKOUT_TOWER) {
					if (
						buildingsStorageStoreRefs[
							FrameName.MOUNTAINS_LOOKOUT_TOWER
						].value === 0
					) {
						if (
							buildingsStorageStoreRefs[
								FrameName.HILLS_LOOKOUT_TOWER
							].value === 0
						)
							throw new Error("No lookout towers in storage");

						buildingsStorageStore.decrease(
							FrameName.HILLS_LOOKOUT_TOWER,
							1,
							"handleUpgrade: pick building from storage",
						);
					} else {
						buildingsStorageStore.decrease(
							FrameName.MOUNTAINS_LOOKOUT_TOWER,
							1,
							"handleUpgrade: pick building from storage",
						);
					}
				}
			} else {
				buildingsStorageStore.decrease(
					nextFrameName,
					1,
					"handleUpgrade: pick building from storage",
				);
			}

			const { reward } = selectedTile.upgrades.get(
				nextFrameName,
			) as UpgradeParams;
			if (reward) grantReward(reward);

			if (playSound) SoundService.getInstance().playBuildingSound();
			this.emitter.emit(EmitterEvents.PhaserBuildAnimation, {
				x: selectedTile.x,
				y: selectedTile.y,
			});
		} else {
			// Ak je staviam budovu, ktoru nemam v storage, tak odratam resources.

			if (
				!tilerepoForestFrameNames.has(nextFrameName) &&
				!tilerepoGrasslandFrameNames.has(nextFrameName) &&
				nextFrameName !== FrameName.NOT_SET
			) {
				const isNeighborToSettlement = this.isNeighborToSettlement(
					selectedTile.x,
					selectedTile.y,
				);

				const isNeighborToEntityConnectedToMainSettlement =
					this.isNeighborToEntityConnectedToMainSettlement(
						selectedTile.x,
						selectedTile.y,
					);

				const entity = this.createEntityByFrameName(nextFrameName, {
					x: SAFE_COORDINATE_OUTSIDE_MAP,
					y: SAFE_COORDINATE_OUTSIDE_MAP,
				});

				if (
					entity &&
					entity.requirements.has(ResourceType.SETTLEMENT_LEVEL) &&
					selectedTile.assignedCastleTileEntity &&
					selectedTile.assignedCastleTileEntity.level <
						entity.requirements.get(ResourceType.SETTLEMENT_LEVEL)!
				) {
					useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile!.x,
						y: selectedTile!.y,
						text: "ui.settlement_level_requirement_not_met",
						messageTime: 1800,
					});
					return null;
				}

				const populationConsumption = (
					entity!.consumption?.has(
						ResourceType.POPULATION_CONSUMPTION,
					)
						? entity!.consumption?.get(
								ResourceType.POPULATION_CONSUMPTION,
							)
						: 0
				) as number;
				if (populationConsumption > 0) {
					const enoughResources =
						this.resourcesStore.validateResourceAmount(
							ResourceType.POPULATION_DIFF,
							populationConsumption,
						);

					if (!enoughResources) {
						this.emitter.emit(
							EmitterEvents.PhaserNotEnoughResource,
							{
								x: selectedTile.x,
								y: selectedTile.y,
								resource: "workforce",
							},
						);
						return null;
					}
				}

				const foodConsumption = (
					entity!.consumption?.has(ResourceType.FOOD_CONSUMPTION)
						? entity!.consumption?.get(
								ResourceType.FOOD_CONSUMPTION,
							)
						: 0
				) as number;
				if (foodConsumption > 0) {
					const enoughResources =
						this.resourcesStore.validateResourceAmount(
							ResourceType.FOOD_DIFF,
							foodConsumption,
						);

					if (!enoughResources) {
						this.emitter.emit(
							EmitterEvents.PhaserNotEnoughResource,
							{
								x: selectedTile.x,
								y: selectedTile.y,
								resource: "food",
							},
						);
						return null;
					}
				}

				const isRoadlessBuilding =
					!!entity?.requirements &&
					entity.requirements.has(ResourceType.SETTLEMENT_CONNECTION)
						? entity.requirements.get(
								ResourceType.SETTLEMENT_CONNECTION,
							)! === 0
						: false;

				let isShipyardConnectedToMainSettlement = false;
				if (entity?.frameName === FrameName.SHIPYARD) {
					const connectedSeaTiles = this.getConnectedSeaTiles(
						selectedTile.x,
						selectedTile.y,
					);

					const shipyardsInSameSea =
						this.getAllShipyardEntities().filter(
							(shipyard: TileEntity): boolean =>
								connectedSeaTiles.has(
									`${shipyard.x},${shipyard.y}`,
								),
						);

					if (shipyardsInSameSea.length > 0) {
						isShipyardConnectedToMainSettlement = true;
					}
				}

				if (
					!isNeighborToSettlement &&
					!isNeighborToEntityConnectedToMainSettlement &&
					!isRoadlessBuilding &&
					!isShipyardConnectedToMainSettlement
				) {
					if (playSound)
						SoundService.getInstance().playUpdateMaxLevel();
					this.emitter.emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile.x,
						y: selectedTile.y,
						text: "ui.connect_road",
						messageTime: 1800,
					});
					return null;
				}
			}

			const realNextFrameName = this.getCorrectRoadFrameName(
				selectedTile.x,
				selectedTile.y,
				nextFrameName,
			);

			//táto podmienka slúži nato, aby sa zemdzila možnosť stavania mostov vedľa seba
			//pokaiľ niesú spojený s inou cestou ako mostom vedľa nich
			if (tilerepoBridgeRoadsFrameNames.has(realNextFrameName)) {
				const neighbors =
					this.getDefaulNonDiagonalNeighborCoordinates();

				let isConnected = false;

				for (const { dx, dy } of neighbors) {
					const nx = selectedTile.x + dx;
					const ny = selectedTile.y + dy;

					const index = this.getTileIndexByTileCoordinates({
						x: nx,
						y: ny,
					});
					const neighborEntity: TileEntity | null =
						this.getTileEntityByTileIndex(index);

					if (!neighborEntity) continue;

					const isRoadLike =
						tilerepoRoadsFrameNames.has(
							neighborEntity.frameName as FrameName,
						) ||
						tilerepoBuildingsFrameNames.has(
							neighborEntity.frameName as FrameName,
						) ||
						tilerepoSettlementEmptyFrameNames.has(
							neighborEntity.frameName as FrameName,
						);

					if (!isRoadLike) continue;

					if (
						// Horizontálny most – smie susediť len L/R
						[
							FrameName.WOODEN_BRIDGE_1,
							FrameName.STONE_BRIDGE_1,
							FrameName.FORD_CROSSING_1,
						].includes(realNextFrameName) &&
						dx !== 0
					) {
						isConnected = true;
						break;
					}

					if (
						// Vertikálny most – smie susediť len U/D
						[
							FrameName.WOODEN_BRIDGE_2,
							FrameName.STONE_BRIDGE_2,
							FrameName.FORD_CROSSING_2,
						].includes(realNextFrameName) &&
						dy !== 0
					) {
						isConnected = true;
						break;
					}
				}

				if (!isConnected) {
					if (playSound)
						SoundService.getInstance().playUpdateMaxLevel();
					this.emitter.emit(EmitterEvents.PhaserWarningMessage, {
						x: selectedTile.x,
						y: selectedTile.y,
						text: "ui.connect_road",
						messageTime: 1800,
					});
					return null;
				}
			}

			if (!selectedTile.upgrades.has(nextFrameName)) return null;

			const { reward } = selectedTile.upgrades.get(
				nextFrameName,
			) as UpgradeParams;
			let price = selectedTile.resolveDynamicResources(
				selectedTile.upgrades.get(nextFrameName)!.price ?? {},
			);

			if (isSkip && selectedTile.skipDefeatGemsPrice)
				price = {
					[ResourceType.GEMS]: selectedTile.skipDefeatGemsPrice,
				};

			if (price) {
				const entries = Object.entries(price || {}) as Array<
					[ResourceType, number]
				>;
				if (entries.length) {
					// TODO: Toto mozno prerobit, nech najskor pozbiera vsetky chyby a az potom hodi error so vsetkym, nie postupne.
					for (const [key, value] of entries) {
						const enoughResources =
							this.resourcesStore.validateResourceAmount(
								key,
								value,
							);
						if (!enoughResources) {
							this.emitter.emit(
								EmitterEvents.PhaserNotEnoughResource,
								{
									x: selectedTile.x,
									y: selectedTile.y,
									resource: key,
								},
							);
							return null;
						}
					}

					const isPremiumBuilding =
						tilerepoPremiumGemsBuildings.has(nextFrameName);

					const reason = isPremiumBuilding
						? `premiumBuilding: price - ${nextFrameName.replace(".png", "").toLowerCase()}`
						: selectedTile.skipDefeatGemsPrice
							? "skipDefeatGems : price"
							: "handleUpgrade: price";

					entries.forEach(
						([key, value]: [ResourceType, number]): void => {
							this.resourcesStore.decrease(key, value, reason);
						},
					);
				}
			}

			if (reward) grantReward(reward);
		}

		const upgrade = selectedTile.upgrades.get(nextFrameName);
		if (nextFrameName === FrameName.NOT_SET || nextFrameName === null) {
			if (upgrade && typeof upgrade.onUpgrade === "function") {
				upgrade.onUpgrade();
			} else {
				SoundService.getInstance().playDemolitionSound();
			}
			this.emitter.emit(EmitterEvents.PhaserDemolishAnimation, {
				x: selectedTile.x,
				y: selectedTile.y,
			});
			QuestRepository.getInstance().manageQuestState(
				QuestObjectiveActions.SALVAGE,
				prevFrameName,
				1,
			);
		} else {
			if (upgrade && typeof upgrade.onUpgrade === "function") {
				upgrade.onUpgrade();
			} else {
				if (tilerepoPremiumBuildings.has(nextFrameName)) {
					if (playSound)
						SoundService.getInstance().playPremiumBuildingSound();
				} else {
					if (playSound)
						SoundService.getInstance().playBuildingSound();
				}
			}
			this.emitter.emit(EmitterEvents.PhaserBuildAnimation, {
				x: selectedTile.x,
				y: selectedTile.y,
			});
		}

		if (upgrade?.mutate0 && tilerepoLayer0FrameNames.has(upgrade.mutate0)) {
			this.setTileByTileCoordinates(
				{
					x: selectedTile.x,
					y: selectedTile.y,
				},
				upgrade.mutate0,
			);
		}

		if (
			tilerepoBanditsFrameNames.has(prevFrameName) ||
			tilerepoPiratesFrameNames.has(prevFrameName) ||
			tilerepoFightableMainEnemiesFrameNames.has(prevFrameName)
		) {
			this.fixRedTiles({ ignoreEnemyTileIndex: selectedTile.index });
		} else if (
			tilerepoFriendlyUnitsFrameNames.has(prevFrameName) ||
			tilerepoFightableMainEnemiesFrameNames.has(nextFrameName)
		) {
			this.emitter.emit(
				EmitterEvents.PhaserRenderBlockedByEnemy,
				new Set([
					{
						x: selectedTile.x,
						y: selectedTile.y,
						value: false,
					},
				]),
			);
		}

		this.emitter.emit(EmitterEvents.TileRepositoryUpgrade, {
			coordinates: { x: selectedTile!.x, y: selectedTile!.y },
			frameName: nextFrameName,
		});

		const newSelectedTile = this.getTileEntityByTileCoordinates({
			x: selectedTile!.x,
			y: selectedTile!.y,
		});

		if (useQuestStore().tutorialActive) {
			this.emitter.emit(EmitterEvents.PhaserTutorialTileUpgrade, {
				coordinates: { x: selectedTile!.x, y: selectedTile!.y },
				frameName: nextFrameName,
			});
		}

		if ("onUpgrade" in selectedTile)
			(selectedTile as TileEntityWithOnUpgradeMethod).onUpgrade({
				x: selectedTile.x,
				y: selectedTile.y,
			} as TileCoordinates);
		const searchToolFrameNames =
			useRuntimeStore().searchToolActiveFrameName;
		if (searchToolFrameNames?.includes(nextFrameName)) {
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: { x: selectedTile.x, y: selectedTile.y },
				skin: TileLabelSkin.SEARCH_TOOL,
				action: TileLabelAction.CREATE,
			});
		} else if (
			newSelectedTile &&
			searchToolFrameNames?.includes(newSelectedTile?.frameName)
		) {
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: { x: newSelectedTile.x, y: newSelectedTile.y },
				skin: TileLabelSkin.SEARCH_TOOL,
				action: TileLabelAction.CREATE,
			});
		} else {
			this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
				coordinates: { x: selectedTile.x, y: selectedTile.y },
				skin: TileLabelSkin.SEARCH_TOOL,
				action: TileLabelAction.DELETE,
			});
		}

		return newSelectedTile;
	}

	private handleSiegeSystemUnitUpgrade(
		tileIndex: TileIndex,
		prevFrameName: FrameName | null,
		nextFrameName: FrameName | null,
	): void {
		if (!prevFrameName || !nextFrameName) return;

		const ssr = useSiegeSystemRepository();
		const { x, y } = this.getTileCoordinatesByTileIndex(tileIndex);

		if (tilerepoFriendlyUnitsFrameNames.has(nextFrameName)) {
			ssr.setMainEnemyTileIndexByTileIndex(tileIndex);

			// Toto musi byt v timeoute, lebo niekedy sa zmena stihne prejavit a niekedy nie.
			setTimeout((): void => {
				this.emitter.emit(EmitterEvents.SiegeSystemProgressUpdate, {
					x,
					y,
				});

				if (ssr.allFriendlySiegeSystemUnitsTileIndexes.size > 1) return;

				for (const enemyTileIndex of ssr.allEnemySiegeSystemUnitsTileIndexes) {
					this.emitter.emit(
						EmitterEvents.SiegeSystemProgressUpdate,
						this.getTileCoordinatesByTileIndex(enemyTileIndex),
					);
				}
			});
		}

		if (
			tilerepoFriendlyUnitsFrameNames.has(prevFrameName) ||
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName) ||
			tilerepoFightableEnemiesFrameNames.has(prevFrameName) ||
			tilerepoFightableEnemiesFrameNames.has(nextFrameName)
		) {
			ssr.updateStoreUnits();

			if (!ssr.siegeResult)
				ssr.rerenderBattlefieldLabelByTileIndex(tileIndex);
		}

		if (
			tilerepoFightableEnemiesFrameNames.has(prevFrameName) &&
			!ssr.allEnemySiegeSystemUnitsTileIndexes.size
		) {
			ssr.finishSiege(SiegeSystemResult.VICTORY);
		}

		if (
			tilerepoFriendlyUnitsFrameNames.has(prevFrameName) &&
			!ssr.allFriendlySiegeSystemUnitsWithoutSupplyUnitsTileIndexes.size
		) {
			ssr.finishSiege(SiegeSystemResult.DEFEAT);
		}

		if (tilerepoFightableEnemiesFrameNames.has(prevFrameName)) {
			this.fixRedTiles();
		}

		if (
			tilerepoFriendlyUnitsFrameNames.has(prevFrameName) ||
			tilerepoFriendlyUnitsFrameNames.has(nextFrameName)
		) {
			const mainEnemyTileIndex =
				this.getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
					tileIndex,
				);
			if (mainEnemyTileIndex)
				this.fixRedTiles({
					forceMainEnemyTileIndex: mainEnemyTileIndex,
				});
		}

		if (tilerepoFriendlyMilitaryUnitsFrameNames.has(nextFrameName)) {
			ssr.setCanEnemyAttackGuard(true);
		}
	}

	public getFullSettlementsCount(): number {
		let count = 0;
		settlementNames.forEach(({ x, y }: SettlementItem): void => {
			const entity = this.getTileEntityByTileCoordinates({
				x,
				y,
			}) as SettlementCastleBuildingTileEntity;
			if (entity && entity.isFull) count++;
		});
		return count;
	}

	public getCorrectBeachFrameName({
		x,
		y,
	}: TileCoordinates): FrameName | null {
		const tileId = this.getTileIdsByTileCoordinates({
			x,
			y,
		})[0];
		const frameName = this.getFrameNameByTileId(tileId);
		if (frameName !== FrameName.SHALLOW_SEA) return null;

		const neighbourIndexes = this.getNeighbourTileIndexesByTileCoordinates(
			{ x, y },
			1,
			false,
			ALL_VISIBILITY_TYPES,
		);

		const neighbours = neighbourIndexes.map(
			(tileIndex: TileIndex): BeachNeighbourType => {
				const entity =
					this.getTileEntityFactoriesByTileIndex(tileIndex)[0]();
				if (!entity?.frameName) return BeachNeighbourType.Water;
				return tilerepoSeasFrameNames.has(entity.frameName) ||
					tilerepoRiversFrameNames.has(entity.frameName) ||
					[
						FrameName.WATERFALL2,
						FrameName.OUTPOST_SEA1,
						FrameName.OUTPOST_SEA2,
						FrameName.OUTPOST_SEA3,
						FrameName.OUTPOST_SEA4,
						FrameName.CASTAWAY_ISLAND1,
						FrameName.PIRATES_HIDEOUT4,
						FrameName.PIRATES_HIDEOUT7,
						FrameName.PIRATES_HIDEOUT8,
						FrameName.PIRATES_HIDEOUT9,
						FrameName.ATOL1,
						FrameName.ATOL2,
						FrameName.ATOL3,
						FrameName.ATOL4,
						FrameName.ATOL5,
						FrameName.ATOL6,
						FrameName.ATOL7,
						FrameName.ATOL8,
						FrameName.ATOL9,
					].includes(entity.frameName)
					? BeachNeighbourType.Water
					: BeachNeighbourType.Land;
			},
		) as Tuple<BeachNeighbourType, 8>;

		return getCorrectBeachFrameName(neighbours);
	}

	public initTint(): void {
		const exploredTilesBlockedByEnemies =
			new Set<TileCoordinatesWithValue>();
		for (let i = 0; i < this.size; i++) {
			if (
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED &&
				this.getInternalTileValue(
					i,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
				) === 1
			) {
				// siege jednotky nemame mat zafarbene na cerveno, ale stale to je blokovane uzemie
				const [, , tileId2] = this.getTileIdsByTileIndex(i);
				const frameName2 = this.getFrameNameByTileId(tileId2);
				if (
					frameName2 &&
					(tilerepoFriendlyUnitsFrameNames.has(frameName2) ||
						tilerepoFightableEnemiesFrameNames.has(frameName2))
				)
					continue;

				const tileToTint = this.getTileCoordinatesByTileIndex(i);
				exploredTilesBlockedByEnemies.add({
					x: tileToTint.x,
					y: tileToTint.y,
					value: false,
				});
			}
		}

		this.emitter.emit(
			EmitterEvents.PhaserRenderBlockedByEnemy,
			exploredTilesBlockedByEnemies,
		);

		this.refreshMainSettlementConnections();
	}

	public getSerializedRoadGraph(): SerializedRoadGraph {
		return Array.from(this.roadGraph.entries()).map(
			([k, v]: [TileIndex, Set<TileIndex>]): [
				TileIndex,
				Array<TileIndex>,
			] => [k, Array.from(v)],
		);
	}

	public setSerializedRoadGraph(data: SerializedRoadGraph): void {
		this.roadGraph.clear();

		for (let i = 0; i < data.length; i++) {
			const [key, value] = data[i];
			this.roadGraph.set(key, new Set<TileIndex>(value));
		}
	}

	private getInternalTileValue(
		tileIndex: TileIndex,
		layerIndexOrParamName:
			| TileRepositoryLayerIndex
			| keyof TileEntityParams,
	): TileId | number {
		const isParam = typeof layerIndexOrParamName === "string";

		if (isParam) {
			return this.params[layerIndexOrParamName][tileIndex];
		} else {
			return this.tiles[layerIndexOrParamName][tileIndex];
		}
	}

	private setInternalTileValue(
		tileIndex: TileIndex,
		layerIndexOrParamName:
			| TileRepositoryLayerIndex
			| keyof TileEntityParams,
		value: TileId | number,
	): void {
		if (
			this.getInternalTileValue(tileIndex, layerIndexOrParamName) ===
			value
		)
			return;

		const isParam = typeof layerIndexOrParamName === "string";

		if (isParam) {
			this.params[layerIndexOrParamName][tileIndex] = value as number;
		} else {
			this.tiles[layerIndexOrParamName][tileIndex] = value as TileId;
		}

		if (
			isParam &&
			layerIndexOrParamName === TileEntityParam.VISIBILITY &&
			value === TileVisibilityType.EXPLORED
		) {
			this._internalExploredTileCount++;

			const frameName = this.getFrameNameByTileId(
				this.getTileIdsByTileIndex(tileIndex)[0],
			);
			if (frameName && tilerepoWaterFrameNames.has(frameName)) {
				this._internalExploredWaterTileCount++;
			}
		}

		this.emitter.emit(EmitterEvents.MapChange, {
			tileIndex,
		});
	}

	public getCurrentNumberOfPremiumRoads(): {
		[FrameName.GRAVEL_ROAD]: number;
		[FrameName.COBBLED_ROAD]: number;
	} {
		const result = {
			[FrameName.GRAVEL_ROAD]: 0,
			[FrameName.COBBLED_ROAD]: 0,
		};
		result[FrameName.GRAVEL_ROAD] = this.getTileCount2(
			new Set<FrameName>([
				...tilerepoGravelRoadsFrameNames,
				...tilerepoWoodenBridgeRoadsFrameNames,
			]),
		);
		result[FrameName.COBBLED_ROAD] = this.getTileCount2(
			new Set<FrameName>([
				...tilerepoCobbledRoadsFrameNames,
				...tilerepoStoneBridgeRoadsFrameNames,
				...tilerepoTunnelRoadsFrameNames,
			]),
		);

		return result;
	}

	// gc = garbage collector
	public initEntityCacheGc(): void {
		this.destroyEntityCacheGc();

		this.entityCacheGcIntervalId = setInterval((): void => {
			this.runEntityCacheGc();
		}, GC_ENTITY_CACHE_INTERVAL);
	}

	public runEntityCacheGc(clearAll?: boolean): void {
		if (!this.entityCache.size) return;

		if (clearAll) {
			this.entityCache.clear();
			return;
		}

		const now = Date.now();

		for (const [cacheKey, entity] of this.entityCache) {
			if (now - entity.createdAt < GC_ENTITY_CACHE_INTERVAL) continue;
			this.entityCache.delete(cacheKey);
		}
	}

	public destroyEntityCacheGc(): void {
		if (!this.entityCacheGcIntervalId) return;

		clearInterval(this.entityCacheGcIntervalId);
		this.entityCacheGcIntervalId = null;
	}

	private runTiledErrata(): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("tr.runTiledErrata()");
		}

		const emptyTileId = this.frameNameToTileIdCache.get(FrameName.NOT_SET)!;

		// v tiled na policku 413/422 je gold vein v layeri 3 namiesto layeru 1
		const tileIndex413422 = this.getTileIndexByTileCoordinates({
			x: 413,
			y: 422,
		});
		if (
			this.getInternalTileValue(tileIndex413422, 2) ===
			this.frameNameToTileIdCache.get(FrameName.GOLD_VEIN)
		) {
			this.setInternalTileValue(
				tileIndex413422,
				0,
				this.frameNameToTileIdCache.get(FrameName.GOLD_VEIN)!,
			);
			this.setInternalTileValue(tileIndex413422, 2, emptyTileId);
		}

		// v tiled na policku 534/389 su ryby v layeri 0 namiesto mora
		const tileIndex534389 = this.getTileIndexByTileCoordinates({
			x: 534,
			y: 389,
		});
		if (
			this.getInternalTileValue(tileIndex534389, 0) ===
			this.frameNameToTileIdCache.get(FrameName.SHALLOW_SEA_SMALL_FISH)
		) {
			this.setInternalTileValue(
				tileIndex534389,
				0,
				this.frameNameToTileIdCache.get(FrameName.SHALLOW_SEA)!,
			);
		}

		// v tiled su niektore koralove utesy v layeri 2 namiesto layeru 0
		// prebehneme vsetky koralove utesy podla tiled configu, lebo niektorym hracom po predchadzajucom fixe niektore zmizli
		const coralReefTileIds = new Set<TileId>([
			this.frameNameToTileIdCache.get(FrameName.SHALLOW_SEA_CORAL_REEF)!,
			this.frameNameToTileIdCache.get(FrameName.DEEP_SEA_CORAL_REEF)!,
		]);
		for (let i = 0; i < this.size; i++) {
			// Vytiahneme si tile ids.
			const [, , tileId2] = this.getTileIdsByTileIndex(i);
			const tiledTileId0 = this.tiledConfig!.layers[0].data[i];
			const tiledTileId2 = this.tiledConfig!.layers[2].data[i];

			// Ak v Tiled configu nie je definovany na tejto suradnici coral reef v layeri 0 alebo 2, tak early exitneme.
			if (
				!coralReefTileIds.has(tiledTileId0) &&
				!coralReefTileIds.has(tiledTileId2)
			)
				continue;

			// Ak je, tak nasilu hracovi setneme coral reef do layeru 0 (na poradi 2 || 0 zalezi - nemenit), layer 2 teraz este neriesime.
			this.setInternalTileValue(i, 0, tiledTileId2 || tiledTileId0);

			// Ak je v hracovom layeri 2 ulozeny coral reef, tak ho odstranime.
			if (coralReefTileIds.has(tileId2)) {
				this.setInternalTileValue(
					i,
					2,
					this.frameNameToTileIdCache.get(FrameName.NOT_SET)!,
				);
			}
		}

		// hrac hlasi, ze ma cierne tiles, ktore nejdu preskumat
		// ak narazime na stav, ze ani vo vrstve 0 ani v 2 nie je tileId, ale v tiled configu je
		// a tile je preskumany alebo sused je preskumany, tak dame tileId z tiled configu
		for (let i = 0; i < this.size; i++) {
			const [tileId0, , tileId2] = this.getTileIdsByTileIndex(i);
			const tiledTileId0 = this.tiledConfig!.layers[0].data[i];
			const tiledTileId2 = this.tiledConfig!.layers[2].data[i];

			if (
				tileId0 === emptyTileId &&
				tileId2 === emptyTileId &&
				tiledTileId0 !== emptyTileId
				// Tu naschval nie je podmienka na vrstvu 2, lebo moze a nemusi byt prazdne.
			) {
				// Ak hrac ma nastavenu viditelnost na preskumanu, tak opravime tileId.
				if (
					this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
					TileVisibilityType.EXPLORED
				) {
					this.setInternalTileValue(i, 0, tiledTileId0);
					this.setInternalTileValue(i, 2, tiledTileId2);
					continue;
				}

				// Ak ma aspon jedeneho suseda preskumaneho, tak opravime tileId.
				const neighborIndexes =
					this.getNeighbourTileIndexesByTileCoordinates(
						this.getTileCoordinatesByTileIndex(i),
						0,
						false,
						new Set<TileVisibilityType>([
							TileVisibilityType.EXPLORED,
						]),
					);
				if (neighborIndexes.length) {
					this.setInternalTileValue(i, 0, tiledTileId0);
					this.setInternalTileValue(i, 2, tiledTileId2);
					this.setInternalTileValue(
						i,
						TileEntityParam.VISIBILITY,
						TileVisibilityType.EXPLORED,
					);
					continue;
				}
			}
		}

		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("tr.runTiledErrata()");
		}
	}

	// TODO: Toto treba zjednotit s initTint, lebo ak ten nespustim, tak toto nefunguje, lebo sa spolieha, ze uz su tie tiles zafarbene.
	public fixRedTiles({
		ignoreEnemyTileIndex,
		forceMainEnemyTileIndex,
	}: {
		ignoreEnemyTileIndex?: TileIndex;
		forceMainEnemyTileIndex?: TileIndex;
	} = {}): void {
		const tilesToRemoveTint = new Set<TileCoordinatesWithValue>();
		const applyRedTintByTileIndexes = new Map<TileIndex, boolean>();
		const forceTileIndexes = new Set<TileIndex>();

		// 1. Označíme všetky aktuálne blokované tiles ako kandidátov na odblokovanie
		for (let i = 0; i < this.size; i++) {
			if (
				this.getInternalTileValue(
					i,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
				) === 0
			)
				continue;

			applyRedTintByTileIndexes.set(i, false);
		}

		// 2. Označíme tiles, ktoré majú byť blokované podľa dosahu živých enemákov
		for (let i = 0; i < this.size; i++) {
			if (
				ignoreEnemyTileIndex !== undefined &&
				i === ignoreEnemyTileIndex
			)
				continue;

			const tileId = this.getTileIdsByTileIndex(i)[2];
			const frameName = this.getFrameNameByTileId(tileId);
			const isForcedEnemyTileIndex = i === forceMainEnemyTileIndex;
			if (
				!isForcedEnemyTileIndex &&
				(!frameName ||
					(!tilerepoBanditsFrameNames.has(frameName) &&
						!tilerepoPiratesFrameNames.has(frameName) &&
						!tilerepoFightableMainEnemiesFrameNames.has(frameName)))
			)
				continue;

			const entity = this.getTileEntityByTileIndex(i);
			if (!entity) continue;

			const range = entity.range || 0;
			for (let dy = -range; dy <= range; dy++) {
				for (let dx = -range; dx <= range; dx++) {
					if (dx === 0 && dy === 0) continue;
					const x = entity.x + dx;
					const y = entity.y + dy;
					const targetIndex = this.getTileIndexByTileCoordinates({
						x,
						y,
					});

					applyRedTintByTileIndexes.set(targetIndex, true);
					if (isForcedEnemyTileIndex)
						forceTileIndexes.add(targetIndex);
				}
			}
		}

		// 3. Synchronizácia dátového modelu a určenie vizuálneho stavu pre Phaser
		const finalTintMap = new Map<TileIndex, boolean>();

		for (const i of applyRedTintByTileIndexes.keys()) {
			const shouldBeBlocked =
				applyRedTintByTileIndexes.get(i) === true ||
				forceTileIndexes.has(i);
			const wasBlocked =
				this.getInternalTileValue(
					i,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
				) === 1;

			// Aktualizácia interných dátových hodnôt
			if (shouldBeBlocked && !wasBlocked) {
				this.setInternalTileValue(
					i,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
					1,
				);
				this.setInternalTileValue(
					i,
					TileEntityParam.DURABILITY,
					this.getInternalTileValue(i, TileEntityParam.DURABILITY) *
						3,
				);
			} else if (!shouldBeBlocked && wasBlocked) {
				this.setInternalTileValue(
					i,
					TileEntityParam.IS_BLOCKED_BY_ENEMY,
					0,
				);
				this.setInternalTileValue(
					i,
					TileEntityParam.DURABILITY,
					this.getInternalTileValue(i, TileEntityParam.DURABILITY) /
						3,
				);
			}

			// 1. Zistíme, či je políčko vo Fog of War (použite vašu metódu/parameter pre FoW)
			const isTileInFog =
				this.getInternalTileValue(i, TileEntityParam.VISIBILITY) ===
				TileVisibilityType.FOG;

			// 2. Kontrola aktívnej jednotky (riešime len pre viditeľné políčka)
			const isIgnoredTile =
				ignoreEnemyTileIndex !== undefined &&
				i === ignoreEnemyTileIndex;

			let hasActiveUnit = false;
			if (!isIgnoredTile && !isTileInFog) {
				const [, , tileId] = this.getTileIdsByTileIndex(i);
				const frameName = this.getFrameNameByTileId(tileId);
				if (
					frameName &&
					(tilerepoFriendlyUnitsFrameNames.has(frameName) ||
						tilerepoFightableEnemiesFrameNames.has(frameName))
				) {
					hasActiveUnit = true;
				}
			}

			// 3. Vizuálne pravidlo pre Phaser:
			// Red tint sa zobrazí (removeTint = false) LEN AK je políčko blokované, NIE JE v hmle a NESTOJÍ na ňom jednotka.
			const showRedTint =
				shouldBeBlocked && !isTileInFog && !hasActiveUnit;
			const removeTint = !showRedTint; // value = true znamená ODSTRÁNIŤ/NEZOBRAZOVAŤ tint

			finalTintMap.set(i, removeTint);
		}

		// Prevedenie vyčistených stavov do emitter Set-u
		for (const [i, removeTint] of finalTintMap.entries()) {
			tilesToRemoveTint.add({
				...this.getTileCoordinatesByTileIndex(i),
				value: removeTint,
			});
		}

		this.emitter.emit(
			EmitterEvents.PhaserRenderBlockedByEnemy,
			tilesToRemoveTint,
		);

		// Update cache pre zničenú hlavnú budovu / enemáka
		if (ignoreEnemyTileIndex !== undefined) {
			const entity = this.getTileEntityByTileIndex(ignoreEnemyTileIndex);
			if (!entity) return;
			if (!tilerepoFightableMainEnemiesFrameNames.has(entity.frameName))
				return;

			const range = entity.range || 0;
			for (let dy = -range; dy <= range; dy++) {
				for (let dx = -range; dx <= range; dx++) {
					if (dx === 0 && dy === 0) continue;
					const x = entity.x + dx;
					const y = entity.y + dy;
					const targetIndex = this.getTileIndexByTileCoordinates({
						x,
						y,
					});
					this.setBlockedByEnemyTileIndexByTileIndex(
						targetIndex,
						ignoreEnemyTileIndex,
						false,
					);
				}
			}
		}
	}

	private migrateSavedFarmFieldTileIds(
		tileIds: Tuple<TileId, TileRepositoryLayerCount>,
	): Tuple<TileId, TileRepositoryLayerCount> {
		if (this.FARM_FIELD_TILE_IDS.has(tileIds[0])) {
			tileIds[2] = tileIds[0];
			tileIds[0] = this.frameNameToTileIdCache.get(
				this.getRandomGrasslandFrameName(),
			)!;
		}

		return tileIds;
	}

	private randomizeSettlementEmptyTiles(): void {
		const { SETTLEMENT_EMPTY_TILE_ID } = this;

		for (let i = 0; i < this.size; i++) {
			if (this.getInternalTileValue(i, 0) !== SETTLEMENT_EMPTY_TILE_ID)
				continue;

			this.setRandomSettlementEmptyFrameNameByTileIndex(i, true);
		}
	}

	public getSettlementCoordinatesOrderedByDistance(): TileCoordinates[] {
		let settlements: { x: number; y: number; distance: number }[] = [];
		settlementNames.forEach((name: TileCoordinates, key: number): void => {
			if (
				this.getTileEntityByTileCoordinates({
					x: name.x,
					y: name.y,
				})?.getParamByName(TileEntityParam.VISIBILITY) !==
				TileVisibilityType.BLACK
			)
				return;
			if ([5, 6, 7, 71].includes(key)) return; // skip special settlements

			const mapCenterPoint = this.MAP_SIZE / 2;
			const x = name.x - mapCenterPoint;
			const y = name.y - mapCenterPoint;
			const distance = Math.abs(x) + Math.abs(y);
			settlements.push({ x: name.x, y: name.y, distance });
		});
		const { minX, maxX, minY, maxY } =
			useMapExpansionStore().getEffectiveMapBorderCoordinates();
		// filter out settlements that are outside the effective map border
		settlements = settlements.filter(
			(settlement: { x: number; y: number; distance: number }): boolean =>
				settlement.x >= minX &&
				settlement.x <= maxX &&
				settlement.y >= minY &&
				settlement.y <= maxY,
		);
		return settlements.sort(
			(
				a: { x: number; y: number; distance: number },
				b: { x: number; y: number; distance: number },
			): number => a.distance - b.distance,
		);
	}

	public getClosestExploredSettlementCoordinatesByMinLevel(
		fromCoordinates: TileCoordinates,
		level: number,
	): TileCoordinates | null {
		let closestSettlement: TileCoordinates | null = null;
		let closestDistance = Infinity;

		settlementNames.forEach((name: TileCoordinates): void => {
			const entity = this.getTileEntityByTileCoordinates({
				x: name.x,
				y: name.y,
			}) as SettlementCastleBuildingTileEntity;
			if (
				!entity ||
				entity.level < level ||
				entity.getParamByName(TileEntityParam.VISIBILITY) !==
					TileVisibilityType.EXPLORED
			)
				return;

			const distance =
				Math.abs(fromCoordinates.x - name.x) +
				Math.abs(fromCoordinates.y - name.y);

			if (distance < closestDistance) {
				closestDistance = distance;
				closestSettlement = { x: name.x, y: name.y };
			}
		});

		return closestSettlement;
	}

	public getAllTileEntitesByFrameNames(
		frameNames: Set<FrameName>,
	): Array<TileEntity> {
		const entities = new Array<TileEntity>();

		for (const frameName of frameNames) {
			const tileIndexes = this.frameNameToTileIndexesCache.get(frameName);
			if (!tileIndexes?.size) continue;

			for (const tileIndex of tileIndexes) {
				const entity = this.getTileEntityByTileIndex(tileIndex);
				if (!entity) continue;

				entities.push(entity);
			}
		}

		return entities;
	}

	public collectAllGemsFromGemMines(coordinates: TileCoordinates): void {
		if (useUserStore().gemMinesClaimable === false) return;
		let totalGemsCollected = 0;
		this.frameNameToTileIndexesCache
			.get(FrameName.GEMS_MINE)
			?.forEach((tileIndex: TileIndex): void => {
				const entity = this.getTileEntityByTileIndex(
					tileIndex,
				) as GemsMineBuildingTileEntity | null;
				if (!entity) return;
				totalGemsCollected += entity.gemsProductionWithUpgrade;
				entity.collectGems();
			});
		useUserStore().setGemMinesClaimable(false);
		this.emitter.emit(EmitterEvents.PhaserTileClick, {
			x: coordinates.x,
			y: coordinates.y,
			reward: {
				[ResourceType.GEMS]: totalGemsCollected,
			},
		});
	}

	public getAllBuildingTileIndexes(): Map<FrameName, Set<TileIndex>> {
		return new Map<FrameName, Set<TileIndex>>(
			this.frameNameToTileIndexesCache,
		);
	}

	public markSegmentAsDirty({
		tileIndex,
		prevFrameName,
		nextFrameName,
	}: {
		tileIndex: TileIndex;
		prevFrameName?: FrameName | null;
		nextFrameName?: FrameName | null;
	}): void {
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.time("markSegmentAsDirty");
		}

		const sm = useSegmentManager();
		sm.markSegmentAsDirtyByTileIndex(tileIndex);

		// ak nemame frame name params, early exit
		if (
			(!prevFrameName || prevFrameName === FrameName.NOT_SET) &&
			(!nextFrameName || nextFrameName === FrameName.NOT_SET)
		)
			return;

		for (const frameName of [prevFrameName, nextFrameName]) {
			if (!frameName || frameName === FrameName.NOT_SET) continue;

			const entity = this.createEntityByFrameName(
				frameName,
				this.getTileCoordinatesByTileIndex(tileIndex),
			);
			if (!entity || !entity.suppliesTileIndexes.size) continue;

			for (const tileIndex of entity.suppliesTileIndexes) {
				this.markSegmentAsDirty({
					tileIndex,
					prevFrameName: this.getFrameNameByTileId(
						this.getInternalTileValue(tileIndex, 2) ||
							this.getInternalTileValue(tileIndex, 0),
					),
					nextFrameName: null,
				});
			}
		}
		if (FLAG_ENABLE_TIMERS) {
			// eslint-disable-next-line no-console
			console.timeEnd("markSegmentAsDirty");
		}
	}

	public getFishIncome(): number {
		const fishingBoats = this.getAllFishingBoatEntities();
		const bigFishingBoats = this.getAllBigFishingBoatEntities();
		const fishTraps = this.getAllFishTrapEntities();
		const fishermansHuts = this.getAllFishermansHutEntities();
		return [
			...fishingBoats,
			...bigFishingBoats,
			...fishTraps,
			...fishermansHuts,
		].reduce(
			(acc: number, ship: TileEntity): number =>
				acc +
				(ship.production.get(ResourceType.FOOD_INCOME)! as number),
			0,
		);
	}

	public showAllEntitiesSearchTileLabels(frameName: FrameName): void {
		if (isTerrain(frameName)) {
			this.getTileCoordinates0(frameName).forEach(
				(coordinates: TileCoordinates): void => {
					this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
						coordinates,
						skin: TileLabelSkin.SEARCH_TOOL,
						action: TileLabelAction.CREATE,
					});
				},
			);
		} else {
			this.getTileCoordinates2(frameName).forEach(
				(coordinates: TileCoordinates): void => {
					this.emitter.emit(EmitterEvents.PhaserUpdateTileLabel, {
						coordinates,
						skin: TileLabelSkin.SEARCH_TOOL,
						action: TileLabelAction.CREATE,
					});
				},
			);
		}
	}

	public getAllExploredPoi1TileIndexes(): Set<TileIndex> {
		const poi1TileIndexes = new Set<TileIndex>();

		tilerepoPOIs1FrameNames.forEach((frameName: FrameName): void => {
			if (!this.frameNameToTileIndexesCache.has(frameName)) return;

			this.frameNameToTileIndexesCache
				.get(frameName)!
				.forEach((tileIndex: TileIndex): void => {
					if (
						this.getInternalTileValue(
							tileIndex,
							TileEntityParam.VISIBILITY,
						) !== TileVisibilityType.EXPLORED
					)
						return;

					const entity = this.getTileEntityByTileIndex(
						tileIndex,
					) as AbstractPoiTileEntity;
					if (!entity || !entity.isFullyExplored) return;

					poi1TileIndexes.add(tileIndex);
				});
		});

		return poi1TileIndexes;
	}

	public setBlockedByEnemyTileIndexByTileIndex(
		tileIndex: TileIndex,
		mainEnemyTileIndex: TileIndex | null, // null ak uz nevieme, kto ho blokoval, ale uz vieme, ze je odblokovany - pouziva sa na restore dat z BE
		isBlocked: boolean,
	): void {
		if (mainEnemyTileIndex === null) {
			if (!isBlocked) {
				this.blockedTileIndexesCache.delete(tileIndex);
				return;
			}

			throw new GameError(
				GameErrorType.SIEGE_SYSTEM_ERROR,
				"Cannot set tile as blocked by enemy without enemy tile index",
			);
		}

		if (isBlocked) {
			const enemyTileId = this.getInternalTileValue(
				mainEnemyTileIndex,
				2,
			);
			const enemyFrameName = this.getFrameNameByTileId(enemyTileId);
			if (
				!enemyFrameName ||
				!tilerepoFightableMainEnemiesFrameNames.has(enemyFrameName)
			) {
				throw new GameError(
					GameErrorType.SIEGE_SYSTEM_ERROR,
					`Enemy tile index ${mainEnemyTileIndex} does not have a valid enemy frame name`,
				);
			}
			if (!this.blockedTileIndexesCache.has(tileIndex)) {
				this.blockedTileIndexesCache.set(
					tileIndex,
					new Set<TileIndex>([mainEnemyTileIndex]),
				);
			} else {
				this.blockedTileIndexesCache
					.get(tileIndex)!
					.add(mainEnemyTileIndex);
			}
		} else {
			const owners = this.blockedTileIndexesCache.get(tileIndex);
			if (!owners) return;

			owners.delete(mainEnemyTileIndex);
			if (!owners.size) this.blockedTileIndexesCache.delete(tileIndex);
		}
	}

	public getBlockedByEnemiesMainEnemyTileIndexByTileIndex(
		tileIndex: TileIndex,
	): TileIndex | null {
		if (!this.blockedTileIndexesCache.has(tileIndex)) {
			const tileId = this.getInternalTileValue(tileIndex, 2);
			const frameName = this.getFrameNameByTileId(tileId);
			if (
				frameName &&
				tilerepoFightableMainEnemiesFrameNames.has(frameName)
			) {
				return tileIndex;
			}

			return null;
		}

		return Array.from(this.blockedTileIndexesCache.get(tileIndex)!)[0];
	}

	/**
	 *
	 * ZAČIATOK METÓD URČENYCH PRE PRACU S INDEXDB V TileRepo
	 */

	/**
	 * Uloží aktuálny graf do IndexedDB ako JSON string.
	 */
	public async saveRoadGraphToDB(data: SerializedRoadGraph): Promise<void> {
		const db = await this.getDB();
		// Pridané ': void' pre executor funkciu Promise
		return new Promise<void>((resolve, reject): void => {
			const transaction = db.transaction([STORE_NAME], "readwrite");
			const store = transaction.objectStore(STORE_NAME);

			// OPTIMALIZÁCIA: Serializujeme do stringu
			const jsonString = JSON.stringify(data);

			const request = store.put(jsonString, GRAPH_KEY);

			// Pridané ': void' pre arrow funkcie
			request.onsuccess = (): void => resolve();
			request.onerror = (): void => reject(request.error);
		});
	}

	/**
	 * Načíta graf z IndexedDB a parsuje JSON.
	 */
	private async loadRoadGraphFromDB(): Promise<SerializedRoadGraph | null> {
		const db = await this.getDB();
		// Pridané ': void' pre executor funkciu Promise
		return new Promise((resolve, reject): void => {
			const transaction = db.transaction([STORE_NAME], "readonly");
			const store = transaction.objectStore(STORE_NAME);
			const request = store.get(GRAPH_KEY);

			// Pridané ': void'
			request.onsuccess = (): void => {
				const result = request.result;
				if (!result) {
					resolve(null);
					return;
				}

				try {
					// OPTIMALIZÁCIA: Parsujeme string späť na dáta
					const data = JSON.parse(result) as SerializedRoadGraph;
					resolve(data);
				} catch (e) {
					console.error("Failed to parse RoadGraph JSON", e);
					resolve(null);
				}
			};
			// Pridané ': void'
			request.onerror = (): void => reject(request.error);
		});
	}

	/**
	 * Získa singleton inštanciu DB.
	 * Ak ešte neexistuje, otvorí ju a uloží pre budúce použitie.
	 */
	private getDB(): Promise<IDBDatabase> {
		// 1. Ak už máme otvorenú inštanciu, vrátime ju okamžite (0ms latency)
		if (this.dbInstance) {
			return Promise.resolve(this.dbInstance);
		}

		// 2. Ak už prebieha otváranie (Promise existuje), vrátime ten istý Promise
		// Toto zabráni tomu, aby sa open() volalo 2x naraz
		if (this.dbPromise) {
			return this.dbPromise;
		}

		// 3. Otvoríme nové spojenie
		// Pridané ': void' pre executor funkciu Promise
		this.dbPromise = new Promise((resolve, reject): void => {
			// Check podpory
			if (!("indexedDB" in window)) {
				reject(new Error("IndexedDB not supported"));
				return;
			}

			const request = indexedDB.open(DB_NAME, DB_VERSION);

			// Pridané ': void'
			request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
				const db = (event.target as IDBOpenDBRequest).result;
				if (!db.objectStoreNames.contains(STORE_NAME)) {
					db.createObjectStore(STORE_NAME);
				}
			};

			// Pridané ': void'
			request.onsuccess = (event: Event): void => {
				const db = (event.target as IDBOpenDBRequest).result;
				this.dbInstance = db; // Uložíme singleton

				// Ošetríme zatvorenie (napr. pri force close), aby sme vyčistili premennú
				// Pridané ': void'
				db.onclose = (): void => {
					this.dbInstance = null;
					this.dbPromise = null;
				};

				// Chyba spojenia
				// Pridané ': void'
				db.onerror = (e: Event): void => {
					console.error("Database error", e);
				};

				resolve(db);
			};

			// Pridané ': void'
			request.onerror = (event: Event): void => {
				this.dbPromise = null; // Reset pri chybe
				reject((event.target as IDBOpenDBRequest).error);
			};

			// TIP: Ak je DB zablokovaná iným tabom
			// Pridané ': void'
			request.onblocked = (): void => {
				console.error(
					"IndexedDB is blocked by another tab. Please close other tabs with this game.",
				);
			};
		});

		return this.dbPromise;
	}

	/**
	 * Statická metóda na kompletné zmazanie IndexedDB databázy.
	 * Vhodné použiť pri hard resete hry, zmene verzie DB alebo pri odhlásení.
	 */
	public static async deleteDatabase(): Promise<void> {
		return new Promise<void>((resolve, reject): void => {
			// Kontrola podpory
			if (!("indexedDB" in window)) {
				console.error("IndexedDB not supported, nothing to delete.");
				resolve();
				return;
			}

			// DB_NAME je konštanta definovaná na začiatku tvojho súboru
			const request = indexedDB.deleteDatabase(DB_NAME);

			request.onsuccess = (): void => {
				// eslint-disable-next-line no-console
				console.log(`IndexedDB '${DB_NAME}' bola úspešne zmazaná.`);
				resolve();
			};

			request.onerror = (event: Event): void => {
				console.error(
					"Chyba pri mazaní IndexedDB:",
					(event.target as IDBOpenDBRequest).error,
				);
				reject((event.target as IDBOpenDBRequest).error);
			};

			request.onblocked = (): void => {
				console.error(
					`Mazanie databázy '${DB_NAME}' je blokované. Pravdepodobne je otvorená v inom tabe alebo nebola korektne zatvorená inštancia (db.close()).`,
				);
			};
		});
	}

	/**
	 *
	 * KONIEC METÓD URČENYCH PRE PRACU S INDEXDB V TileRepo
	 */

	/**
	 * Hlavná inicializačná metóda pre RoadGraph.
	 * Skúsi načítať z DB, ak nie je, prepočíta a uloží.
	 */
	public async initializeRoadGraph(): Promise<void> {
		// 1. Skúsime otvoriť DB a načítať dáta
		const cachedGraph = await this.loadRoadGraphFromDB();

		if (cachedGraph) {
			// 2. Ak sa nachádza v DB - Načítame
			// eslint-disable-next-line no-console
			console.log("TileRepository: Loading RoadGraph from IndexedDB");
			this.setSerializedRoadGraph(cachedGraph);
		} else {
			// 3. Ak sa nenachádza - Vypočítame a Uložíme
			// eslint-disable-next-line no-console
			console.log(
				"TileRepository: Rebuilding RoadGraph and saving to IndexedDB",
			);

			// Toto je naša optimalizovaná metóda na prepočet suchozemských aj vodných ciest nanovo podľa entít na mape
			this.rebuildRoadGraph();

			// Získame dáta v tvare Array<[TileIndex, Array<TileIndex>]> v preklade SerializedRoadGraph typ
			//čiže sa jedná len o konvertovanie pre IndexDB
			const serialized = this.getSerializedRoadGraph();

			// Uložíme do DB (nečakáme blokujúco na zápis, môže bežať na pozadí)
			this.saveRoadGraphToDB(serialized).catch(e =>
				console.error("TileRepository: Error saving to IndexedDB", e),
			);
		}
	}

	/**
	 *
	 * ZAČIATOK BUDÚCEHO WASM MODULU
	 */

	/**
	 * Kompletne prepočíta roadGraph na základe aktuálneho stavu Layer 0 a Layer 2.
	 * Volá sa pri inicializácii hry namiesto načítania serializedRoadGraph z DB.
	 */
	public rebuildRoadGraph(): void {
		// 1. Vyčistíme existujúci graf
		this.roadGraph.clear();

		//2 Príprava dátových štruktúr, ktoré budú repreznetovať cesty naprieč jednotlivými vrstvami
		//2.A.1 Príprava nekonkrétnych "budov" ktoré nemajú slúžiť ako cesty
		const excludedBuildingFrames = new Set<FrameName>([
			...tilerepoFarmFieldsFrameNames,
		]);
		//2.A.2 Vylúčenie výnimiek s tilerepoBuildingsFrameNames
		const tilerepoBuildingsFrameNamesWithoutExceptions = new Set<FrameName>(
			[...tilerepoBuildingsFrameNames].filter(
				frame => !excludedBuildingFrames.has(frame),
			),
		);

		//2.B.1 Príprava nekonkrétnych "ciest" ktoré nemajú slúžiť ako cesty, paradox že?
		const excludedRoadFrames = new Set<FrameName>([
			FrameName.DIRT_ROAD_TUTORIAL,
		]);
		//2.B.2 Vylúčenie výnimiek s tilerepoRoadsFrameNames
		const tilerepoRoadsFrameNamesWithoutExceptions = new Set<FrameName>(
			[...tilerepoRoadsFrameNames].filter(
				frame => !excludedRoadFrames.has(frame),
			),
		);

		// 2.C Zozbierame všetky kategórie frameNames, ktoré tvoria uzly grafu
		// Toto je kritické pre výkon - neiterujeme mapu, ale len existujúce objekty
		const relevantFrameNames = new Set<FrameName>([
			...tilerepoSettlementEmptyFrameNames,
			...tilerepoRoadsFrameNamesWithoutExceptions,
			...tilerepoBuildingsFrameNamesWithoutExceptions,
		]);

		// 3. Iterujeme cez všetky relevantné frameNames a ich tileIndexes
		for (const frameName of relevantFrameNames) {
			//už nejdeme porade ako sú jednotlivé tiles uložené na mape a prehľadávame ich všetky ale vyberáme entitu
			//po entite ktorá môže byť cestou a ak máme taký tileIndex pokračujeme
			const tileIndexes: Set<number> | undefined =
				this.frameNameToTileIndexesCache.get(frameName);
			if (!tileIndexes || tileIndexes.size === 0) continue;

			//ak máme entitu napríklad GATHERERS_HUT tak si prejdeme všetky jej výskyty na mape hráča
			for (const tileIndex of tileIndexes) {
				//zistíme stav entity či je odhalená alebo nie, toto je hlavne kôli "cestám" na vrstve 0 - settlement
				const tileEntityVisibility = this.getInternalTileValue(
					tileIndex,
					TileEntityParam.VISIBILITY,
				);
				//cesta môže byť len a len tile ktorý je už odhalený
				if (tileEntityVisibility === TileVisibilityType.EXPLORED) {
					const { x, y } =
						this.getTileCoordinatesByTileIndex(tileIndex);
					// VÝNIMKA: Ak je to Hrad, aktivuj aj okolité "blato" (Settlement Empty)
					// čiže neprechádzame blato, blato, blato, hrad atď. ale ak narazíme na hrad, tak hneď spravíme
					// cesty aj s blata
					if (
						tilerepoSettlementCastleBuildingsFrameNames.has(
							frameName,
						)
					) {
						this.activateSurroundingSettlementEmptyTiles(x, y);
					} else {
						// Pre každý validný tile skontrolujeme susedov a vytvoríme hrany
						this.updateRoadGraphNode(tileIndex, x, y, frameName);
					}
				}
			}
		}

		// 4. Špeciálna logika pre Shipyardy (Prepojenie cez more)
		// Toto musíme spraviť až keď je základný graf hotový, aby sme vedeli
		// identifikovať susedné moria. + hlavné mesto nevie byť napojené vodnou cestou
		//napriamo, musí k moru viesť suchozemská cesta
		this.rebuildShipyardConnections();
	}

	/**
	 * Helper metóda pre výnimku súvisiacu s blatom v settlmentoch.
	 * Pozrie sa okolo hradu (vrátane diagonál) a ak nájde Settlement Empty, pridá ho do grafu.
	 */
	private activateSurroundingSettlementEmptyTiles(
		castleX: number,
		castleY: number,
	): void {
		// Prechádzame mriežku 3x3 okolo hradu (resp. možné upraviť podľa veľkosti hradu)
		// Týmto pokryjeme hore, dole, vľavo, vpravo aj diagonály
		for (let dy = -1; dy <= 1; dy++) {
			for (let dx = -1; dx <= 1; dx++) {
				if (dx === 0 && dy === 0) continue; // Samotný hrad bude spracovaný cez updateRoadGraphNode ako "sused blata"

				const nx = castleX + dx;
				const ny = castleY + dy;

				// Check bounds
				if (
					nx < 0 ||
					nx >= this.MAP_SIZE ||
					ny < 0 ||
					ny >= this.MAP_SIZE
				)
					continue;

				const nIndex = this.getTileIndexByTileCoordinates({
					x: nx,
					y: ny,
				});

				// Získame Layer 0 (terén)
				const nTileId0 = this.getInternalTileValue(nIndex, 0);
				const nFrameName0 = this.getFrameNameByTileId(nTileId0);

				// Ak je sused "Settlement Empty", explicitne ho pridáme do grafu
				if (
					nFrameName0 &&
					tilerepoSettlementEmptyFrameNames.has(nFrameName0)
				) {
					this.updateRoadGraphNode(nIndex, nx, ny, nFrameName0);
				}
			}
		}
	}

	/**
	 * Pomocná metóda, ktorá robí to isté čo časť updateRoadGraph,
	 * ale izolovaná pre jeden uzol bez side-effectov. Teda LEN NA STAVANIE OD NULY!!!
	 */
	private updateRoadGraphNode(
		tileIndex: TileIndex,
		x: number,
		y: number,
		currentFrameName: FrameName,
	): void {
		const neighborDirections =
			this.getDefaulNonDiagonalNeighborCoordinates();
		const neighbors = new Set<TileIndex>();

		// Načítanie frameov aktuálneho uzla z pamäte
		const frameName0 = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 0),
		);
		const frameName2 = this.getFrameNameByTileId(
			this.getInternalTileValue(tileIndex, 2),
		);

		for (const dir of neighborDirections) {
			const nx = x + dir.dx;
			const ny = y + dir.dy;
			// Check bounds
			if (nx < 0 || nx >= this.MAP_SIZE || ny < 0 || ny >= this.MAP_SIZE)
				continue;

			const neighborIndex = this.getTileIndexByTileCoordinates({
				x: nx,
				y: ny,
			});
			// Získame FrameNames suseda (optimalizované čítanie z interného poľa)
			const nTileId0 = this.getInternalTileValue(neighborIndex, 0);
			const nTileId2 = this.getInternalTileValue(neighborIndex, 2);

			const nFrame0 = this.getFrameNameByTileId(nTileId0);
			const nFrame2 = this.getFrameNameByTileId(nTileId2);
			// Logika validácie suseda či sused môže byť uzlom v roadGraph alebo nie (skopírovaná a upravená z updateRoadGraph)
			const isNeighborGraphNode = this.isValidGraphNode(nFrame0, nFrame2);
			if (!isNeighborGraphNode) continue;

			// Ak sú oba uzly validné, ale nepasujú k sebe (napr. Hlinisko a More), preskočíme ich.
			// OPRAVA: Použitie novej signatúry kompatibility uzlov
			if (
				!this.areGraphNodesCompatible(
					frameName0,
					frameName2,
					nFrame0,
					nFrame2,
				)
			) {
				continue;
			}
			//----- Logika Mostov (Bridge Restrictions)
			if (
				nFrame2 &&
				tilerepoBridgeRoadsFrameNames.has(currentFrameName)
			) {
				// Horizontálny most (L/R)
				if (
					[
						FrameName.WOODEN_BRIDGE_1,
						FrameName.STONE_BRIDGE_1,
						FrameName.FORD_CROSSING_1,
					].includes(currentFrameName)
				) {
					if (dir.dx === 0) continue; // Ignoruj vertikálnych susedov
				}
				// Vertikálny most (U/D)
				if (
					[
						FrameName.WOODEN_BRIDGE_2,
						FrameName.STONE_BRIDGE_2,
						FrameName.FORD_CROSSING_2,
					].includes(currentFrameName)
				) {
					if (dir.dy === 0) continue; // Ignoruj horizontálnych susedov
				}
			}
			// To isté musíme skontrolovať aj pre suseda, ak je on most!
			if (nFrame2 && tilerepoBridgeRoadsFrameNames.has(nFrame2)) {
				if (
					[
						FrameName.WOODEN_BRIDGE_1,
						FrameName.STONE_BRIDGE_1,
						FrameName.FORD_CROSSING_1,
					].includes(nFrame2)
				) {
					if (dir.dx === 0) continue;
				}
				if (
					[
						FrameName.WOODEN_BRIDGE_2,
						FrameName.STONE_BRIDGE_2,
						FrameName.FORD_CROSSING_2,
					].includes(nFrame2)
				) {
					if (dir.dy === 0) continue;
				}
			}
			//Ak sused prešiel všetkými podmienkami na pridanie do roadGraph je pridaný do poľa susedov
			neighbors.add(neighborIndex);

			// Keďže staviame graf od nuly, musíme zabezpečiť obojsmernosť.
			// Buď to spravíme tu (pridať mňa susedovi), alebo sa spoľahneme na to,
			// že slučka prejde aj suseda a pridá mňa.
			// Pre istotu a konzistenciu pri rebuilding je lepšie explicitne pridať hranu obom.
			if (!this.roadGraph.has(neighborIndex)) {
				this.roadGraph.set(neighborIndex, new Set());
			}
			//POZOR!!! tu sa neprída aktuálny tile do roadGraph ale tu sa pridáva prepojenie
			// aktuálneho suseda na aktuálny tile. Teda v cykle máme 4 susedov a ten ktorý je teraz na rade ak prešiel
			// podmienakymi nie len že je pridaný do roadGraphu, to robí kód vyššie ale je hneď aj prepojený
			//s tile na ktorý sa aktuálna metóda pozerá
			this.roadGraph.get(neighborIndex)!.add(tileIndex);
		}
		//v tejto fáže sú všetci susedia pridaný do roadGraph a ich napojenie je na tile vystupujúci pod tileIndex
		// teraz ak ešte neexistuje v roadGraph vytvoríme nový uzol v roadGraph pre tento hlavný tile pre ktorého
		// susedov sme to už spravili
		if (!this.roadGraph.has(tileIndex)) {
			this.roadGraph.set(tileIndex, new Set());
		}
		//aby sme sa vyhli performace issue cez Map Lookup urobíme si refenciu na daný uzol v roadGraph
		// cez vytvorenie konštanty currentSet (toto je refernecia do pamäte nie kópia hodnoty, POZOR!!!)
		//následne prejdeme všetkých susedov a pridáme ich spojenia na hlavný uzol tejto metódy - ÁNO môže to pôsobiť
		//divne že najskôr referencujeme neexistujúci uzol ale treba si uvedomiť že performance je takto lepší a sú to len
		//pre programovací jazyk nič nehovoriace čísla, teda technicky to môžeme urobiť
		const currentSet = this.roadGraph.get(tileIndex)!;
		neighbors.forEach(n => currentSet.add(n));
	}

	/**
	 * Špeciálna metóda na prepojenie prístavov cez more
	 */
	private rebuildShipyardConnections(targetTileIndex?: TileIndex): void {
		//inicialiyuj pole shipyardom pre ktore chceme kalkulovat morske cesty
		let shipyards: TileEntity[] = new Array<TileEntity>();
		//ak uvádzame tileIndex jendého konkrétneho shipyardu, tak sa vezmú len shipyardz s jeho mora
		if (targetTileIndex) {
			const shipyardEntity =
				this.getTileEntityByTileIndex(targetTileIndex);
			if (!shipyardEntity) return;

			shipyards = this.shipyardsInSameSea(shipyardEntity, false);
		} else {
			//inak sa berú všetky shipyardy
			shipyards = this.getAllShipyardEntities(false);
		}
		//ak mám menej ako 2 shipyardy, nemám aké vodné cesty stavať
		if (shipyards.length < 2) return;

		// Množina spracovaných lodeníc (uložíme si ich indexy), aby sme ich preskočili
		// ak už sme ich priradili k nejakému moru v predchádzajúcom kroku.
		const processedShipyardIndexes = new Set<TileIndex>();

		// 1. ITERÁCIA CEZ LODENICE
		for (let i = 0; i < shipyards.length; i++) {
			const s1: TileEntity = shipyards[i];
			const s1Index = this.getTileIndexByTileCoordinates({
				x: s1.x,
				y: s1.y,
			});

			// Ak už je táto lodenica súčasťou spracovaného mora, preskočíme ju
			if (processedShipyardIndexes.has(s1Index)) continue;

			// 2. ZÍSKANIE MORA (Flood Fill) - Toto je tá drahá operácia,
			// ale teraz ju voláme LEN RAZ pre každé izolované more.
			const seaTiles = this.getConnectedSeaTiles(s1.x, s1.y);

			// Ak lodenica nie je pri vode (bug/error), označíme ju a ideme ďalej
			if (seaTiles.size === 0) {
				processedShipyardIndexes.add(s1Index);
				continue;
			}

			// 3. NÁJDEME VŠETKY OSTATNÉ LODENICE NA TOM ISTOM MORI
			// Namiesto BFS pozeráme do `seaTiles` (O(1) lookup), či tam ležia iné lodenice.
			const shipyardsInSameSea: typeof shipyards = [s1];
			processedShipyardIndexes.add(s1Index);

			for (let j = i + 1; j < shipyards.length; j++) {
				const s2: TileEntity = shipyards[j];
				const s2Index = this.getTileIndexByTileCoordinates({
					x: s2.x,
					y: s2.y,
				});

				if (processedShipyardIndexes.has(s2Index)) continue;

				// Rýchly check: Je súradnica S2 v množine seaTiles?
				const s2Key = `${s2.x},${s2.y}`;
				if (seaTiles.has(s2Key)) {
					shipyardsInSameSea.push(s2);
					processedShipyardIndexes.add(s2Index);
				}
			}

			// Ak je na tomto mori len jedna lodenica, nemáme čo prepájať
			if (shipyardsInSameSea.length < 2) continue;

			// OPTIMALIZÁCIA: Zoradíme lodenice, aby sme ich prepájali pekne rad za radom
			// a nie "cik-cak" cez celú mapu. Zoradenie podľa X a potom Y je jednoduchá heuristika.
			shipyardsInSameSea.sort((a, b) => a.x - b.x || a.y - b.y);

			// 4. PREPOJENIE AKO REŤAZ (O(N) namiesto O(N^2))
			// Prepájame len susedov v zozname: 0-1, 1-2, 2-3...
			for (let k = 0; k < shipyardsInSameSea.length - 1; k++) {
				const shipA = shipyardsInSameSea[k];
				const shipB = shipyardsInSameSea[k + 1];

				const path = this.findShortestSeaPathBFS(
					{ x: shipA.x, y: shipA.y },
					{ x: shipB.x, y: shipB.y },
					seaTiles,
				);

				// Pridaj celú cestu do grafu
				for (let p = 0; p < path.length - 1; p++) {
					const coordA = path[p];
					const coordB = path[p + 1];
					const keyA = this.getTileIndexByTileCoordinates(coordA);
					const keyB = this.getTileIndexByTileCoordinates(coordB);

					if (!this.roadGraph.has(keyA))
						this.roadGraph.set(keyA, new Set());
					if (!this.roadGraph.has(keyB))
						this.roadGraph.set(keyB, new Set());

					this.roadGraph.get(keyA)!.add(keyB);
					this.roadGraph.get(keyB)!.add(keyA);
				}
			}
		}
	}

	/**
	 * Centrálna definícia toho, čo tvorí uzol v RoadGraph.
	 * Používa sa pri rebuildRoadGraph aj updateRoadGraph pre konzistenciu.
	 */
	private isValidGraphNode(
		frameName0: FrameName | null,
		frameName2: FrameName | null,
	): boolean {
		// 1. Settlement (Blato) - Layer 0
		if (frameName0 && tilerepoSettlementEmptyFrameNames.has(frameName0))
			return true;

		// 2. More (ak je súčasťou cesty) - Layer 0
		if (frameName0 && tilerepoSeasFrameNames.has(frameName0)) return true;

		// 3. Cesty (okrem tutorialovej) - Layer 2
		if (
			frameName2 &&
			tilerepoRoadsFrameNames.has(frameName2) &&
			frameName2 !== FrameName.DIRT_ROAD_TUTORIAL
		)
			return true;

		// 4. Budovy - Layer 2
		if (frameName2 && tilerepoBuildingsFrameNames.has(frameName2)) {
			// VÝNIMKY: Tieto "budovy" netvoria cestu (lodičky, pasce... - zmena výnimiek,
			// už lodicky a pasce môžu byť cesta lebo nedokážu  byť spojnica medzi vodnou a suchozmekos cestou)
			if (tilerepoFarmFieldsFrameNames.has(frameName2)) return false;

			return true;
		}

		return false;
	}

	/**
	 * Overuje, či sú dva uzly kompatibilné na vytvorenie spojenia.
	 * Rieši logiku: Zem <-> Zem, Voda <-> Voda, Shipyard <-> Všetko.
	 * Zamedzuje spojeniu Zem <-> Voda.
	 */
	private areGraphNodesCompatible(
		currentFrame0: FrameName | null,
		currentFrame2: FrameName | null,
		neighborFrame0: FrameName | null,
		neighborFrame2: FrameName | null,
	): boolean {
		// 1. Ak je hociktorý z nich Shipyard, spojenie je vždy povolené (most medzi vodou a zemou)
		if (currentFrame2 && tilerepoShipyardFrameNames.has(currentFrame2))
			return true;
		if (neighborFrame2 && tilerepoShipyardFrameNames.has(neighborFrame2))
			return true;

		// 2. Kontrola typu povrchu striktne podľa Layer 0 (voda vs zem)
		const currentIsSea =
			currentFrame0 && tilerepoSeasFrameNames.has(currentFrame0);
		const neighborIsSea =
			neighborFrame0 && tilerepoSeasFrameNames.has(neighborFrame0);

		// 3. Voda sa spojí len s vodou, Zem len so zemou
		return !!currentIsSea === !!neighborIsSea;
	}

	public isValidHighwayPath(
		start: TileCoordinates,
		end: TileCoordinates,
	): boolean {
		let isBlocked = false;
		let tileCoords = { x: start.x, y: start.y };
		do {
			isBlocked = this.isHighwayTileBlocked(tileCoords);
			tileCoords = this.getOneCoordinateCloser(tileCoords, end);
			if (isBlocked) return false;
		} while (tileCoords.x !== end.x || tileCoords.y !== end.y);
		return true;
	}

	private isHighwayTileBlocked(coordinates: TileCoordinates): boolean {
		const tile = this.getTileEntityByTileCoordinates(coordinates);
		if (!tile || !tile.isExplored) return true;
		let canUpgradeToRoad = false;

		for (const [frameName] of tile.upgrades) {
			if (tilerepoRoadsFrameNames.has(frameName)) {
				canUpgradeToRoad = true;
				break; // Našli sme zhodu, môžeme cyklus hneď ukončiť
			}
		}
		const prevFrameName = tile.frameName;
		if (
			tilerepoSalvageableFrameNames.has(prevFrameName) ||
			tilerepoBuildingsFrameNames.has(prevFrameName) ||
			tilerepoRoadsFrameNames.has(prevFrameName)
		)
			canUpgradeToRoad = true;
		if (!canUpgradeToRoad || tile.isBlockedByEnemy) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: tile!.x,
				y: tile!.y,
				text: "ui.obstacle_on_the_road",
				messageTime: 1800,
			});
			return true;
		}
		return false;
	}

	public buildHighway(start: TileCoordinates, end: TileCoordinates): void {
		let tileCoords = { x: start.x, y: start.y };
		do {
			tileCoords = this.getOneCoordinateCloser(tileCoords, end);
			this.upgradeTileForHighway(tileCoords);
		} while (tileCoords.x !== end.x || tileCoords.y !== end.y);

		SoundService.getInstance().playBuildingSound();
	}

	upgradeTileForHighway(coordinates: TileCoordinates): void {
		let tile = this.getTileEntityByTileCoordinates(coordinates);
		if (!tile) return;
		const building = useQuickToolStore().building;

		let prevFrameName = tile.frameName;
		const nextBuilding = getCorrectRoadFrameNameByPrevFrameName(
			building,
			prevFrameName,
		);
		if (
			!tile.isLevelRequirementSatisfied(
				this.createEntityByFrameName(nextBuilding)!,
			) &&
			(useBuildingsStorageStore()?.[nextBuilding] || 0) === 0
		) {
			useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
				x: tile!.x,
				y: tile!.y,
				text: "ui.building_not_available",
				messageTime: 1800,
			});
			return;
		}
		const numberOfRoadsOnMap = this.getCurrentNumberOfPremiumRoads();
		const userRoadCap = useSettlementsStore().getTotalAvailableRoadCaps;
		const remainingRoads = {
			[FrameName.GRAVEL_ROAD]:
				userRoadCap[FrameName.GRAVEL_ROAD] -
				numberOfRoadsOnMap[FrameName.GRAVEL_ROAD],
			[FrameName.COBBLED_ROAD]:
				userRoadCap[FrameName.COBBLED_ROAD] -
				numberOfRoadsOnMap[FrameName.COBBLED_ROAD],
		};
		if (
			tilerepoGravelRoadsFrameNames.has(nextBuilding) ||
			tilerepoWoodenBridgeRoadsFrameNames.has(nextBuilding)
		) {
			if (remainingRoads[FrameName.GRAVEL_ROAD] <= 0) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: tile!.x,
					y: tile!.y,
					text: "ui.gravel_road_limit",
				});
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: tile!.x,
					y: tile!.y,
					text: "ui.increase_more_towns_to_level",
					replace: { placeholder: "{level}", value: "10" },
					messageNumber: 2,
				});
				return;
			}
		} else if (
			tilerepoCobbledRoadsFrameNames.has(nextBuilding) ||
			tilerepoTunnelRoadsFrameNames.has(nextBuilding) ||
			tilerepoStoneBridgeRoadsFrameNames.has(nextBuilding)
		) {
			if (remainingRoads[FrameName.COBBLED_ROAD] <= 0) {
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: tile!.x,
					y: tile!.y,
					text: "ui.cobbled_road_limit",
				});
				useEmitter().emit(EmitterEvents.PhaserWarningMessage, {
					x: tile!.x,
					y: tile!.y,
					text: "ui.increase_more_towns_to_level",
					replace: { placeholder: "{level}", value: "10" },
					messageNumber: 2,
				});
				return;
			}
		}
		if (tilerepoSalvageableFrameNames.has(prevFrameName)) {
			tile =
				this.handleUpgrade(
					prevFrameName,
					FrameName.NOT_SET,
					tile,
					undefined,
					undefined,
					false,
				) ?? tile;
			prevFrameName = tile.frameName;
		}
		if (tilerepoBuildingsFrameNames.has(prevFrameName)) {
			tile.setParamByName(
				TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT,
				1,
			);
			return;
		}
		this.handleUpgrade(
			prevFrameName,
			nextBuilding,
			tile,
			undefined,
			undefined,
			false,
		);
	}

	public getOneCoordinateCloser(
		start: TileCoordinates,
		end: TileCoordinates,
	): TileCoordinates {
		const newStart = { x: start.x, y: start.y };
		// move one coordinate closer to end
		if (start.x < end.x) {
			newStart.x += 1;
		} else if (start.x > end.x) {
			newStart.x -= 1;
		} else if (start.y < end.y) {
			newStart.y += 1;
		} else if (start.y > end.y) {
			newStart.y -= 1;
		}
		return newStart;
	}

	/**
	 *
	 * KONIEC BUDÚCEHO WASM MODULU
	 */

	public removePlacedBuildings(buildings: ReadonlySet<FrameName>): void {
		for (const building of buildings) {
			const tileIndexes = this.frameNameToTileIndexesCache.get(building);
			if (!tileIndexes?.size) continue;

			for (const tileIndex of tileIndexes) {
				const entity = this.getTileEntityByTileIndex(tileIndex);
				if (!entity) continue;

				this.setTileByTileCoordinates(
					{ x: entity.x, y: entity.y },
					FrameName.NOT_SET,
				);
			}
		}
	}

	public removeEventBuildings(): void {
		this.removePlacedBuildings(tilerepoRemovableEventBuildingsFrameNames);
	}
}

export default TileRepository;
